import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Los textos del panel, en los dos idiomas y en un solo lugar.
 *
 * ── Por qué una traducción faltante rompe la compilación ────────────────────────
 * El diccionario español es la fuente de las claves; el inglés se declara como
 * `Record<Clave, string>`. Olvidarse de una entrada no produce una cadena vacía ni el texto en
 * español colándose en una pantalla en inglés: produce un error de tipos.
 *
 * Es el mismo razonamiento que sostiene el aislamiento en el servidor y los avisos en el
 * cliente: un control que depende de que alguien se acuerde de aplicarlo se rompe con el
 * próximo que se escriba, y no hay forma de notarlo. Acá el que se acuerda es el compilador.
 *
 * ── Qué NO se traduce ───────────────────────────────────────────────────────────
 * Lo que escribe el modelo. Una ficha se genera en el idioma que se pidió y se guarda así: la
 * historia de la influencer no es interfaz, es contenido, y traducirla al vuelo la reescribiría
 * cada vez que alguien cambia el selector. Los catálogos —nichos y rasgos— sí vienen en los dos,
 * porque son opciones nuestras: los sirve `GET /api/catalogo` con `es` y `en` en cada entrada.
 *
 * ── Por qué el diccionario es plano y con claves con punto ──────────────────────
 * Anidarlo se lee mejor en el archivo y peor en todos los demás: `t('nueva.edad.titulo')` se
 * encuentra con una búsqueda de texto, `t(dicc.nueva.edad.titulo)` no.
 */

export const IDIOMAS = ['es', 'en'] as const;
export type Idioma = (typeof IDIOMAS)[number];

/** Cómo se ofrece cada idioma en el selector: siempre en su propio idioma. */
export const NOMBRE_DE_IDIOMA: Record<Idioma, string> = {
  es: 'Español',
  en: 'English',
};

const ES = {
  // ── Comunes ──
  'comun.cancelar': 'Cancelar',
  'comun.guardar': 'Guardar',
  'comun.guardando': 'Guardando…',
  'comun.volver': 'Volver',
  'comun.editar': 'Editar',
  'comun.opcional': 'Opcional',
  'comun.idioma': 'Idioma',
  'comun.cerrarSesion': 'Cerrar sesión',
  'comun.cerrar': 'Cerrar',
  'comun.salir': 'Salir',
  'comun.reintentar': 'Reintentar',

  'comun.cargando': 'Cargando…',
  'comun.codigo': 'Código: {code}',
  'comun.temaClaro': 'Cambiar a tema claro',
  'comun.temaOscuro': 'Cambiar a tema oscuro',

  // ── Estados de una influencer ──
  'estado.DRAFT': 'Borrador',
  'estado.GENERATING_IDENTITY': 'Generando identidad',
  'estado.GENERATING_MEDIA': 'Generando imágenes',
  'estado.AWAITING_REVIEW': 'Esperando revisión',
  'estado.WAITING_FOR_ACCOUNT_CREATION': 'Falta crear la cuenta',
  'estado.WAITING_FOR_EMAIL_VERIFICATION': 'Falta verificar el correo',
  'estado.WAITING_FOR_KYC': 'Falta el KYC',
  'estado.WAITING_FOR_OAUTH': 'Falta conectar OAuth',
  'estado.READY': 'Lista',
  'estado.ACTIVE': 'Activa',
  'estado.PAUSED': 'Pausada',
  'estado.QUOTA_EXHAUSTED': 'Cuota agotada',
  'estado.BLOCKED': 'No disponible todavía',
  'estado.ERROR': 'Error',

  // ── Tareas humanas ──
  'tarea.CREATE_ACCOUNT': 'Crear la cuenta',
  // En Facebook no se crea una cuenta: se crea una Página desde un perfil que ya existe.
  'tarea.CREATE_ACCOUNT.facebook': 'Crear la Página',
  'tarea.VERIFY_EMAIL': 'Verificar el correo',
  'tarea.VERIFY_PHONE': 'Verificar el teléfono',
  'tarea.COMPLETE_KYC': 'Completar el KYC',
  'tarea.ACCEPT_TERMS': 'Aceptar los términos',
  'tarea.CONVERT_TO_PROFESSIONAL': 'Convertir a cuenta profesional',
  'tarea.CONNECT_OAUTH': 'Conectar por OAuth',
  'tarea.APPROVE_PROFILE': 'Aprobar el perfil',
  'tarea.CREATE_DEVELOPER_APP': 'Crear la app de desarrollador',
  'tarea.CREATE_EMAIL_ACCOUNT': 'Crear el correo electrónico',
  'tarea.COMPLETE_WITHDRAWAL': 'Cobrar un retiro',
  'tarea.SETUP_AUTOREPLY': 'Configurar las respuestas automáticas',

  // ── Navegación ──
  'nav.principal': 'Principal',
  'nav.inicio': 'Inicio',
  'nav.nueva': 'Nueva influencer',
  /*
   * El mismo destino, con nombre corto para la barra del teléfono.
   *
   * «Nueva influencer» en una columna de 78 px sale «Nueva influ…», que es peor que decir
   * menos: la elipsis ocupa el lugar donde iría una palabra entera. En el menú lateral, donde
   * hay ancho de sobra, sigue el nombre largo.
   */
  'nav.nuevaCorta': 'Nueva',
  'nav.plataformas': 'Plataformas',
  'nav.referencias': 'Leads',
  'nav.consumo': 'Consumo',
  'nav.paginas': 'Páginas',
  'nav.modelos': 'Modelos',
  'nav.servicios': 'Servicios',
  'nav.credenciales': 'Credenciales',
  'nav.guia': 'Guía',
  'nav.mas': 'Más',
  'nav.cuentas': 'Cuentas',
  'nav.laboratorio': 'Laboratorio',
  'nav.menu': 'Menú',

  'app.verificandoSesion': 'Verificando la sesión…',
  'app.paginaInexistente': 'Página inexistente.',

  // ── Acceso ──
  'login.titulo': 'Virtual Creator OS',
  'login.subtitulo': 'Entrá para administrar tus influencers.',
  'login.usuario': 'Usuario',
  'login.contrasena': 'Contraseña',
  'login.entrar': 'Entrar',
  'login.entrando': 'Entrando…',
  'login.panelAdmin': 'Panel de administración',
  'login.correo': 'Correo',
  'login.correoVacio': 'vacío para entrar como administrador',
  'login.correoPlaceholder': 'tu@correo.com',
  'login.verificando': 'Verificando…',
  'login.detrasDeAccess': 'Detrás de Cloudflare Access esta pantalla no aparece.',
  'login.noSePudoConectar': 'No se pudo conectar.',

  'comun.recargar': 'Recargar la página',
  'error.seccionFallo': 'Esta sección falló',
  'error.seccionDetalle':
    'El resto del panel sigue funcionando: podés navegar a otra sección desde el menú. No se ' +
    'perdió nada — es un fallo al dibujar la pantalla, no al guardar datos.',

  // ── Servicios externos ──
  'servicios.cargando': 'Cargando servicios…',
  'servicios.titulo': 'Servicios externos',
  'servicios.subtitulo': 'Todo lo que este sistema usa por fuera, con su consola y su estado real.',
  'servicios.activos': 'Activos',
  'servicios.sinConfigurar': 'Sin configurar',
  'servicios.sumanCapacidad': 'Opcionales, pero suman capacidad',
  'servicios.fase3': 'Fase 3',
  'servicios.previstos': 'Previstos para más adelante',
  'servicios.estado.configured': 'Configurado',
  'servicios.estado.missing': 'Sin configurar',
  'servicios.estado.phase_3': 'Fase 3',
  'servicios.estado.built_in': 'Integrado',
  'servicios.costo': 'Costo: ',
  'servicios.comoSeCargan': 'Cómo se cargan las claves',
  'servicios.dondeVan':
    'En desarrollo van en apps/worker/.dev.vars, que está fuera del repositorio. En producción se ' +
    'cargan como secrets:',
  'servicios.nuncaMuestra':
    'Este panel nunca muestra el valor de una clave, sólo si está presente.',

  // ── Corrida del workflow ──
  'corrida.iniciando': 'Iniciando el workflow…',
  'corrida.titulo': 'Generando influencer',
  'corrida.cuotaAgotada': 'La cuota diaria se agotó. El trabajo continúa solo cuando se reinicie.',
  'corrida.reinicio': 'Reinicio: {cuando}',
  'corrida.noSeReintenta':
    'Este error no se reintenta automáticamente: reintentar no cambiaría el resultado.',
  'corrida.pasos': 'Pasos · {hechos} de {total}',
  'corrida.consumoEstimado': 'Consumo estimado: {n} créditos',
  'corrida.verInfluencer': 'Ver la influencer',

  // ── Formulario simple ──
  'nueva.empecemos': 'Empecemos',
  'nueva.deQueHabla': '¿De qué habla?',
  'nueva.deQueHablaAyuda': 'Elegí uno o escribí el tuyo. Si lo dejás vacío, lo elige la IA.',
  'nueva.masOpciones': 'Más opciones',
  'nueva.masOpcionesDetalle': 'rasgos, estilo, plataformas — todo tiene un valor que funciona',
  'nueva.rasgosPocos': 'Ver los {n} rasgos',
  'nueva.oBien': 'o',
  'nueva.listaVacia': 'La IA elige',

  'nueva.opcionesCambiaron':
    'Cambiaste algo de arriba después de escribir esta ficha, así que la ficha todavía es la ' +
    'anterior.',
  'nueva.volverAEscribir': 'Volver a escribirla',
  'nueva.antesDeEscribir': 'Ajustá lo que quieras y después escribí la ficha.',

  // ── Paso 1: quién es ──
  'nueva.titulo': 'Generar nueva influencer',
  'nueva.subtitulo':
    'Elegí un tema y una edad, y la IA escribe el resto. Podés leerla y corregirla antes de que ' +
    'se genere una sola imagen.',
  'nueva.paso2': 'Su ficha',

  'nueva.queCrear': 'Qué crear',
  'nueva.kind.human': 'Influencer',
  'nueva.kind.humanDetalle': 'Persona virtual. Elegís nicho, edad, rasgos y estilo.',
  'nueva.kind.character': 'Personaje no humano',
  'nueva.kind.characterDetalle':
    'Una criatura, un ser, una forma. No se pide nada: se pulsa y sale.',
  'nueva.kind.characterAviso':
    'La cadena es la misma: candidatas, elegís la referencia, hoja de identidad y contenido. Lo ' +
    'que no va a funcionar es la etiqueta de compatibilidad: el medidor busca caras humanas y en ' +
    'un personaje no encuentra ninguna, así que las tomas quedan «sin medir».',

  'nueva.nicho': 'Nicho',
  'nueva.nichoAyuda':
    'Elegí de la lista o escribí el tuyo. Si lo dejás vacío lo elige el modelo, y tiende a ' +
    'inventar temas sin público.',
  'nueva.nichoLibre': 'Agregar uno propio',
  'nueva.nichoPlaceholder': 'Escribilo y pulsá Enter',
  'nueva.nichoVerMas': 'Ver todos los nichos',
  'nueva.nichoVerMenos': 'Ver menos',

  'nueva.edad': 'Edad',
  'nueva.edadAyuda':
    'Se usa tal cual: la apariencia de la ficha y de las imágenes va a corresponder a esta edad.',
  'nueva.edadAnios': '{n} años',
  'nueva.edadCercaDelPiso':
    'Cerca del mínimo el proveedor de imagen rechaza más seguido —modera por apariencia, no por ' +
    'el número de la ficha— y cada rechazo se paga.',

  'nueva.pais': 'País narrativo',
  'nueva.paisPlaceholder': 'Buenos Aires, Argentina',
  'nueva.idiomasDePublicacion': 'Idiomas en que publica',

  'nueva.rasgos': 'Rasgos distintivos',
  'nueva.rasgosAyuda':
    'Lo que la hace reconocible de un vistazo. Sin pedir nada, el modelo devuelve siempre un ' +
    'lunar y unas pecas: el promedio de lo que aprendió. Podés elegir varios.',
  'nueva.rasgosElegidos': '{n} elegidos',
  'nueva.rasgosLimpiar': 'Quitar todos',

  // ── Paso 2: la ficha ──
  'nueva.fichaTitulo': 'Su ficha',
  'nueva.fichaVacia':
    'Todavía no hay ficha. Pulsá «Escribir la ficha» y el modelo la redacta con lo de arriba.',
  'nueva.escribirFicha': 'Escribir la ficha',
  'nueva.reescribirFicha': 'Escribir otra',
  'nueva.escribiendo': 'Escribiendo…',
  'nueva.alAzar': 'Sorprendeme',
  'nueva.alAzarAyuda': 'Llena los campos de arriba al azar y escribe la ficha.',
  'nueva.fichaEditable': 'Todo esto es editable. Lo que dejes acá es lo que se va a generar.',
  'nueva.fichaViolaciones':
    'Así como está, la creación se va a rechazar. Corregí esto antes de continuar:',
  'nueva.fichaAdvertencias': 'Esto no bloquea, pero conviene mirarlo:',

  'ficha.publicName': 'Nombre público',
  'ficha.fictionalAge': 'Edad',
  'ficha.birthday': 'Cumpleaños',
  'ficha.countryNarrative': 'Ciudad y país',
  'ficha.niche': 'Nichos',
  'ficha.languages': 'Idiomas',
  'ficha.personalityTraits': 'Rasgos de personalidad',
  'ficha.backstory': 'Historia',
  'ficha.values': 'Valores',
  'ficha.interests': 'Intereses',
  'ficha.toneOfVoice': 'Tono de voz',
  'ficha.catchphrases': 'Muletillas',
  'ficha.prohibitedClaims': 'Afirmaciones prohibidas',
  'ficha.prohibitedClaimsAyuda':
    'Temas sobre los que nunca va a opinar con autoridad. Se respetan al escribir cada publicación.',

  'ficha.visualIdentity': 'Identidad visual',
  'ficha.skinTone': 'Tono de piel',
  'ficha.faceShape': 'Forma del rostro',
  'ficha.eyeColor': 'Color de ojos',
  'ficha.hairColor': 'Color de cabello',
  'ficha.hairStyle': 'Peinado',
  'ficha.bodyDescription': 'Complexión',
  'ficha.distinguishingFeatures': 'Rasgos distintivos',
  'ficha.defaultWardrobe': 'Vestuario habitual',
  'ficha.colorPalette': 'Paleta de color',
  'ficha.photographyStyle': 'Estilo fotográfico',

  'ficha.paraElGenerador': 'Instrucciones para el generador',
  'ficha.paraElGeneradorDetalle': 'en inglés · sólo si querés afinar el prompt',
  'ficha.paraElGeneradorPorQue':
    'Estos cuatro campos no describen a la influencer: le dicen al modelo de imagen qué ' +
    'dibujar. Van en inglés porque es el idioma en que ese modelo trabaja, y traducirlos ' +
    'empeora el resultado de forma medible. Si no vas a afinar el prompt, no hace falta que los ' +
    'toques.',

  'ficha.basePrompt': 'Descripción física',
  'ficha.bodyPrompt': 'Sólo de cuerpo entero',
  'ficha.bodyPromptAyuda':
    'Lo que no entra en un retrato: prótesis, silla de ruedas, tatuajes de brazo. Va aparte ' +
    'porque la descripción física se repite también en los primeros planos, y ahí un rasgo que ' +
    'no está en cuadro no se descarta — el generador lo reubica.',
  'ficha.negativePrompt': 'Qué evitar',
  'ficha.listaSeparada': 'Uno por línea',

  // ── Paso 3 ──
  'nueva.generar': 'Generar influencer',
  'nueva.generando': 'Generando…',
  'nueva.necesitaFicha': 'Escribí la ficha antes de generar.',
  // ── Panel de inicio ──
  'panel.titulo': 'Panel',
  'panel.cargando': 'Cargando el panel…',
  'panel.faltaConfig': 'Falta configuración obligatoria',
  'panel.modoCero': 'Modo USD 0',
  'panel.perfil': 'perfil',
  'panel.modoGasto': 'Modo de gasto habilitado · presupuesto USD {monto}',
  'panel.generarNueva': 'Generar nueva influencer',

  'panel.activas': 'Influencers activas',
  'panel.esperandoVerificacion': 'Esperando verificación',
  'panel.tareasPendientes': 'Tareas pendientes',
  'panel.requierenAccion': 'Requieren acción humana',
  'panel.nadaPendiente': 'Nada pendiente',
  'panel.programadas': 'Publicaciones programadas',
  'panel.proxima': 'Próxima {cuando}',
  'panel.ninguna': 'Ninguna',

  'panel.trabadaUna': '{n} publicación tiene hora puesta pero sigue en borrador',
  'panel.trabadasVarias': '{n} publicaciones tienen hora puesta pero siguen en borrador',
  'panel.vencidaUna': ', y una ya venció',
  'panel.vencidasVarias': ', y {n} ya vencieron',
  'panel.trabadasPorQue':
    'El cron sólo publica lo aprobado, así que van a quedarse ahí hasta que las apruebes desde ' +
    'la pestaña Contenido de cada influencer.',

  'panel.borradas': 'Borradas',
  'panel.borradasDetalle':
    'Siguen guardadas con sus imágenes y su ficha. Recuperarlas las devuelve al panel tal como ' +
    'estaban — salvo que se hayan borrado también las imágenes, que no se puede deshacer.',
  'panel.borradaEl': 'Borrada {cuando}',
  'panel.recuperar': 'Recuperar',

  'panel.cuotaHoy': 'Cuota gratuita de hoy',
  'panel.cuotaAgotada': 'Cuota diaria agotada',
  'panel.seReinicia': 'Se reinicia {cuando} ({fecha})',
  'panel.cuotaAgotadaDetalle':
    'Cloudflare dejó de aceptar peticiones. El ledger había estimado {usados} de {limite} ' +
    'créditos, pero la facturación real fue mayor: una generación rechazada consume cuota igual ' +
    'y las tarifas publicadas no cubren todo el costo.',
  'panel.sinTope': '{usados} créditos hoy, sin tope diario',
  'panel.contadorSeReinicia': 'El contador se reinicia {cuando}',
  'panel.creditosEstimados': '{usados} de {limite} créditos estimados',

  'panel.costo':
    'Con el perfil {perfil}, cada imagen cuesta {porImagen} créditos, y una influencer ' +
    'completa {porInfluencer}.',
  'panel.noEntraNinguna':
    'Hoy ya no entra ninguna más. Las corridas que dispares quedan en la cola diferida y ' +
    'arrancan solas al reiniciarse la cuota.',
  'panel.entraUna': 'Entra {n} influencer más hoy.',
  'panel.entranVarias': 'Entran {n} influencers más hoy.',
  'panel.desglose':
    'Desglose: {frontales} frontales · {hoja} hoja de identidad · {set} set final · {texto} texto',

  'panel.modelosDePago': 'Modelos de pago',
  'panel.creditosDe': '· {usados} de {limite} créditos',
  'panel.bolsaAparte':
    'Bolsa aparte: lo que se gasta acá no consume la cuota gratuita de Cloudflare.',

  'panel.erroresRecientes': 'Errores recientes',
  'panel.sinErrores': 'Sin errores registrados.',
  'panel.diferidas': 'Operaciones diferidas',
  'panel.nadaEnEspera': 'Nada en espera.',
  'panel.proveedoresTexto': 'Proveedores de texto',
  'panel.disponible': 'disponible',
  'panel.sinConfigurar': 'sin configurar',

  'panel.influencers': 'Influencers',
  'panel.mostrarFallidaUna': 'Mostrar {n} fallida o incompleta',
  'panel.mostrarFallidas': 'Mostrar {n} fallidas o incompletas',
  'panel.ocultarFallidaUna': 'Ocultar {n} fallida o incompleta',
  'panel.ocultarFallidas': 'Ocultar {n} fallidas o incompletas',
  'panel.sinInfluencers': 'Todavía no hay influencers',
  'panel.ningunaCompleta': 'Ninguna influencer completa',
  'panel.pulsaGenerar': 'Pulsá «Generar nueva influencer» para crear la primera.',
  'panel.todasIncompletas':
    'Las {n} que hay fallaron o quedaron a medias. Mostralas con el enlace de arriba.',
  'panel.seguidores': 'seguidores',
  'panel.sinNicho': 'Sin nicho',
  'panel.abrirPerfil': 'Abrir @{usuario} en {plataforma}',
  'panel.tareaUna': '{n} tarea',
  'panel.tareas': '{n} tareas',
  // ── Tiempos y componentes comunes ──
  'tiempo.nunca': 'nunca',
  'tiempo.recien': 'recién',
  'tiempo.haceMin': 'hace {n} min',
  'tiempo.haceH': 'hace {n} h',
  'tiempo.ayer': 'ayer',
  'tiempo.haceDias': 'hace {n} días',
  'tiempo.haceUnMes': 'hace un mes',
  'tiempo.haceMeses': 'hace {n} meses',
  'tiempo.yaDisponible': 'ya disponible',
  'tiempo.enHM': 'en {h} h {m} min',
  'tiempo.enM': 'en {m} min',

  'comun.copiar': 'Copiar',
  'comun.cuotaUsada': 'Cuota usada: {n} por ciento',
  'comun.cerrarAviso': 'Cerrar el aviso',
  // ── Correo recibido ──
  'correo.soloPublicidad':
    'Sólo llegó publicidad. Lo que se espera acá es un enlace de confirmación.',
  'correo.publicidad': 'Publicidad · {n}',
  'correo.publicidadDetalle': 'campañas de la plataforma, no hacen falta para ningún paso',
  'correo.sinAsunto': '(sin asunto)',
  'correo.ocultarMensaje': 'Ocultar el mensaje',
  'correo.verMensaje': 'Ver el mensaje completo',
  'correo.descartar': 'Descartar',

  // ── Estadísticas ──
  'stats.leyendo': 'Leyendo métricas…',
  'stats.sinMetricas': 'Todavía no hay métricas.',
  'stats.ratio': 'Seguidores por cada cuenta seguida',
  'stats.ultimos7': 'Últimos 7 días',
  'stats.evolucion': 'Cómo evoluciona',
  'stats.sinMediciones':
    'Todavía no hay ninguna medición guardada. Se toma una por día, así que el primer gráfico ' +
    'aparece en un par de días.',
  'stats.unaMedicion': 'Hay una sola medición. Con dos ya se puede dibujar la línea: volvé mañana.',
  'stats.referencias': 'Referencias que seguís · {n}',
  'stats.agregalas': 'Agregalas acá.',

  // ── Páginas públicas ──
  'paginas.titulo': 'Páginas públicas',
  'paginas.bajada': 'Quién entra a {dominio} y adónde va desde ahí.',
  'paginas.abiertas': 'Páginas abiertas',
  'paginas.distintos': 'Distintos, por día',
  'paginas.clics': 'Clics a las redes',
  'paginas.nadieEntro': 'Todavía no entró nadie',
  'paginas.ningunaPublicada':
    'Ninguna influencer tiene su página publicada. Se genera desde su pestaña Tareas.',
  'paginas.nadieLasAbrio':
    'Las páginas están publicadas pero nadie las abrió en este período. La dirección va en la ' +
    'biografía de cada red.',
  'paginas.porDia': 'Por día',
  'paginas.todasLasInfluencers': 'Todas las influencers',
  'paginas.experimento': 'Experimento A/B/C',
  'paginas.experimentoQue':
    'A qué destino conviene mandar a la gente. Lo que se compara es el porcentaje que ' +
    'pulsó, no los clics: una rama con más visitas tiene más clics sin ser mejor.',
  'paginas.ramaA': 'A · sólo Fanvue',
  'paginas.ramaB': 'B · sólo Telegram',
  'paginas.ramaC': 'C · las dos',
  'paginas.rama-': 'Antes del experimento',
  'paginas.ramaCuenta': '{visitas} visitas · {clics} clics',
  'paginas.porInfluencer': 'Por influencer',
  'paginas.deDondeVienen': 'De dónde vienen',
  'paginas.directo': 'Directo o sin referente',
  'paginas.sinDatos': 'Sin datos todavía.',
  'paginas.desdeQuePais': 'Desde qué país',
  'paginas.sinDeterminar': 'Sin determinar',
  'paginas.adondeVan': 'Adónde van',
  'paginas.nadieSiguio': 'Nadie siguió ningún enlace todavía.',
  'stats.volverPlataformas': '← Plataformas',
  'stats.sinMetricasTitulo': 'Todavía no hay métricas.',
  'stats.sinMetricasDetalle':
    'O no hay ninguna cuenta conectada, o son tan nuevas que Meta no tiene nada que medir. Las ' +
    'dos cosas se arreglan solas: conectalas en Plataformas y publicá.',
  'stats.seguidores': 'Seguidores',
  'stats.seguidos': 'Seguidos',
  'stats.publicaciones': 'Publicaciones',
  'stats.ratioTitulo': 'Ratio',
  'stats.alcance': 'Alcance',
  'stats.vistas': 'Vistas',
  'stats.interacciones': 'Interacciones',
  'stats.cuentasAlcanzadas': 'Cuentas alcanzadas',
  'stats.totalesSuman':
    'Los totales suman Instagram y Facebook. Facebook informa menos campos que Instagram —no ' +
    'dice a cuántas cuentas sigue una Página ni su alcance—, así que donde figura «—» el dato ' +
    'no existe, no es cero.',
  'stats.pasadoNoVuelve':
    'El pasado no se puede recuperar —Meta no lo da—, así que la serie arranca el día que se ' +
    'empezó a medir.',
  'stats.lineasSuman':
    'Las líneas suman las dos plataformas. Un día donde sólo se pudo medir una cuenta muestra ' +
    'únicamente esa: por eso puede haber escalones.',
  'stats.sinReferencias': 'No agregaste ninguna cuenta de referencia todavía.',
  'stats.deDondeSon':
    'De dónde son las cuentas que mirás. El solapamiento de seguidores no se puede calcular: ' +
    'Instagram no expone la lista de seguidores de ninguna cuenta, ni la propia.',
  'stats.seg': 'seg.',

  'paginas.dias': '{n} días',
  'paginas.visitas': 'Visitas',
  'paginas.visitantes': 'Visitantes',
  'paginas.filaDia': '{visitas} · {personas} pers. · {clics} clics',
  'paginas.filaInfluencer': '{visitas} visitas · {personas} pers. · {clics} clics',
  'paginas.queSeGuarda':
    'No se guarda ninguna IP, ni agente de usuario, ni cookie. «Visitantes» sale de una huella ' +
    'irreversible que cambia todos los días, así que distingue dos visitas de hoy y no permite ' +
    'seguir a nadie entre días. El país lo pone Cloudflare en el borde y del referente sólo se ' +
    'guarda el dominio. Todo se borra a los 90 días.',

  // ── Laboratorio ──
  'lab.titulo': 'Laboratorio',
  'lab.bajada':
    'Sondas contra proveedores. {gastan} y no pasan por los topes de consumo: son mediciones, no producción.',
  'lab.gastan': 'Gastan dinero real',
  'lab.comparar': 'Laboratorio · comparar modelos de imagen',
  'lab.mandaAntes': 'Manda ',
  'lab.mandaFuerte': 'el mismo prompt, las mismas referencias de identidad y la misma semilla',
  'lab.mandaDespues':
    ' a varios modelos. La única variable es el modelo, así que la diferencia que se vea es ' +
    'suya. Compararlos de a uno en tandas distintas no sirve: cambia la escena, cambia la ' +
    'semilla y cambia el recuerdo de quien mira.',
  'lab.cuestaAntes':
    'Cuesta una imagen por modelo, entre cuatro y seis centavos cada una — los de Cloudflare ' +
    'entran en la cuota gratuita. Las imágenes se guardan pero ',
  'lab.noEnGaleria': 'no aparecen en la galería',
  'lab.cuestaDespues': ': son mediciones, no contenido que alguien eligió.',
  'lab.influencer': 'Influencer',
  'lab.elegiUna': 'Elegí una…',
  'lab.pegarOtro': 'runware/civitai:257749@290640 (pegá cualquier otro)',
  'lab.agregar': 'Agregar',
  'lab.generando': 'Generando…',
  'lab.compararBoton': 'Comparar',

  // ── Cuentas ──
  'cuentas.titulo': 'Cuentas',
  'cuentas.bajada':
    'Cada cuenta ve sólo sus influencers y usa sólo sus credenciales. Comparten el panel, no el ' +
    'saldo.',
  'cuentas.cargando': 'Cargando cuentas…',
  'cuentas.crear': 'Crear cuenta',
  'cuentas.nueva': 'Nueva cuenta',
  'cuentas.nombre': 'Nombre',
  'cuentas.nombreEjemplo': 'Nombre y apellido',
  'cuentas.password': 'Contraseña · mínimo 12 caracteres',
  'cuentas.passwordEjemplo': 'la que le vas a pasar',
  'cuentas.generarTitulo': 'Genera una contraseña fuerte y legible',
  'cuentas.generar': 'Generar',
  'cuentas.copiarTitulo': 'Copiar al portapapeles',
  'cuentas.seGuardaCifrada':
    'Se guarda cifrada y no se puede volver a ver: copiala antes de crear la cuenta.',
  'cuentas.creando': 'Creando…',
  'cuentas.crearBoton': 'Crear',
  'cuentas.desde': '{correo} · desde {cuando}',
  'cuentas.sinGuia': ' · todavía no terminó la guía',
  'cuentas.confirmarPassword':
    'Nueva contraseña para {quien}:\n\n{clave}\n\nCopiala ahora: no se puede volver a ver. ' +
    '¿Confirmás el cambio?',
  'cuentas.passwordCambiada': 'Contraseña cambiada, y copiada al portapapeles.',
  'cuentas.nuevaPassword': 'nueva contraseña',
  'cuentas.confirmarQuitar': '¿Quitar la cuenta de {quien}?',
  'cuentas.quitar': 'quitar',
  'cuentas.correoEsIdAntes':
    'El correo es sólo el identificador para entrar: no se envía nada ni se verifica. Por eso ',
  'cuentas.sinRecuperacion': 'no hay recuperación de contraseña',
  'cuentas.correoEsIdDespues':
    ' — si alguien la olvida, usá «nueva contraseña», que conserva su cuenta y todo lo que hizo.',
  'cuentas.quitarNoBorraAntes':
    'Quitar una cuenta no borra sus influencers ni sus imágenes: quedan sin dueño con sesión, así ' +
    'que dejan de verse. Y volver a crearla con el mismo correo ',
  'cuentas.noLasRecupera': 'no las recupera',
  'cuentas.quitarNoBorraDespues':
    ': el identificador es nuevo. Por eso conviene cambiar la contraseña en vez de borrar y recrear.',

  'rol.operator': 'Operador',
  'rol.operatorQue': 'Crea y opera sus propias influencers. No ve el laboratorio.',
  'rol.admin': 'Administrador',
  'rol.adminQue': 'Todo lo anterior, más el laboratorio y estas cuentas.',
  'rol.viewer': 'Lectura',
  'rol.viewerQue': 'Sólo mira. Pensado para alguien que revisa sin tocar.',

  // ── Plataformas ──
  'plat.titulo': 'Plataformas',
  'plat.bajada': 'Todas las influencers juntas, para ver a cuál le va mejor.',
  'plat.resumen': 'Resumen',
  'plat.medirAhora': 'Medir ahora',
  'plat.midiendo': 'Midiendo…',
  'plat.medidas': '{n} cuentas medidas.',
  'plat.noSePudoMedir': 'No se pudieron leer las métricas.',
  'plat.alcance': 'Alcance',
  'plat.piezas': 'Piezas en las cuentas',
  'plat.publicadas': 'Publicadas',
  'plat.pendientes': 'Pendientes de publicar',
  'plat.seguidores': 'Seguidores',
  'plat.seguidos': 'Seguidos',
  'plat.sumaDeCuentaUna': 'suma de {n} cuenta conectada',
  'plat.sumaDeCuentas': 'suma de {n} cuentas conectadas',
  'plat.sinMetricas':
    '{cuantas} de {total} no devolvieron métricas de Instagram. Sus columnas de seguidores y ' +
    'alcance quedan vacías —también en el resumen, donde se suma lo que sí respondió—; lo ' +
    'publicado y lo pendiente sale de nuestra base y sigue siendo exacto.',
  'plat.comparativa': 'Comparativa',
  'plat.ordenarPor': 'Ordenar por',
  'plat.confirmarQuitar': '¿Quitar {quien} de la comparativa?',
  'plat.quitar': 'quitar',
  'plat.externa': 'No la genera este sistema: se conecta para medirla',
  'plat.cuenta': 'Cuenta',
  'plat.laPagina': 'la Página',
  'plat.conectarOAuth': 'Conectar por OAuth',
  'plat.ratio': 'Ratio',
  'plat.ratioTitulo': 'Seguidores por cada cuenta seguida',
  'plat.enFacebook': 'En Facebook',
  'plat.enInstagram': 'En Instagram',
  'plat.enSusCuentas': 'En sus cuentas',
  'plat.notaRatio':
    ' son seguidores por cada cuenta seguida: separa a la que creció porque la buscaron de la ' +
    'que creció devolviendo seguimientos. Por encima de 1× va en verde.',
  'plat.notaFuentes':
    ' los devuelve Meta en el momento de abrir esta pantalla. Publicadas y pendientes salen de ' +
    'nuestra base: son exactas y existen aunque la cuenta no esté conectada.',
  'plat.agregarExterna': 'Agregar influencer externa',
  'plat.externaQueAntes': 'Una cuenta que ya existe y que este sistema no genera. Agrega ',
  'plat.externaSoloFila': 'sólo una fila acá',
  'plat.externaQueDespues':
    ' —no aparece en la lista de influencers ni tiene galería, contenido ni consumo— y desde esa ' +
    'fila la conectás con «Conectar por OAuth». Instagram sólo entrega seguidores y alcance de ' +
    'cuentas cuyo dueño autoriza el acceso, así que tiene que ser una que administres. Para ' +
    'seguir la cuenta de otra persona está Referencias.',
  'plat.nombrePublico': 'Nombre público',
  'plat.usuarioIg': '@usuario de Instagram (opcional)',
  'plat.agregando': 'Agregando…',
  'plat.agregar': 'Agregar',

  // ── Ficha de la influencer: pestañas ──
  'tab.resumen': 'Resumen',
  'tab.identidad': 'Identidad',
  'tab.galeria': 'Galería',
  'tab.contenido': 'Contenido',
  'tab.lora': 'LoRA',
  'tab.plataformas': 'Plataformas',
  'tab.estadisticas': 'Estadísticas',
  'nav.estadisticas': 'Estadísticas',
  'nav.herramientas': 'Herramientas',
  'tab.descargadores': 'Descargadores',
  'ventas.bajada':
    'Cada foto de pago que alguien desbloqueó en Telegram. El total dice si el canal funciona; ' +
    'la lista dice cuál de las fotos lo hizo funcionar.',
  'ventas.porInfluencer': 'Por influencer',
  'ventas.cuantasQue': 'fotos desbloqueadas',
  'ventas.compradoresQue': 'personas distintas',
  'ventas.filaInfluencer': '{ventas} ventas · {compradores} personas',
  'herr.titulo': 'Descargadores',
  'herr.bajada':
    'Enlaces a herramientas de afuera que usás a mano. No son leads: acá no se mide nada, es un ' +
    'cajón para no volver a buscarlas.',
  'herr.cargando': 'Buscando tus herramientas…',
  'herr.agregar': 'Agregar una',
  'herr.agregarBoton': 'Agregar',
  'herr.guardando': 'Guardando…',
  'herr.agregada': 'Guardada.',
  'herr.nombre': 'Nombre',
  'herr.nombreEjemplo': 'Descargador de Instagram',
  'herr.url': 'Dirección',
  'herr.nota': 'Nota',
  'herr.notaEjemplo': 'para qué la usás',
  'herr.listaTitulo': 'Tus herramientas',
  'herr.quitar': 'Quitar',
  'herr.ninguna': 'Todavía no guardaste ninguna',
  'herr.ningunaQue':
    'Pegá arriba la dirección de la que uses y queda acá. Se abren en otra pestaña; el sistema no ' +
    'las ejecuta ni les manda nada.',
  'tab.paginas': 'Páginas',
  'tab.ventas': 'Ventas',
  'ventas.contando': 'Contando ventas…',
  'ventas.ninguna': 'Todavía no vendió nada',
  'ventas.ningunaQue':
    'Acá aparece cada compra de una foto de pago en Telegram: cuál, quién y cuándo. Si publicaste ' +
    'cobrando y no aparece nada, revisá el aviso de ventas en Credenciales → Telegram.',
  'ventas.cuantas': 'Ventas',
  'ventas.enStars': 'Cobrado',
  'ventas.compradores': 'Compradores',
  'ventas.listaTitulo': 'Una por una',
  'ventas.compradaPor': 'Comprada por {quien} · {fecha}',
  'ventas.sinPublicacion': 'La publicación ya no existe',
  'tab.correo': 'Correo',
  'tab.tareas': 'Tareas',
  'tab.workflow': 'Workflow',
  'tab.consumo': 'Consumo',
  'tab.auditoria': 'Auditoría',
  'tab.secciones': 'Secciones',

  // ── Ficha: cabecera y borrado ──
  'ficha.cargando': 'Cargando la influencer…',
  'ficha.edadFicticia': ' · {n} años (ficticia)',
  'ficha.reanudar': 'Reanudar',
  'ficha.pausar': 'Pausar',
  'ficha.borrar': 'Borrar',
  'ficha.borrarA': 'Borrar a {quien}',
  'ficha.borrarQueAntes': 'Desaparece del panel y de las listas. ',
  'ficha.borrarSePuedeRecuperar': 'Se puede recuperar',
  'ficha.borrarQueDespues':
    ': las imágenes, la ficha y las publicaciones quedan guardadas, y la influencer vuelve desde ' +
    'Inicio → «Borradas». Lo publicado en Instagram no se toca.',
  'ficha.escribiParaConfirmarAntes': 'Escribí ',
  'ficha.escribiParaConfirmarDespues': ' para confirmar',
  'ficha.recuperarEspacio': '¿Necesitás recuperar espacio de almacenamiento?',
  'ficha.borrarTambienImagenes': 'Borrar también las imágenes',
  'ficha.noSePuedeDeshacer': 'no se puede deshacer',
  'ficha.purgaQueDespues': ': la influencer se podría recuperar pero volvería sin ninguna foto.',

  // ── Ficha: resumen ──
  'ficha.referenciaCanonica': 'Referencia canónica',
  'ficha.referenciaVisualDe': 'Referencia visual de {quien}',
  'ficha.seAdjunta': 'Se adjunta como imagen de referencia a cada generación posterior',
  'ficha.sinReferencia': 'Sin referencia elegida',
  'ficha.sinReferenciaQue':
    'Entrá en la pestaña Galería y elegí cuál de las candidatas será la referencia canónica.',
  'ficha.estado': 'Estado',
  'ficha.imagenesGeneradas': 'Imágenes generadas',
  'ficha.enCalendario': 'Contenidos en calendario',
  'ficha.tareasPendientes': 'Tareas pendientes',
  'ficha.versionDeLaFicha': 'Versión de la ficha',
  'ficha.proximaAccion': 'Próxima acción humana',
  'ficha.sinFicha': 'Sin ficha de personaje',
  'ficha.sinFichaQue': 'Todavía no se generó.',

  // ── Ficha: identidad ──
  'ident.estiloVisual': 'Estilo visual',
  'ident.registro': 'Registro fotográfico',
  'api.sinConexion': 'No se pudo conectar con el servidor.',
  // ── Opciones del perfil creativo ──────────────────────────────────────────
  'perfil.estilo.veinte': 'Veintipocos, sin producción',
  'perfil.estilo.veinte.det':
    'Cara lavada, pecas, pelo sin peinar, ropa de casa. Como un selfie de verdad.',
  'perfil.estilo.producida': 'Adulta con estilo',
  'perfil.estilo.producida.det':
    'Maquillaje hecho, peinado armado, ropa elegida. Se nota la producción.',
  'perfil.reg.natural': 'Natural',
  'perfil.reg.natural.det':
    'Cámara real, sin retoque. Se ve la piel. Es el que menos se nota generado.',
  'perfil.reg.editorial': 'Editorial',
  'perfil.reg.editorial.det':
    'Producción de moda: luz controlada, vestuario armado. Más pulido, más artificial.',
  'perfil.reg.amateur': 'Celular',
  'perfil.reg.amateur.det':
    'Foto sacada con el teléfono: flash directo, encuadre imperfecto, algo de ruido.',
  'perfil.piel.calle': 'Calle',
  'perfil.piel.calle.det': 'Ropa cotidiana, siempre cubierta.',
  'perfil.piel.deportivo': 'Deportivo',
  'perfil.piel.deportivo.det': 'Top y calzas cuando la escena es de entrenamiento.',
  'perfil.piel.playa': 'Playa',
  'perfil.piel.playa.det': 'Traje de baño donde el entorno lo justifica.',
  'perfil.piel.sensual': 'Sensual',
  'perfil.piel.sensual.det':
    'Lencería e interiores, insinuado y nunca explícito. Genera con fal en vez de Google, porque el filtro de Google lo rechaza y cobra el intento igual.',
  'perfil.piel.atrevido': 'Atrevido',
  'perfil.piel.atrevido.det':
    'Tanga y corte alto, vistas de espalda, plano entero. Mismo límite: sin desnudez ni explicitud. Es el registro con más rechazos del proveedor.',
  'perfil.cuerpo.sin': 'Sin preferencia',
  'perfil.cuerpo.sin.det': 'Lo elige el modelo al escribir la ficha.',
  'perfil.cuerpo.atletico': 'Atlética',
  'perfil.cuerpo.atletico.det': 'Tonificada, de alguien que entrena.',
  'perfil.cuerpo.curvilineo': 'Curvilínea',
  'perfil.cuerpo.curvilineo.det':
    'Cintura estrecha y caderas marcadas, figura de reloj de arena. En forma.',
  'perfil.cuerpo.delgado': 'Delgada',
  'perfil.cuerpo.delgado.det': 'Estilizada, de líneas largas.',
  'perfil.tono.neutro': 'Neutro',
  'perfil.tono.neutro.det': 'Color fiel, sin corrección.',
  'perfil.tono.calido': 'Cálido',
  'perfil.tono.calido.det': 'Dorados y ámbar.',
  'perfil.tono.frio': 'Frío',
  'perfil.tono.frio.det': 'Azules y grises.',
  'perfil.tono.contraste': 'Contraste',
  'perfil.tono.contraste.det': 'Sombras profundas, color saturado.',
  'perfil.calidad.limpia': "Buena",
  'perfil.calidad.limpia.det': "La de siempre. Foto nítida, bien expuesta, color vivo de teléfono moderno.",
  'perfil.calidad.cruda': "Cámara mala",
  'perfil.calidad.cruda.det': "Como una foto sacada sin pensar y mandada al toque: con grano, algo movida, medio desenfocada, baja resolución y color apagado. Es lo que más la hace parecer real. Va con cualquier luz — con «Interior de noche» es la foto de la cama; con «Luz de ventana» o «Hora dorada», la misma cámara mala pero de día.",
  'perfil.escena.mezcla': 'Mezcla',
  'perfil.escena.mezcla.det': 'Alterna interiores y exteriores.',
  'perfil.escena.interiores': 'Interiores',
  'perfil.escena.interiores.det': 'Casa, estudio, gimnasio techado.',
  'perfil.escena.exteriores': 'Exteriores',
  'perfil.escena.exteriores.det': 'Calle, parque, playa, terraza.',
  'perfil.luz.frontal': "Frontal de selfie",
  'perfil.luz.frontal.det': "La de siempre. Luz abundante y de frente, sin sombra al costado, como una selfie sacada a un brazo contra una ventana. Cara plana y bien iluminada. Casera.",
  'perfil.luz.flash': "Flash de celular",
  'perfil.luz.flash.det': "De noche, con el flash directo: ella clara y el fondo mucho más oscuro, con la sombra dura pegada en la pared. La más reconocible de todas, y la más casera.",
  'perfil.luz.noche': "Interior de noche",
  'perfil.luz.noche.det': "Lámparas cálidas, poca luz, algo de grano en las sombras. Más oscura que el resto. Casera.",
  'perfil.luz.ventana': "Luz de ventana",
  'perfil.luz.ventana.det': "Entra de un solo lado: deja sombra bajo el pómulo y el resto del cuarto más oscuro. Es la que más volumen da sin parecer producida.",
  'perfil.luz.contraluz': "A contraluz",
  'perfil.luz.contraluz.det': "La ventana detrás: fondo quemado, cara iluminada por rebote, algo de velo y destello. Queda linda y se nota buscada.",
  'perfil.luz.tarde': "Hora dorada",
  'perfil.luz.tarde.det': "Sol bajo de la tarde, sombras largas, borde dorado en el pelo. Exteriores. Producida.",
  'perfil.luz.neon': "Neón de noche",
  'perfil.luz.neon.det': "Ciudad de noche: rosa y celeste sobre la piel, fondo oscuro, balance de blancos mezclado. Producida.",
  'perfil.luz.estudio': "Estudio",
  'perfil.luz.estudio.det': "Softbox grande con relleno, exposición pareja, sombras limpias. La más profesional, y también la que menos se parece a lo que publica una persona.",

  'ident.estiloEtario': 'Estilismo',
  'ident.nivelDePiel': 'Nivel de piel',
  'ident.complexion': 'Complexión',
  'ident.tonoDelFeed': 'Tono del feed',
  'ident.escenas': 'Escenas',
  'ident.iluminacion': 'Iluminación',
  'ident.calidad': 'Calidad',
  'ident.guardadoYReescrito':
    'Guardado, y la descripción física se reescribió con la complexión nueva. Las imágenes que ' +
    'ya existen tienen el cuerpo anterior: regenerá las frontales y la hoja de identidad en la ' +
    'Galería para que coincidan.',
  'ident.guardadoSinReescribir':
    'Guardado. La ficha no se pudo reescribir; revisá que haya proveedor de texto.',
  'ident.guardadoSinRehacer':
    'Guardado. Se aplica a lo que generes de acá en adelante; no hay nada que rehacer.',
  'ident.esPersonaje':
    'Es un personaje no humano: el nivel de piel, la complexión y el tipo de escena no se ' +
    'aplican. El generador tiene prohibido vestirlo o ponerle objetos humanos, y las escenas ' +
    'salen del mundo donde vive — no de interiores o exteriores, que son categorías de una casa.',
  'ident.cambiaCuerpoAntes':
    'Cambiar la complexión reescribe la descripción física de la ficha, conservando rostro, pelo ' +
    'y tono de piel. ',
  'ident.cambiaCuerpoFuerte': 'Las imágenes que ya existen tienen el cuerpo anterior',
  'ident.cambiaCuerpoDespues':
    ': vas a tener que regenerar las frontales y la hoja de identidad para que coincidan.',
  'ident.guardarCambios': 'Guardar cambios',
  'ident.ficha': 'Ficha',
  'ident.guardarFicha': 'Guardar ficha',
  'ident.fichaGuardada': 'Ficha guardada.',
  'ident.noSePudoGuardarFicha': 'No se pudo guardar la ficha.',
  'ident.noSePudoGuardar': 'No se pudo guardar.',
  'ident.versionNuevaAntes':
    'Se guarda como versión nueva; las anteriores quedan porque explican con qué texto salió ' +
    'cada imagen ya generada. Cambiar la ',
  'ident.descripcionFisica': 'descripción física',
  'ident.versionNuevaDespues':
    ' no rehace lo que ya existe: para que coincida hay que regenerar las frontales y la hoja ' +
    'de identidad.',
  'ident.editar': 'editar',

  // ── Ficha: galería ──
  'gal.accionFallo': 'No se pudo completar la acción.',
  'gal.ultimasDe': 'últimas {n} de {total}',
  'gal.confirmarCambioFrontal':
    'La hoja de identidad se generó con la frontal actual adjunta como referencia. Si cambiás la ' +
    'frontal, la hoja va a seguir siendo la cara anterior: hay que regenerar sus tres ángulos, y ' +
    'lo que ya se haya generado a partir de ella también.\n\nNada se borra, y podés volver a ' +
    'elegir la anterior cuando quieras.\n\n¿Cambiar la frontal?',
  'gal.generando':
    'Generando imágenes. Tarda alrededor de un minuto y aparecen solas — no hace falta recargar ' +
    'ni volver a pulsar.',
  'gal.interrumpida': 'La generación se interrumpió',
  'gal.interrumpidaSet':
    'Las tomas que ya aparecieron están guardadas. Pulsá «Completar set» para generar únicamente ' +
    'las que faltan.',
  'gal.interrumpidaHoja':
    'Los ángulos que ya aparecieron están guardados. Pulsá «Completar hoja» para generar ' +
    'únicamente los que faltan.',
  'gal.etapa1': 'Etapa 1 · Elegí la frontal canónica',
  'gal.etapa1Que':
    'La que elijas se adjunta como imagen de referencia a todo lo que venga después. No es una ' +
    'foto para publicar: es la cara que el modelo va a repetir.',
  'gal.frontalElegida': 'Frontal elegida',
  'gal.frontalElegidaQue':
    'Las demás se conservan con su procedencia. Podés cambiar de frontal cuando quieras: elegí ' +
    'otra y se reemplaza',
  'gal.frontalElegidaConHoja':
    ' — la hoja de identidad se generó con la actual, así que habría que regenerarla.',
  'gal.sinCandidatas': 'Todavía no se generaron candidatas.',
  'gal.faltaFrontal': 'Falta elegir la frontal',
  'gal.faltaFrontalQue':
    'La hoja de identidad se genera a partir de la cara que elijas en la etapa 1.',
  'gal.etapa2Generar': 'Etapa 2 · Generá la hoja de identidad',
  'gal.etapa2FaltaUno': 'Etapa 2 · Falta {n} ángulo',
  'gal.etapa2FaltanVarios': 'Etapa 2 · Faltan {n} ángulos',
  'gal.hojaAprobada': 'Hoja aprobada',
  'gal.etapa2Revisa': 'Etapa 2 · Revisá y aprobá la hoja',
  'gal.etapa2GenerarQue':
    'Tres ángulos —tres cuartos, perfil y plano medio— derivados de la frontal elegida. Es la ' +
    'memoria visual del personaje: se adjunta a cada imagen final para que la cara no derive.',
  'gal.etapa2FaltanQue':
    'Ya hay {n} de 3 ángulos guardados. Al continuar no se repiten: sólo se generan los que faltan.',
  'gal.hojaAprobadaQue':
    'Estos tres ángulos se adjuntan, junto con la frontal, a cada imagen del set final.',
  'gal.etapa2RevisaQue':
    'Mirá que las tres sean la misma persona que la frontal. Regenerá la que no lo sea: si ' +
    'aprobás una hoja floja, todo el set final sale flojo.',
  'gal.generandoBoton': 'Generando…',
  'gal.generarHoja': 'Generar hoja de identidad',
  'gal.completarHoja': 'Completar hoja',
  'gal.aprobando': 'Aprobando…',
  'gal.aprobarHoja': 'Aprobar hoja',
  'gal.confirmarRehacerHoja':
    'Se van a descartar los tres ángulos actuales y generar una hoja nueva desde la frontal ' +
    'canónica.\n\nLa hoja es la referencia de identidad que se adjunta a TODO el contenido que ' +
    'se genere después: con una hoja nueva, lo próximo va a parecerse a ella y no al contenido ' +
    'que ya tenés.\n\nLos ángulos viejos quedan descartados, no borrados: vas a poder mirarlos ' +
    'para comparar.\n\n¿Rehacer la hoja?',
  'gal.rehaciendo': 'Rehaciendo…',
  'gal.rehacerHoja': '↻ Rehacer hoja completa',
  'gal.sinHoja': 'Todavía no se generó la hoja de identidad.',
  'gal.frontales': 'Frontales',
  'gal.frontalesQue': 'elegís una',
  'gal.hojaDeIdentidad': 'Hoja de identidad',
  'gal.hojaQue': 'revisás y aprobás',
  'gal.cuerpo': 'Cuerpo entero',
  'gal.cuerpoQue': 'para los planos lejanos',
  'gal.etapa3': 'Falta la referencia de cuerpo entero',
  'gal.etapa3Que':
    'La canónica es un recorte de cara, y el modelo hereda el encuadre de la referencia: sin ' +
    'una foto de cuerpo entero, las tomas de plano entero salen encuadradas de cerca igual. ' +
    'Se genera de a una porque sale a 1920×2880 y tarda. Quedate con la que se le vea la ' +
    'figura completa y siga siendo ella; si no convence, generá otra.',
  'gal.cuerpoElegido': 'Referencia de cuerpo elegida',
  'gal.cuerpoElegidoQue':
    'Se manda en lugar de la cara cuando la toma es de plano entero o de plano general. En los ' +
    'primeros planos sigue mandando la canónica: ahí ésta es peor, porque la cara ocupa cien ' +
    'píxeles.',
  'gal.generarCuerpo': 'Generar una',
  'gal.desdeFoto': 'Derivar de una foto',
  'gal.limpiarCandidatas': 'Borrar las {n} no elegidas',
  'gal.limpiarHoja': 'Borrar las {n} no aprobadas',
  'gal.limpiarHojaConfirmar':
    'Se borran {n} tomas de la hoja y sus archivos, sin vuelta atrás. Las aprobadas no se tocan.',
  'gal.limpiarCandidatasConfirmar':
    'Se borran {n} candidatas y sus archivos, sin vuelta atrás. La canónica no se toca.',
  'gal.desdeFotoElegi':
    'La cara de esa foto está girada, así que además se generaron tres frontales. Elegí abajo ' +
    'cuál queda de canónica: el recorte conserva mejor el parecido, las generadas quedan de ' +
    'frente. La hoja de identidad y la referencia de cuerpo hay que rehacerlas después.',
  'gal.derivando': 'Recortando…',
  'gal.desdeFotoQue':
    'Si ya tenés una publicación que funciona, sacá la canónica de ahí: se le recorta la cara y ' +
    'se escala, sin pasarla por ningún modelo. Medido, recortar conserva el parecido (0,95) y ' +
    'regenerar lo pierde (0,54). La foto tiene que tener la cara bien visible.',
  'gal.desdeFotoHecho':
    'Canónica cambiada. La hoja de identidad y la referencia de cuerpo salieron de la anterior, ' +
    'así que ahora describen otra cara: conviene rehacer las dos antes de generar contenido.',
  'gal.subirCuerpo': 'Subir una foto',
  'gal.formaTitulo': 'Forma del cuerpo',
  'gal.formaQue':
    'La silueta la dibuja un LoRA, no el texto: sin él sale recta aunque el prompt pida ' +
    'caderas. Este es el peso que le toca a ella por su perfil «{perfil}» y su nivel ' +
    '«{nivel}». Cambialo y volvé a generar para ver la diferencia.',
  'gal.formaMedido':
    'Medido: 0,8 iguala la referencia anterior y 1,0 empieza a estilizar. Los valores entre ' +
    'medio no están medidos.',
  'gal.formaSinLora': 'Sin LoRA de forma',
  'gal.formaGuardando': 'Guardando…',
  'gal.formaAfecta': 'Afecta a toda influencer con el mismo perfil y nivel.',
  'gal.subiendoCuerpo': 'Subiendo…',
  'gal.subirCuerpoQue':
    'Si ya tenés una foto de cuerpo entero de ella, subila: es mejor que cualquiera generada, porque la figura es la de verdad y no una que el modelo inventó. Queda elegida al subirla.',
  'gal.generarMasCuerpo': 'Generar otra',
  'gal.sinCuerpos': 'Todavía no generaste ninguna.',
  'gal.limpiarCuerpo': 'Borrar las {n} no elegidas',
  'gal.limpiando': 'Borrando…',
  'gal.limpiarCuerpoConfirmar':
    'Se borran {n} imágenes y sus archivos, sin vuelta atrás. La elegida no se toca.',
  'gal.etapas': 'Etapas de la generación',

  // ── Ficha: tarjeta de imagen y rejilla ──
  'gal.sinImagenes': 'Sin imágenes en esta etapa',
  'gal.nadaActivo': 'Nada activo',
  'gal.nadaActivoQue':
    'Todas las imágenes de esta etapa están descartadas. Generá de nuevo o mirá el historial.',
  'gal.descartadaUna': '1 imagen descartada',
  'gal.descartadasVarias': '{n} imágenes descartadas',
  'gal.confirmarBorrar':
    'Se va a borrar el archivo de esta imagen ({kb} kB). No se puede deshacer.\n\nSu ' +
    'procedencia —prompt, semilla, modelo y créditos— se conserva en el historial, así que el ' +
    'consumo del día se sigue pudiendo explicar.\n\n¿Eliminar?',
  'asset.alt': '{tipo} — generada con {modelo}',
  'asset.canonica': '★ Referencia canónica',
  'asset.modelo': 'Modelo',
  'asset.semilla': 'Semilla',
  'asset.creditos': 'Créditos',
  'asset.verPrompt': 'Ver prompt',
  'asset.usarEnSuLugar': '★ Usar esta en su lugar',
  'asset.usarComoReferencia': '★ Usar como referencia',
  'asset.aprobar': '✓ Aprobar',
  'asset.descartar': '✗ Descartar',
  'asset.sinSemilla':
    'Esta imagen no guardó su semilla, así que la nueva no va a ser comparable: va a cambiar la ' +
    'pose además de la cara.',
  'asset.realceNormalQue':
    'Regenera con la cara de Evaluna al 50 %, misma semilla. Ojo: cambia los rasgos, no sólo la ' +
    'piel — la persona no va a ser la misma.',
  'asset.realceAltoQue':
    'Regenera con la cara de Evaluna al 75 %, misma semilla. A esta fuerza casi no queda nada de ' +
    'la cara original.',
  'asset.realceNormal': '✨ Realce normal',
  'asset.realceAlto': '✨ Realce alto',
  'asset.eliminar': '🗑 Eliminar',
  'asset.copiarPrompt': 'Copiar prompt',

  // ── Ficha: estudio de contenido ──
  'video.recorteAvisado':
    'No es {aspecto}: se extiende la foto antes de animarla (+USD 0,04). La imagen queda intacta ' +
    'y sólo se genera el borde que falta.',
  'video.estadoEspera': '{estado} · {segundos}s',
  'video.generando': 'generando',
  'video.sigueGenerandose': 'Sigue generándose. Aparece solo cuando termine.',
  'video.movimiento': 'Qué hace en el clip (opcional)',
  'video.movimientoEjemplo': 'she stands up and sticks her tongue out',
  'video.segundos': 'Segundos',
  /*
    Se pide en inglés porque el modelo entiende mucho mejor en inglés.

    El texto viaja tal cual al codificador de Wan. En español obedece a medias: reconoce el sujeto
    y pierde el verbo, que es justo la parte que se estaba pidiendo. Por eso el ejemplo del campo
    también está en inglés — es la única indicación que se lee sin abrir la ayuda.
  */
  'video.consejo':
    'Escribilo en inglés: el modelo obedece mucho mejor. Sin texto, el clip se mueve apenas: ' +
    'respira y parpadea. Escribí una acción concreta para que pase algo. Evitá «gently», ' +
    '«subtle» y «slowly»: producen justo lo contrario. Cada segundo cuesta más de un minuto de ' +
    'GPU, y más de 10 el modelo pierde la cara.',
  'video.noSePudo': 'No se pudo generar el clip.',
  'video.conVideo': 'Animar con un video…',
  'video.reemplazo': 'Meterla en el video',
  'video.reemplazoQue':
    'Entra el video entero —escena, luz, encuadre— y sólo se cambia quién lo protagoniza. La ' +
    'ropa y el lugar los pone el video, no la ficha.',
  'video.conVideoQue':
    'Subí un clip y ella hace ESE movimiento. Se usan los primeros 5 segundos y el clip dura eso; del video sólo sale el esqueleto, así que la persona que aparezca no llega al resultado. Sólo con la placa propia.',
  'estudio.sinTomas':
    'Todavía no hay tomas publicables. Generá el set final en Galería → 3 · Contenido.',
  'estudio.sePublicaClip': 'Se publica este clip',
  'estudio.sePublicaFoto': 'Se publica esta foto',
  'estudio.seRecorta': ' — el calendario pidió {aspecto}, se recorta al publicar',
  'estudio.comoReel': 'Se publica como reel.',
  'musica.titulo': 'Música del clip',
  'musica.sinMusica': 'Sin música',
  'musica.subir': 'Subir…',
  'musica.subirQue':
    'Agregá una pista a tu biblioteca. Usá música libre de derechos: Instagram silencia o bloquea lo que reconoce.',
  'musica.volumen': 'Volumen',
  'musica.mezclando': 'Mezclando…',
  'musica.buscar': 'Buscar…',
  'musica.buscarQue':
    'Buscá música libre en Openverse, filtrada a CC0 y dominio público: no exige atribución ni limita el uso comercial.',
  'musica.buscarAccion': 'Buscar',
  'musica.buscarEjemplo': 'lofi, ambient, guitar…',
  'musica.deDonde':
    'Openverse, sólo CC0 y dominio público. Son en su mayoría bucles de librería, no canciones terminadas.',
  'musica.importarQue': 'Traer a tu biblioteca',
  'estudio.cambiar': 'Cambiar',
  'estudio.elegiImagen': 'Elegí la imagen de esta publicación',
  'estudio.coincide': '{ancho}×{alto} — coincide con {aspecto}',
  'estudio.noCoincide': '{ancho}×{alto} — el calendario pidió {aspecto}, se va a recortar',
  'estudio.calendarioPidioAntes': 'El calendario pidió ',
  'estudio.calendarioPidioDespues': '. Las de otra relación se recortan al publicar.',
  'estudio.generadas': '{hechas} de {total} generadas',
  'estudio.unaToma': 'Generando una toma…',
  'estudio.cadaTomaTarda':
    'Tarda unos 40 segundos. Si se traba, un paso puede quedar hasta cuatro minutos colgado ' +
    'antes de reintentar; si quedó colgada de verdad, «Generar igual» arranca otra.',
  'encargo.tipo.actividad': 'Haciendo algo',
  'encargo.tipo.selfie': 'Selfie',
  'encargo.tipo.espejo': 'Espejo',
  'encargo.tipo.atractivo': 'Atractiva',
  'encargo.tipo.imperfecta': 'Mal sacada',
  'encargo.encuadre.close_up': 'Primer plano',
  'encargo.encuadre.medium': 'Plano medio',
  'encargo.encuadre.full_body': 'Cuerpo entero',
  'encargo.encuadre.wide': 'Plano general',
  'encargo.cubo.agua': 'De agua',
  'encargo.cubo.gimnasio': 'De gimnasio',
  'encargo.cubo.interior': 'De entrecasa',
  'encargo.cubo.calle': 'De calle',
  'encargo.titulo': 'Cómo sale esta tanda',
  'encargo.todoAlAzar': 'Todo al azar',
  'encargo.fijados': '{n} fijado(s)',
  'encargo.alAzar': 'Al azar',
  'encargo.rol': 'Papel que juega',
  'encargo.sesion': 'Sesión de fotos',
  'encargo.escrito': 'Contalo con tus palabras',
  'encargo.escritoEjemplo': 'una sesión en una pasarela en Sídney, de noche',
  'encargo.traducir': 'Armar la configuración',
  'encargo.traduciendo': 'Armando…',
  'encargo.entendi': 'Entendí: {texto}',
  'estudio.sinTanda': 'Anteriores',
  /*
    Se llamaba «Hacer un set», y el nombre prometía una sesión de fotos.

    No lo es, y la diferencia importa: esto EDITA esta foto, así que conserva el lugar, la ropa
    y la CÁMARA. Está medido que el modelo no mueve el punto de vista por más que se le pida
    —tres formulaciones distintas devolvieron la misma toma— y eso es la otra cara de que
    conserve la identidad en +0,93.

    Quien quiere ángulos distintos tiene otra herramienta, la casilla de sesión de fotos, y el
    texto la nombra para que no haya que adivinarlo.
  */
  'asset.hacerSet': '+ Más de este momento',
  'asset.setDescripcion': 'Algo que quieras pedirle (opcional). Dejalo vacío y sale igual a ésta, cambiando la pose.',
  'asset.generada': 'Generada',
  'asset.setDe': '{n} tomas del mismo momento',
  'asset.setQue':
    'Saca más tomas de ESTA foto: el mismo lugar, la misma ropa y la misma cámara, con ella en ' +
    'otra pose o a otra distancia. Se agrupan para publicarlas juntas en un carrusel y ésta ' +
    'queda como la primera. Si lo que querés son ángulos distintos del lugar, eso lo hace la ' +
    'casilla «sesión de fotos» al generar una tanda.',
  'encargo.sesionAyuda': 'todas en el mismo lugar y con la misma ropa',
  'encargo.ellaMisma': 'Ella misma',
  'encargo.techo': 'Bajo techo o afuera',
  'encargo.adentro': 'Bajo techo',
  'encargo.afuera': 'Al aire libre',
  'encargo.lugar': 'Lugar',
  'encargo.tipo': 'Tipo de foto',
  'encargo.encuadre': 'Encuadre',
  'encargo.ropa': 'Ropa',
  'encargo.participa': 'Usa el lugar',
  'encargo.participaSi': 'Sí, lo está usando',
  'encargo.participaNo': 'No, está al lado',
  'encargo.participaNoAplica': 'Este lugar no tiene nada en lo que estar: sale igual.',
  'encargo.actividad': 'Qué hace',
  'encargo.gestos': 'Gestos (una mano)',
  'encargo.ropaDelLugar': 'La decide el lugar elegido.',
  'encargo.pie':
    'Lo que elijas vale para TODAS las tomas de la tanda. Lo que dejes en «al azar» se sortea ' +
    'por toma, como hasta ahora.',
  'estudio.generarContenido': 'Generar contenido',
  'estudio.faltaAprobarHoja': 'Falta aprobar la hoja de identidad',
  'estudio.generarContenidoQue':
    'Las escenas las propone el modelo a partir del nicho, evitando lo que ya publicaste, y cada ' +
    'imagen se genera con la hoja adjunta. El texto se pide después, imagen por imagen, cuando ya ' +
    'la miraste.',
  'estudio.faltaAprobarHojaQue':
    'El contenido se genera con la hoja de identidad adjunta. Sin ella, cada toma saldría con ' +
    'otra cara. Terminala en la Galería.',

  'estudio.tandaReutiliza':
    'Tanda de {total}: {existentes} ya estaban generadas y se reutilizan, faltan {faltan}.',
  'estudio.completarSet': 'Generar una toma',
  'estudio.generarIgual': 'Generar igual',
  'estudio.generarIgualConfirmar':
    'Ya figura una generación en curso. Si quedó colgada, esto arranca otra toma: cuesta una imagen más.',
  'estudio.laUltimaFallo': 'La última generación falló',
  'estudio.generarSet': 'Generar una toma',
  'estudio.generarSesion': 'Generar la sesión (4 fotos)',
  'estudio.sinPublicacion': 'Contenido sin publicación · {n}',
  'estudio.descartado': 'Contenido descartado · {n}',
  'estudio.sinPublicacionQue': 'generadas pero sin texto que las acompañe',
  'estudio.descartadoQue': 'no se usaron y no van a publicarse',
  'estudio.huerfanasQue':
    'Se les puede escribir la publicación a partir de la escena con la que se generaron, sin ' +
    'gastar imágenes nuevas.',
  'estudio.escribiendo': 'Escribiendo…',
  'estudio.escribirPublicaciones': 'Escribir sus publicaciones',
  'tarea.enlaceDePrueba': 'Enlace de prueba gratis',
  'tarea.enlaceDePruebaQue':
    'El de la promoción de Fanvue, con su identificador. Sale en la página pública como un ' +
    'segundo enlace, arriba del normal. Se puede dejar vacío.',
  'estudio.duplicadaAntes': ' se parece demasiado a «',
  'estudio.duplicadaDespues': '», así que no se escribió.',
  'estudio.duplicadaPorQue':
    'Con varias publicaciones del mismo nicho el vocabulario se agota y el parecido es ' +
    'esperable. Si te parece que son distintas, escribila igual y revisala antes de publicar.',
  'estudio.escribirlaIgual': 'Escribirla igual',
  'estudio.imagenes': 'Imágenes',
  'estudio.videos': 'Videos',
  'estudio.quedanDescartadas': 'Las que quedan están descartadas: se ven abajo.',
  'estudio.generarPublicacion': 'Generar publicación',
  'estudio.animarQue':
    'Genera un clip de {segundos} s con {proveedor}, elegido por el nivel de piel de esta ' +
    'influencer.',
  'estudio.aprobarPrimero': 'Aprobá la toma primero: el clip hereda lo que muestre',
  'estudio.animando': 'Animando…',
  'estudio.animar': 'Animar · {precio}',
  'estudio.aprobarParaAnimar': 'Aprobá la toma para animarla',
  'estudio.escalar': 'Escalar',
  'estudio.escalarDeNuevo': 'Escalar otra vez',
  'estudio.escalando': 'Escalando…',
  'estudio.factorDeEscalado': 'Cuánto se agranda',
  'estudio.escalarQue': 'Queda en {ancho}×{alto}, en la placa propia. El original no se toca.',
  'estudio.hayEscalada': 'En alta: {ancho}×{alto} · {mb} MB',
  'estudio.refCuerpo': 'Usar de referencia de cuerpo',
  'estudio.refCuerpoEsLa': 'Es la referencia de cuerpo',
  'estudio.refCuerpoQuitar': 'Dejar de usarla',
  'estudio.refCuerpoGuardando': 'Guardando…',
  'estudio.refCuerpoQue':
    'Se manda como referencia en las tomas de plano entero y de plano general. Tiene que ' +
    'vérsele el cuerpo completo: el encuadre se hereda de esta foto.',
  'estudio.refCuerpoQuitarQue':
    'Sin referencia de cuerpo, las tomas lejanas vuelven a salir con el recorte de cara y ' +
    'terminan de cerca.',
  'estudio.tipoDeContenido': 'Tipo de contenido',
  'estudio.publicableEnTodas': 'Publicable en todas',
  'estudio.soloFanvue': 'Sólo Fanvue',
  'espacio.descartadaUna': '1 descartada',
  'espacio.descartadasVarias': '{n} descartadas',
  'espacio.ocupan':
    ' ocupan {mb} MB y no van a publicarse. Borrarlas libera ese espacio; su procedencia queda ' +
    'en el historial.',
  'espacio.confirmarLiberar':
    'Se van a borrar {n} archivos descartados ({mb} MB). No se puede deshacer.\n\nSu ' +
    'procedencia —prompt, semilla, modelo y créditos— se conserva en el historial.\n\n' +
    '¿Liberar el espacio?',
  'espacio.parcial': 'Se borraron {borradas} y quedaron {fallaron} sin borrar. Liberados {mb} MB.',
  'espacio.total': '{borradas} archivos borrados, {mb} MB liberados.',

  // ── Ficha: contenido para suscriptores ──
  'nsfw.tomasGeneradas': '{n} tomas generadas. Quedan pendientes de revisión.',
  'nsfw.generadasYFallidas': '{hechas} generadas y {fallidas} fallidas.',
  'nsfw.noSePuedeAun': 'Todavía no se puede generar',
  'nsfw.faltaConfigurar': 'Falta configurar algo para poder generar.',
  'nsfw.faltaHoja': 'Falta aprobar la hoja de identidad',
  'nsfw.faltaHojaQue':
    'Cada toma se genera con la hoja adjunta. Sin ella saldría con otra cara — y acá eso importa ' +
    'más que en el resto, porque es lo único que ata este material a la misma persona.',
  'nsfw.noSePudoGenerar': 'No se pudo generar',
  'nsfw.fallaron': '{n} fallaron',
  'nsfw.generadas': '{hechas} de {total} generadas',
  'nsfw.dosPasos':
    'Cada toma son dos pasos: se genera y después se le cambia la cara. Tarda alrededor de un ' +
    'minuto y se cobran las dos llamadas — unos USD 0,04 por imagen.',
  'nsfw.generarContenido': 'Generar contenido',
  'nsfw.enGpuPropia':
    'Las escenas las propone el modelo a partir del nicho. Cada toma se genera en tu propia GPU, ' +
    'en un solo paso y sin costo — la identidad la sostiene el mecanismo de edición de Krea2 ' +
    'desde la hoja aprobada. Hace falta que la máquina esté prendida.',
  'nsfw.enProveedor':
    'Las escenas las propone el modelo a partir del nicho. Cada toma se hace en dos pasos: ' +
    'primero el cuerpo y la escena, después se le cambia la cara por la de ella. Elegís cuáles ' +
    'querés antes de gastar: son unos USD 0,04 por imagen, y se cobra también la que después ' +
    'descartes.',
  'nsfw.proporcion': 'Proporción',
  'nsfw.caraEnGpuPropia':
    'Tu GPU ya sostiene la identidad al generar, así que este paso normalmente sobra. Cuesta ' +
    'USD 0,04 por toma y el editor tiene filtro del fabricante.',
  'nsfw.caraEnProveedor':
    'Los modelos de Runware no sostienen la identidad y generan el registro a la vez, así que la ' +
    'cara se pone en un segundo paso. Cuesta USD 0,04 por toma.',
  'nsfw.cambiarLaCara': 'Cambiar la cara',
  'nsfw.generandoConCara': 'Generando y cambiando la cara…',
  'nsfw.elegiUnaEscena': 'Elegí una escena',
  'nsfw.generarSetN': 'Generar set · {n}',
  'nsfw.queSeVe': 'Qué se ve',
  'nsfw.pensando': 'Pensando…',
  'nsfw.queLasProponga': '✨ Que las proponga el modelo',
  'nsfw.hastaDonde': 'Hasta dónde llega',
  'nsfw.nivelExplicito': 'Desnuda',
  'nsfw.nivelInsinuado': 'Casi explícito',
  'nsfw.nivelExplicitoDetalle':
    'Desnudez completa. Sólo al feed de suscriptores de Fanvue: nunca a Instagram ni a ' +
    'Facebook, y nunca al avatar ni al banner.',
  'nsfw.nivelInsinuadoDetalle':
    'Lencería mínima, o desnuda con lo justo tapado. No se ven las partes íntimas. Va a Fanvue ' +
    'y a Telegram: tampoco entra en Instagram ni en Facebook.',
  'fanvue.otraVez': 'Publicar otra vez',
  'fanvue.publicando': 'Publicando…',
  'fanvue.otraVezTitulo':
    'Vuelve a mandar la misma foto con el mismo texto. La anterior no se toca.',
  'fanvue.otraVezConfirmar':
    '¿Volver a publicar esto en {destino}? Sale de nuevo al canal y eso no se deshace.',
  'fanvue.cancelar': 'Cancelar la publicación',
  'fanvue.cancelando': 'Cancelando…',
  'fanvue.cancelarTitulo': 'Borra la publicación y devuelve la toma a «crear publicación».',
  'fanvue.cancelarConfirmar':
    '¿Cancelar esta publicación? La foto no se borra: vuelve a quedar lista para crear otra.',
  'nsfw.salenDelNicho':
    'Salen de su nicho: una jugadora de vóley y una cocinera no reciben las mismas.',
  'nsfw.deEspaldas': '· de espaldas',
  'nsfw.escribiUna': 'O escribí una: en la cama, luz de la mañana por la ventana…',
  'nsfw.agregar': 'Agregar',
  'nsfw.describiLaEscena':
    'Describí la escena, no a ella: la cara, el cuerpo y el estilo salen de su hoja de identidad, ' +
    'para que sea la misma persona que en el resto del contenido.',

  // ── Ficha: publicar en Fanvue ──
  'fanvue.clipHereda': 'El clip hereda lo que muestre la toma.',
  'fanvue.aprobarPrimero': 'Aprobá la toma primero: el clip hereda lo que muestre.',
  'fanvue.aprobalaParaAnimar': 'Aprobala para animarla',
  'fanvue.creando': 'Creando…',
  'fanvue.yaSalioEn': 'Ya publicada en {destino}.',
  'fanvue.sumarTelegram': '+ Publicarla también en Telegram ({stars} ⭐)',
  'fanvue.publicarConStars': 'Publicar cobrando {stars} ⭐',
  'fanvue.publicarGratis': 'Publicar gratis, abierta',
  'fanvue.enFanvue': 'En Fanvue, tras la suscripción',
  'fanvue.telegramDePago': 'En Telegram, difuminada · {stars} ⭐ para desbloquear',
  'fanvue.telegramGratis': 'En Telegram, ABIERTA · sin Stars',
  'fanvue.gratisEnTelegram': 'Regalarla en Telegram (sin Stars)',
  'fanvue.crearPublicacion': 'Crear publicación',
  'fanvue.faltanSalir': 'Sin publicar',
  'fanvue.yaSalieron': 'Ya publicadas',
  'fanvue.publicada': 'Publicada en Fanvue, para suscriptores.',
  'fanvue.faltaConectar':
    'Falta conectar Fanvue y asignarle un perfil a esta influencer, en Plataformas.',
  'fanvue.manual':
    'No salió por la API: quedó como paquete para publicar a mano. Suele ser que a la conexión ' +
    'de Fanvue le falte un permiso — se arregla volviendo a conectar en Credenciales, y ahí ' +
    'mismo se ve cuál falta.',
  'fanvue.contesto': 'Fanvue contestó «{estado}» en vez de publicar.',
  'fanvue.publicadasTodas': '{n} publicadas en Fanvue.',
  'fanvue.publicadasParciales': '{hechas} publicadas y {fallos} que no salieron.',
  'fanvue.faltaClave': 'Falta la clave de Runware',
  'fanvue.faltaClaveP1Antes':
    'Este contenido se genera con Runware, que se paga por uso y no tiene capa gratuita. La ' +
    'clave es tuya y va en ',
  'fanvue.credenciales': 'Credenciales',
  'fanvue.faltaClaveP1Despues':
    ': así contratás vos directamente, y lo que se genere lo paga quien lo pide.',
  'fanvue.faltaClaveP2':
    'Se eligió Runware por sus términos y no por su precio. Los proveedores más baratos del ' +
    'relevamiento prohíben proveer el servicio a terceros, y este panel tiene más de un usuario ' +
    '— la prohibición nos alcanzaría aunque ninguno de ellos tocara su API.',
  'fanvue.faltaClaveP3Antes': 'Si preferís no pagar, en ',
  'fanvue.modelos': 'Modelos',
  'fanvue.faltaClaveP3Despues':
    ' podés elegir la GPU propia para la imagen de Fanvue: corre en tu máquina, no cuesta y no ' +
    'tiene filtro de nadie.',
  'fanvue.gpuPeroVideo': 'La imagen sale de tu GPU; el video necesita Runware',
  'fanvue.gpuPeroVideoP1Antes': 'La imagen de Fanvue está configurada con ',
  'fanvue.tuPropiaGpu': 'tu propia GPU',
  'fanvue.gpuPeroVideoP1Despues':
    ', así que se genera sin clave y sin costo — sólo hace falta que la máquina esté prendida.',
  'fanvue.gpuPeroVideoP2Antes': 'Lo que sí necesita la clave de Runware es ',
  'fanvue.animar': 'animar',
  'fanvue.gpuPeroVideoP2Despues':
    ' una toma, y el cambio de cara si lo pedís. Las dos cosas se pagan por uso; generar ' +
    'imágenes acá, no.',
  'fanvue.conQueSeGenera': 'Con qué se genera',
  'fanvue.imagen': 'Imagen',
  'fanvue.video': 'Video',
  'fanvue.seEligenAntes': 'Se eligen en ',
  'fanvue.seEligenDespues':
    ', aparte de los del contenido publicable: aquéllos entran en la cuota diaria gratuita y ' +
    'éstos se pagan en dólares por generación. Un desplegable que los mezclara haría que elegir ' +
    'uno caro se pareciera a elegir uno gratis.',
  'fanvue.paraPublicar': 'Para publicar · {n}',
  'fanvue.publicandoLote': 'Publicando… {hechas} de {total}',
  'fanvue.siPublicar': 'Sí, publicar las {n}',
  'fanvue.salenAlFeed': 'Salen al feed de suscriptores. No se puede deshacer.',
  'fanvue.publicarLas': 'Publicar las {n}',
  'fanvue.noSalieron': 'No salieron {n}:',
  'fanvue.ningunaAun':
    'Todavía no hay ninguna. Las {n} imágenes que ya existen están clasificadas como publicables ' +
    'en cualquier plataforma.',
  'fanvue.nadaEnRedesAntes':
    'Nada de lo que aparezca acá puede publicarse en Instagram ni en Facebook: el servidor lo ' +
    'rechaza al intentarlo, no es una casilla que se pueda destildar. En Fanvue va al feed de ' +
    'suscriptores — ',
  'fanvue.no': 'no',
  'fanvue.nadaEnRedesDespues':
    ' al avatar, al banner ni al vídeo de introducción, que se ven sin suscripción y tienen que ' +
    'ser aptos.',

  // ── Ficha: LoRA de identidad ──
  'lora.sinLora': 'Sin LoRA',
  'lora.fallo': 'Falló: {estado}',
  'lora.listo': 'Listo',
  'lora.sinProbar': 'Entrenado, sin probar',
  'lora.entrenando': 'Entrenando',
  'lora.sinLoraQue':
    'La identidad sale de las referencias de la hoja. Para usar Z-Image hace falta entrenar uno.',
  'lora.falloQue':
    'El entrenamiento no terminó bien. Se puede volver a intentar desde la pestaña LoRA.',
  'lora.listoQue':
    'Ya se usó para generar al menos una vez, así que el LoRA funciona. Que se aplique en cada ' +
    'toma depende del modelo elegido: sólo entra si es de su misma familia.',
  'lora.sinProbarQue':
    'Este estado no cambia solo: se queda así hasta que una generación salga bien con él. ' +
    'Escribí una escena a mano en Fanvue y generá una toma — si sale, pasa a «Listo» y se ' +
    'habilita que el modelo proponga escenas. Si la rechaza, no se cobra.',
  'lora.entrenandoQue': '{pasos} pasos, lanzado {cuando}{avance}.',
  'lora.tarjeta': 'LoRA de identidad',

  // ── Ficha: pestaña LoRA ──
  'lora.zipSubido': 'ZIP subido con {n} imágenes.',
  'lora.noSePudo': 'No se pudo',
  'lora.entendidoOcultar': 'Entendido, ocultar',
  'lora.setDeEntrenamiento': 'Set de entrenamiento',
  'lora.setDeEntrenamientoQue':
    'Sale de lo que ya se publicó, que es la señal más fuerte que hay: pasó tu criterio y además ' +
    'se usó. Cada imagen va con un caption que describe la escena y calla los rasgos de la cara ' +
    '— lo que el caption explica es lo que el LoRA deja de aprender.',
  'lora.zipListo': 'ZIP listo con {n} imágenes.',
  'lora.armando': 'Armando…',
  'lora.generarZip': '1 · Generar ZIP · {n}',
  'lora.estimado':
    'Estimado: unos {minutos} minutos en total, sacado de lo que tardaron los anteriores. Es una ' +
    'referencia, no una promesa.',
  'lora.sinEstimacion':
    'Todavía no hay con qué estimar cuánto tarda: es el primero que terminará. El siguiente ya va ' +
    'a tener una referencia.',
  'lora.seActualizaSola':
    'Esta tarjeta se actualiza sola. No hace falta volver a pulsar — cada entrenamiento se cobra ' +
    'aparte.',
  'lora.enUso': 'en uso',
  'setPlaca.tarjeta': 'Set de entrenamiento, generado en la placa',
  'setPlaca.que':
    'Las tomas se generan acá, cruzando encuadre, ángulo, luz, ropa y fondo, para que la ' +
    'variación esté garantizada y no encargada. El set armado con lo ya publicado tiene un ' +
    'techo: es material uniforme, y un LoRA aprende lo que se repite.',
  'setPlaca.generar': 'Generar el set en la placa',
  'setPlaca.arrancando': 'Arrancando…',
  'setPlaca.arrancado': 'Generando {n} tomas. Podés cerrar esta pantalla.',
  'setPlaca.tarda': 'faltan unos {minutos} min',
  'setPlaca.cancelar': 'Cancelar',
  'setPlaca.enLaPlaca': '{n} en la placa · {elegidas} elegidas',
  'setPlaca.armarZip': 'Armar ZIP con {n}',
  'setPlaca.armando': 'Armando…',
  'setPlaca.zipListo': 'ZIP armado con {n} imágenes.',
  'setPlaca.zipEn': 'ZIP con {n} imágenes, {mb} MB. Quedó en la placa:',
  'setPlaca.zipQueHacer':
    'No se descarga: pesa demasiado para pasar por el panel, y ya está en la máquina desde la ' +
    'que se sube a RunPod. Abrí esa carpeta y subilo desde ahí.',
  'loraPlaca.tarjeta': 'LoRA propio en la placa',
  'pila.tarjeta': 'LoRA de estilo · solo GPU local',
  'cuerpo.tarjeta': 'LoRA de forma del cuerpo · solo GPU local',
  'cuerpo.que':
    'La forma la dibuja un LoRA, no el texto: la referencia de cuerpo se genera sin el de ' +
    'identidad —es un ancla, no contenido— y sin ninguno la silueta sale recta aunque el prompt ' +
    'pida caderas. El peso sube con el nivel de piel porque cuánto se ve de la figura no es ' +
    'igual en los seis: en «calle» la ropa la tapa y alcanza con insinuarla, en «atrevido» el ' +
    'cuerpo es la toma.',
  'cuerpo.medido':
    'De la rampa hay dos puntos medidos: 0,8 iguala la referencia anterior y 1,0 empieza a ' +
    'estilizar. Los del medio son interpolación — por eso están acá y no en el código.',
  'cuerpo.sinLora': 'Sin LoRA',
  'cuerpo.porDefecto': 'Como viene',
  'cuerpo.restablecer': 'Volver a como viene',
  'cuerpo.guardando': 'Guardando…',
  'pila.que':
    'Los que describen como se ve el material —piel, grano, camara— y valen para todas las ' +
    'influencers. El LoRA de cada persona no va aca: va en su ficha.',
  'pila.volverAlWorkflow': 'Volver a la pila del workflow',
  'pila.configurada': 'configurada acá',
  'pila.resumenSinTocar':
    'Las cuatro etapas usan la pila que trae su workflow. Tocar para ver o cambiar.',
  'pila.resumenConfiguradas': '{n} de 4 etapas con pila propia. Tocar para ver o cambiar.',
  'pila.ninguno': 'ninguno',
  'pila.delWorkflow': 'la que trae el workflow',
  'pila.loQueNoAparece':
    'No se listan: el LoRA de identidad —no es un estilo, es lo que hace que la referencia ' +
    'signifique algo—, los de cada influencer, que van en su ficha, y los de otra arquitectura ' +
    '(Wan, LTX), que no hacen nada sobre Krea 2. Los workflows de video cargan los suyos ' +
    'cableados y no se configuran acá.',
  'pila.noSePudoListar': 'No se pudieron listar los LoRA de la placa: {motivo}',
  'loraPlaca.ningunoInstalado':
    'No hay ningún LoRA suyo en la placa. Copiá el .safetensors a la carpeta de LoRA de ComfyUI, ' +
    'dentro de una subcarpeta llamada {carpeta}, y aparece acá.',
  'loraPlaca.hayDeOtros':
    'Hay {n} LoRA de personaje en la placa, pero ninguno en una carpeta llamada {carpeta}. ' +
    'Renombrá su carpeta así para que se le asignen a ella.',
  'loraPlaca.que':
    'El LoRA entrenado de esta influencer, apilado al generar con la GPU propia. El archivo se ' +
    'copia a mano a la carpeta de LoRA de ComfyUI: pesa cientos de megas y no puede subirse por ' +
    'el panel.',
  'loraPlaca.archivo': 'Archivo',
  'loraPlaca.ninguno': '— ninguno —',
  'loraPlaca.noEstaEnLaPlaca': '(ya no está en la placa)',
  'loraPlaca.encendido': 'LoRA ON',
  'loraPlaca.encendidoQue': 'se apila en cada generación con la GPU propia',
  'loraPlaca.elegiPrimero': 'elegí un archivo primero',
  'loraPlaca.carpeta': 'carpeta: {carpeta}',
  'loraPlaca.guardado': 'Guardado.',
  'loraPlaca.noSePudoListar': 'No se pudieron listar los LoRA de la placa: {motivo}',
  'lora.ahoraSeGeneraConEste': 'Ahora se genera con éste.',
  'lora.usarEste': 'Usar éste',
  'lora.cuantoTira': 'Cuánto tira',
  'lora.porOmision': ' · por omisión',
  'lora.masMarca': 'Más marca la identidad y también los defectos. No hace falta reentrenar.',
  'lora.cuantoTiraContenido': 'Cuánto tira el de contenido',
  'lora.conPoco': 'Con poco a veces sale vestida; con mucho deforma caras. Es global, no de ella.',
  'lora.sinLoraHasta': 'Se generará sin LoRA hasta que elijas uno.',
  'lora.generarSinLora': 'Generar sin LoRA, para comparar',
  'lora.entrenarTitulo': '2 · Entrenar el LoRA',
  'lora.seEntrenaConAntes': 'Se entrena con ',
  'lora.ese': 'ese',
  'lora.seEntrenaConDespues':
    ' ZIP —{imagenes} imágenes, {mb} MB—. Cada entrenamiento queda con su propio identificador, ' +
    'así que podés tener varios: uno por arquitectura y por cantidad de pasos.',
  'lora.primeroZipAntes': 'Primero generá el ZIP, arriba. Entrenar usa ',
  'lora.primeroZipDespues':
    ' archivo y no uno nuevo: entre armarlo y entrenar pueden cambiar el piso, el tope o las ' +
    'imágenes publicadas, y hay que entrenar con lo que miraste.',
  'lora.primeroZipP2':
    'Podés entrenar todas las veces que quieras — en las dos arquitecturas y con distintos pasos. ' +
    'Cada uno queda aparte en el historial.',
  'lora.soloProbar': ' · sólo probar la tubería',
  'lora.minimoIdentidad': ' · mínimo para identidad',
  'lora.maximoUtil': ' · máximo útil',
  'lora.cuesta': 'Cuesta ',
  'lora.pasadasSobre': ' pasadas sobre las {n} imágenes',
  'lora.entrenandoQueda': 'Entrenando. Queda como {air}.',
  'lora.enviando': 'Enviando…',
  'lora.entrenar': 'Entrenar',
  'lora.zipVenceAntes':
    'El ZIP vence en {cuando} y se borra solo: es un archivo de paso, no una copia. Si movés los ' +
    'controles de arriba hay que volver a generarlo, para que se entrene con lo que estás viendo. ',
  'lora.descargarlo': 'Descargarlo',
  'lora.zipVenceDespues': ' si querés entrenar afuera.',
  'lora.oSubiZip': 'O subí un ZIP',
  'lora.subiendo': 'Subiendo…',
  'lora.arrastraZip': 'Arrastrá un ZIP acá, o hacé clic',
  'lora.sirveElQue':
    'Sirve el que descargaste y podaste a mano, o uno armado con otra herramienta. Se comprueba ' +
    'que sea un ZIP y nada más.',
  'lora.queEntra': 'Qué entra',
  'lora.cuantas': 'Cuántas',
  'lora.parecidoMinimo': 'Parecido mínimo',
  'lora.sinPiso': 'sin piso',
  'lora.deLasPublicadas': ' de {publicadas} publicadas · {medidas} medidas, {sinMedir} sin medir',
  'lora.sonPocas':
    'Son pocas para entrenar. Con menos de doce el LoRA aprende más el fondo y la ropa que la ' +
    'cara. Conviene bajar el piso, o generar y publicar unas cuantas más.',
  'lora.disparadorAntes': 'El disparador es ',
  'lora.disparadorDespues':
    ': la palabra con la que se invoca el LoRA al generar, y la que encabeza cada caption.',
  'lora.lasQueEntran': 'Las que entran · {n}',

  // ── Ficha: pestaña Contenido ──
  'cont.sinMedir': 'sin medir',
  'cont.sinContenido': 'Sin contenido',
  'cont.sinCalendario': 'Todavía no se generó el calendario.',
  'cont.todoDescartado':
    'Todo lo generado se descartó al rechazar sus imágenes. Generá otro set acá arriba.',
  'cont.publicando': 'Publicando',
  'cont.avanceLote': '{hechas} de {total}',
  'cont.publicarEnTanda': 'Publicar en tanda',
  'cont.siPublicarEn': 'Sí, publicar {n} en {plataforma}',
  'cont.plataformaCuantas': '{plataforma} · {n}',
  'cont.salenDeAUna': 'Salen de a una, en fila. No se puede deshacer.',
  'cont.paqueteListo': 'Paquete listo para publicar',
  'cont.copiarTexto': 'Copiar texto',
  'cont.descargarImagen': 'Descargar imagen',
  'cont.abrirPlataforma': 'Abrir la plataforma',
  'cont.publicadoEnInstagram': 'Publicado en Instagram.',
  'cont.verLaPublicacion': 'Ver la publicación',
  'cont.salioComoPaquete': 'No se publicó: salió como paquete manual.',
  'cont.salioComoPaqueteQue':
    'La conexión existe pero no estaba en condiciones de publicar. Revisá el estado en ' +
    'Plataformas y, si hace falta, volvé a conectar.',
  'cont.sinImagenUna': '{n} publicación sin imagen',
  'cont.sinImagenVarias': '{n} publicaciones sin imagen',
  'cont.sinImagenQue':
    'Sin imagen, el paquete de exportación sale con el texto listo y nada que subir. Elegí una ' +
    'miniatura en cada publicación.',
  'cont.paraPublicar': 'Para publicar · {n}',

  // ── Ficha: tarjeta de publicación ──
  'post.elegiImagenPrimero':
    'Elegí una imagen o un clip: sin archivo la publicación falla al salir.',
  'post.sacarDeLaCola': 'Sacarla de la cola. El texto y la imagen no se pierden.',
  'post.ponerEnCola': 'Ponerla en cola: el cron la publica a su hora.',
  'post.republicarTitulo':
    'Escribe un texto nuevo para esta misma foto y la deja lista para publicar otra vez',
  'post.confirmarRepublicar':
    'Se va a crear una publicación nueva con esta misma foto y un texto escrito de cero. La ' +
    'publicación original queda como está. ¿Seguir?',
  'post.borrarTitulo': 'Borrar esta publicación',
  'post.confirmarBorrarPublicada':
    'Esta publicación ya salió a una plataforma. Borrarla acá NO la borra de allá: hay que ' +
    'hacerlo a mano en la app. ¿Borrarla del sistema?',
  'post.confirmarBorrar':
    '¿Borrar esta publicación? La imagen o el clip vuelven a «Contenido sin publicación» y se ' +
    'pueden volver a usar.',
  'post.borrando': 'Borrando…',
  'post.borrar': 'Borrar',
  'post.enBorradorAntes': 'En borrador: ',
  'post.noSePublicaSola': 'no se publica sola',
  'post.enBorradorDespues':
    '. El cron sólo publica lo aprobado — pulsá «Aprobar» y sale a su hora.',
  'post.yaPublicada':
    'Ya publicada. La imagen no se puede cambiar desde acá: cambiarla no tocaría el post que ya ' +
    'está en la plataforma.',
  'post.animarTitulo': 'Genera un clip y lo asigna a esta publicación',
  'post.aprobarEnGaleria': 'Aprobá la toma en la Galería: el clip hereda lo que muestre',
  'post.animarEstaToma': 'Animar esta toma · {precio}',

  'post.editarTexto': 'Editar el texto',
  'post.editarTextoTitulo':
    'Cambia el pie, los hashtags y el alt de esta plataforma. No regenera nada ni cuesta dinero.',
  'post.elPie': 'El pie',
  'post.pieCuantoQueda': 'Quedan {n} caracteres',
  'post.losHashtags': 'Hashtags',
  'post.hashtagsComo':
    'Separados por espacios, con o sin #. Hasta seis, y sin espacios adentro de cada uno.',
  'post.elAlt': 'Texto alternativo',
  'post.altComo': 'Lo que describe la foto a quien no la ve. Vacío o de diez caracteres para arriba.',
  'post.noSePudoGuardarTexto': 'No se pudo guardar el texto.',
  'post.quitarImagen': 'Quitar la imagen',
  'post.yaPublicadoVer': 'Ya publicado · ver',
  'post.yaPublicadoSinEnlace': 'Ya publicado (sin enlace guardado)',
  'post.igSinImagen': 'Instagram no acepta publicaciones sin imagen: elegí una arriba.',
  'post.yaPublicadaEtiqueta': 'Ya publicada',
  'post.yaPublicadas': 'Ya publicadas · {n}',
  'post.yaPublicadasUltimas': 'Ya publicadas · últimas {n} de {total}',
  'post.publicando': 'Publicando…',
  'post.publicarAhora': 'Publicar ahora',
  'post.sePublicaDeVerdad': 'Se publica de verdad.',
  'post.confirmar': 'Confirmar',

  // ── Ficha: recuperar enlaces ──
  'recon.pregunta':
    '¿Alguna publicación dice «sin enlace guardado»? Se recupera de Instagram, para todas las ' +
    'influencers de una vez.',
  'recon.consultando': 'Consultando…',
  'recon.recuperar': 'Recuperar enlaces',
  'recon.resumen':
    '{encontradas} publicaciones en la cuenta · {vinculadas} vinculadas · {corregidas} corregidas',
  'recon.corregido': '↻ enlace corregido: {tema}',
  'recon.yaNoEsta': 'Ya no está en la cuenta: {tema}',
  'recon.sinEmparejar': 'Sin emparejar: {tema}',
  'recon.noSePudoLeer': 'No se pudieron leer.',

  // ── Ficha: métricas de la cuenta ──
  'met.titulo': 'Métricas',
  'met.seguidores': 'Seguidores',
  'met.siguiendo': 'Siguiendo',
  'met.publicaciones': 'Publicaciones',
  'met.ultimosDias': 'Últimos {n} días',
  'met.visualizaciones': 'Visualizaciones',
  'met.alcance': 'Alcance',
  'met.interacciones': 'Interacciones',
  'met.cuentasAlcanzadas': 'Cuentas alcanzadas',
  'met.sinEstadisticas':
    'Instagram todavía no devuelve estadísticas de esta cuenta. Suele pasar mientras no haya ' +
    'publicaciones con alcance.',
  'met.leido': 'Leído {cuando}',

  // ── Ficha: pestaña Plataformas ──
  'plat2.usuario': 'Usuario',
  'plat2.loConfirmaFanvue': 'Lo confirma Fanvue al vincular el perfil',
  'plat2.sinCrear': 'Sin crear',
  'plat2.estado': 'Estado',
  'plat2.correo': 'Correo',
  'plat2.llegaA': 'Los mensajes llegan a',
  'plat2.administracion': 'Administración',
  'tg.escribir': 'Escribir la descripción del canal',
  'tg.otraVez': 'Escribir otra',
  'tg.escribiendo': 'Escribiendo…',
  'tg.escribirQue':
    'La escribe el modelo con su nombre y su país, en tono atrevido, y la aplica al canal. ' +
    'Son 255 caracteres, que es el tope de Telegram. Se puede volver a generar.',
  'tg.noSePudo': 'No se pudo escribir la descripción.',
  'multi.titulo': 'Publicar en varias a la vez',
  'multi.explica':
    'Sale en las marcadas, una después de la otra. Las que ya salieron no aparecen acá.',
  'multi.conStars': 'cobrando {{stars}} Stars',
  'multi.gratis': 'gratis',
  'multi.regalar': 'Regalarla en Telegram',
  'multi.publicarEn': 'Publicar en {{n}}',
  'multi.seVanA': 'Se publica de verdad en {{lista}}.',
  'multi.publicando': 'Publicando {{hechas}} de {{total}}…',
  'multi.salieron': 'Salió en {{n}}.',
  'multi.salieronConManual': 'Salió en {{n}}, y {{manual}} quedaron como paquete manual.',
  'multi.noSalieron': 'Salió en {{n}}, y {{fallos}} no salieron.',
  'multi.elegiUna': 'Marcá al menos una.',
  'tg.fijar': 'Escribir el mensaje de bienvenida',
  'tg.fijarOtra': 'Escribir otro',
  'tg.fijarQue':
    'Lo publica en el canal y lo ancla arriba, que es lo que ve quien entra. Un canal no puede ' +
    'saludar a quien se suscribe: el mensaje anclado es lo más parecido que hay.',
  'tg.noSeFijo': 'No se pudo escribir el mensaje.',
  'tg.sinAnclar':
    'El mensaje se publicó, pero no se pudo anclar: al bot le falta el permiso de anclar, que ' +
    'es distinto del de publicar. Se puede anclar a mano desde Telegram.',
  'plat2.publicaCon': 'Publica con',
  'plat2.botCompartido': 'El bot compartido, desde Credenciales',
  'plat2.conexionOauth': 'Conexión OAuth',
  'plat2.conexionGeneral': 'Conexión general del manager',
  'plat2.conScopes': '{estado} ({n} scopes)',
  'plat2.sinConectar': 'Sin conectar',
  'plat2.usuariosSugeridos': 'Usuarios sugeridos',
  'plat2.disponibilidad': 'La disponibilidad no se verifica: comprobala al crear la cuenta.',
  'plat2.buscandoPaginas': 'Buscando tus Páginas…',
  'plat2.cualPagina': '¿Cuál es la Página de esta influencer?',
  'plat2.cualPaginaQue':
    'Tu perfil administra varias. La que elijas queda atada a ella: el contenido se va a ' +
    'publicar ahí.',
  'plat2.conectadaA': 'Conectada a «{pagina}».',
  'plat2.noSePudoConectar': 'No se pudo conectar.',
  'plat2.conectando': 'Conectando…',
  'plat2.consultandoFanvue': 'Consultando Fanvue…',
  'plat2.primeroManagerAntes': 'Primero conectá una sola vez la cuenta manager desde ',
  'plat2.perfilAsignado': 'Perfil de Fanvue asignado.',
  'plat2.noSePudoAsignar': 'No se pudo asignar el perfil.',
  'plat2.perfilDesasignado': 'Perfil de Fanvue desasignado.',
  'plat2.noSePudoDesasignar': 'No se pudo desasignar el perfil.',
  'plat2.administradaPor': '@{handle} · administrada por la conexión general',
  'plat2.quitando': 'Quitando…',
  'plat2.desasignar': 'Desasignar',
  'plat2.elegiCual': 'Elegí cuál de los perfiles que administra tu manager es esta influencer.',
  'plat2.elegirPerfil': 'Elegir perfil…',
  'plat2.asignando': 'Asignando…',
  'plat2.asignar': 'Asignar',
  'plat2.sinCreadoras':
    'Tu manager todavía no administra ninguna creadora. Creá su cuenta en Fanvue con la casilla ' +
    'de arriba, y desde ESA cuenta invitá al manager en Connect Management.',
  'plat2.todasAsignadas':
    'Todas las creadoras que administra tu manager ya están asignadas a otra influencer. Creá ' +
    'una cuenta nueva en Fanvue e invitá al manager desde ella.',
  'plat2.confirmarCasilla':
    'La dirección que se asigne es PERMANENTE: Fanvue no deja cambiarla después. ¿Asignarla ahora?',
  'plat2.casillaAsignada': 'Casilla asignada: {direccion}',
  'plat2.asignarCasilla': 'Asignar casilla propia',
  'plat2.casillaQueAntes': 'Fanvue exige una dirección real y distinta por cuenta, y ',
  'plat2.rechazaAlias': 'rechaza los alias con «+»',
  'plat2.casillaQueMedio':
    ', así que la heredada de Instagram no sirve acá. Ésta vive en el dominio del panel, su ' +
    'correo se lee en «Correo recibido», y ',
  'plat2.noTocaOtras': 'no toca la de las otras plataformas',

  // ── Ficha: pestaña Correo ──
  'correo.suDireccion': 'Su dirección',
  'correo.todaviaNoTiene': 'Todavía no tiene',
  'correo.cambiarlaPor': 'Cambiarla por',
  'correo.nombre': 'nombre',
  'correo.cambiando': 'Cambiando…',
  'correo.siCambiarla': 'Sí, cambiarla',
  'correo.armadoQue':
    'La dirección anterior deja de existir: lo que llegue a ella va a rebotar. Si hay un alta a ' +
    'medias, el enlace de confirmación se pierde.',
  'correo.enReposoQue':
    'Se usa en todas sus plataformas. Los correos que ya recibió no se pierden al cambiarla.',
  'correo.ahoraSuDireccion': 'Ahora su dirección es {direccion}.',
  'correo.noSePudoCambiar': 'No se pudo cambiar la dirección.',
  'correo.recibido': 'Correo recibido · {n}',
  'correo.seConserva':
    'Se conserva treinta días. Lo que se necesita de estos mensajes —un enlace de confirmación— ' +
    'sirve el primer día.',
  'correo.nadaAun': 'Todavía no llegó nada',
  'correo.nadaAunQue':
    'Acá aparecen las confirmaciones y los códigos que le manden a su dirección. No hace falta ' +
    'abrir ninguna casilla.',
  'correo.llegaronAntes': 'Llegaron {n} correos a su casilla. Están en la pestaña ',

  // ── Ficha: conexión OAuth ──
  'oauth.noSePudoDesconectar': 'No se pudo desconectar.',
  'oauth.conectada': 'Cuenta conectada. Ya se puede publicar desde acá.',
  'oauth.cancelada': 'Se canceló la autorización. No cambió nada.',
  'oauth.sinPermiso':
    'Conectada, pero sin permiso para publicar. Falta agregar {permiso} en la app de Meta, y ' +
    'volver a conectar.',
  'oauth.otro': '{plataforma}: {resultado}',
  'oauth.sinPermisoAviso':
    'Conectada sin permiso de publicar: el contenido va a salir como paquete manual.',
  'oauth.desconectar': 'Desconectar {plataforma}',
  'oauth.conectar': 'Conectar {plataforma}',

  // ── Ficha: contraseña de la cuenta ──
  'clave.titulo': 'Contraseña',
  'clave.ocultar': 'Ocultar',
  'clave.mostrar': 'Mostrar',
  'clave.borrar': 'Borrar',
  'clave.guardada': 'Guardada {cuando}',
  'clave.laQueUsaste': 'La que usaste al crear la cuenta',
  'clave.corregir': 'Corregir lo que cargué',
  'clave.direccionDeCorreo': 'Dirección de correo',
  'clave.identificadorPagina': 'Identificador de la Página',
  'clave.nombreDeUsuario': 'Nombre de usuario',
  'clave.passwordNueva': 'Contraseña nueva',
  'clave.dejaloVacio': 'Dejalo vacío para no cambiarla',

  // ── Ficha: biografías sugeridas ──
  'bio.creadoraDe': '{quien} es creadora de contenido digital',
  'bio.sobre': ' sobre {nichos}',
  'bio.desde': ', desde {lugar}',

  // ── Ficha: datos para completar una tarea ──
  'datos.nombrePublico': 'Nombre público',
  'datos.usuarioSugerido': 'Usuario sugerido',
  'datos.sinSugerencias': 'No se pudieron derivar sugerencias del nombre público.',
  'datos.categoria': 'Categoría / nicho',
  'datos.bioSugerida': 'Biografía sugerida ({n} caracteres)',
  'datos.sitioWeb': 'Sitio web de la Página (ahí sí queda como enlace)',
  'datos.fotoDePerfil': 'Foto de perfil',
  'datos.fotoDePortada': 'Foto de portada ({ancho}×{alto})',
  'datos.aliasSobreCasilla':
    'Alias sobre tu casilla principal. No hay nada que crear: los correos llegan a tu bandeja.',
  'datos.fanvueNecesitaPropia':
    'Fanvue rechaza los alias con «+», así que necesita una dirección propia. Se asigna desde la ' +
    'sección de Fanvue de esta influencer.',
  'datos.sinCasilla':
    'Todavía no hay casilla. Configurá una casilla principal o un dominio propio en Credenciales ' +
    '—y la dirección se genera sola para todas— o completá «Crear el correo electrónico» para ' +
    'poner una a mano.',
  'datos.disclosure': 'Disclosure para la biografía',
  'datos.nombreParaLaCuenta': 'Nombre para la cuenta',
  'datos.direccionesSugeridas': 'Direcciones sugeridas (probá en orden)',
  'datos.sinDirecciones': 'El nombre público no da ninguna dirección válida para Gmail.',
  'datos.categoriaSugerida': 'Categoría sugerida',
  'datos.correoDeLaCuenta': 'Correo de la cuenta',
  'datos.sinCasillaPropia':
    'Todavía no tiene casilla propia. Asignala con el botón de acá abajo: la que figura en ' +
    'Plataformas es un alias con «+» heredado de Instagram, y Fanvue lo rechaza porque lo ' +
    'colapsa a la dirección base.',
  'datos.deQuien': 'Datos de {quien}',
  'datos.abrirImagen': 'Abrir la imagen en una pestaña nueva',
  'datos.portadaGenerada': 'Portada generada. Está en «Foto de portada», acá abajo.',
  'datos.noSePudoGenerar': 'No se pudo generar.',
  'datos.generandoPortada': 'Generando portada…',
  'datos.generarPortada': '🖼 Generar foto de portada',
  'datos.portadaPorQue':
    'Apaisada y con la figura corrida a la derecha, porque Facebook recorta la portada a 851×315 ' +
    'y le tapa la esquina inferior izquierda con la foto de perfil. Ninguna toma del sistema ' +
    'sirve: todas son verticales. Usa el modelo de la etapa 3 —el mismo del contenido— que elegís ' +
    'en Consumo: es el que acepta referencias y elige proporción, y sin las dos cosas la portada ' +
    'sale con otra cara o con otra forma.',
  'datos.bannerGenerado': 'Banner generado. Está acá abajo.',
  'datos.generandoBanner': 'Generando banner…',
  'datos.generarBanner': '🛏 Generar banner de Fanvue',
  'datos.bannerPorQue':
    'Acostada en una cama, en 1904×536 para las medidas que pide Fanvue (1192×335). Acostada ' +
    'porque un cuerpo horizontal es lo único que llena una franja tres veces y media más ancha ' +
    'que alta; de pie queda una columna y dos tercios de pared. Sale un escalón por debajo de su ' +
    'nivel de piel —como mucho lencería— porque el banner se ve sin suscripción y Fanvue lo exige ' +
    'apto. La esquina inferior izquierda queda libre para la foto de perfil.',
  'datos.noSePudoGuardarTarea': 'No se pudo guardar la tarea.',
  'datos.passwordOpcional': 'Contraseña',
  'datos.opcional': '(opcional)',
  'datos.laQuePusiste': 'La que pusiste al crear la cuenta',
  'datos.seGuardaCifrada':
    'Se guarda cifrada. Perder la contraseña de una cuenta ya verificada es perder la cuenta; ' +
    'podés consultarla después en Plataformas.',

  // ── Ficha: página pública y tareas ──
  'proxy.titulo': 'Página pública',
  'proxy.que':
    'Una dirección propia en este dominio, con su foto y todas sus redes. Va en la biografía de ' +
    'cada red: Instagram deja poner un enlace y X deja otro, así que sin esto hay que elegir a ' +
    'cuál de los destinos se renuncia — y cambiarlo a mano cada vez que aparece una cuenta.',
  'proxy.faltaCanonica':
    'Falta elegir su frontal canónica en la galería. Es la foto que muestra la página, y sin ella ' +
    'queda un nombre sobre fondo oscuro.',
  'proxy.abrir': 'Abrir',
  'proxy.generando': 'Generando…',
  'proxy.generar': 'Generar página proxy',
  'proxy.publicada': 'Página publicada.',
  'proxy.despublicada': 'Página despublicada: ahora responde 404.',
  'proxy.noSePudo': 'No se pudo cambiar la página.',

  'tareas.sinTareas': 'Sin tareas',
  'tareas.sinTareasQue': 'No hay acciones humanas pendientes.',
  'tareas.faltaCuenta': 'crear tu cuenta de creador',
  'tareas.faltaKyc': 'verificar tu identidad',
  'tareas.faltaY': ' y ',
  'tareas.antesDeFanvue': 'Antes de los pasos de Fanvue',
  'tareas.antesDeFanvueMedio': ', te falta {que} en Fanvue. Se hace ',
  'tareas.unaSolaVez': 'una sola vez',
  'tareas.antesDeFanvueDespues':
    ' y vale para todas tus influencers: sin eso no se puede crear el perfil vinculado de ésta. ',
  'tareas.estaEnCredenciales': 'Está en Credenciales.',
  'tareas.faltaAlgunPaso':
    '¿Falta algún paso? Los que ya están se actualizan solos; éste agrega los que se hayan sumado ' +
    'desde que se generó la influencer.',
  'tareas.faltaCorreo':
    'Falta crear el correo electrónico. Toda plataforma lo pide al registrarse.',
  'tareas.grupoCorreo': 'Correo electrónico',
  'tareas.grupoInterno': 'Revisión interna',
  'tareas.faltaPrincipal':
    'Falta decidir cuál es la cuenta principal de Fanvue. De eso depende si ésta se da de alta ' +
    'con documento de identidad o como cuenta vinculada, que son dos trámites distintos.',
  'tareas.faltaRetiro':
    'La cuenta principal todavía no completó un retiro, y Fanvue no habilita cuentas vinculadas ' +
    'hasta entonces. Su alta se destraba sola cuando ese paso se marque como hecho.',

  // ── Ficha: cuenta principal de Fanvue ──
  'principal.correoRegistrada': 'Correo con el que quedó registrada',
  'principal.elQueUsaste': 'el que usaste en Fanvue',
  'principal.correoQue':
    'Puede no ser la casilla del panel: la principal suele registrarse con un correo tuyo, y a ' +
    'veces ya existía. Es el que sirve para recuperar la cuenta.',
  'principal.laPrincipal': 'la principal',
  'principal.ahoraEs': '{quien} es ahora la cuenta principal.',
  'principal.sinPrincipal': 'Sin cuenta principal.',
  'principal.antesDeEmpezar': 'Antes de empezar: ',
  'principal.cualEs': '¿cuál de tus influencers es la cuenta principal de Fanvue?',
  'principal.cualEsQue':
    ' Es la que lleva tu verificación de identidad. Hay una sola, y las demás se crean colgando ' +
    'de ella. El panel no lo puede averiguar solo: el KYC vive en Fanvue.',
  'principal.es': 'Es {quien}',
  'principal.comoSeCrea': 'Cómo se crea su cuenta',
  'principal.altaNormal': 'Con el alta normal: es tu cuenta principal.',
  'principal.altaNormalAntes':
    'Usá «Abrir la plataforma», con tu sesión cerrada o en una ventana privada. Podés registrarla ' +
    'con la casilla del panel o con un correo tuyo — la verificación llega adonde lo pongas. ',
  'principal.llevaKyc': 'Sí lleva verificación de identidad',
  'principal.altaNormalDespues':
    ': Fanvue verifica a la persona que cobra, y esa verificación tiene que vivir en alguna ' +
    'cuenta. Las demás influencers cuelgan de ésta.',
  'principal.vinculada': 'Como cuenta vinculada, sin documentos.',
  'principal.vinculadaAntes': 'Entrá con ',
  'principal.vinculadaMedio1':
    ' y andá a Configuración → Cuenta → Cuentas vinculadas → «Crear cuenta vinculada». Elegí tipo ',
  'principal.tipoIa': 'IA',
  'principal.vinculadaMedio2':
    ' y poné el correo de «Correo de la cuenta», acá abajo — copialo en vez de escribirlo, que es ' +
    'permanente. El enlace de alta llega a esa casilla y se lee en «Correo recibido», en esta ' +
    'misma ficha. ',
  'principal.noUsesAltaNormal': 'No uses el alta normal',
  'principal.vinculadaMedio3':
    ': pediría documentos otra vez. Fanvue no habilita la vinculación hasta que la principal haya ' +
    'completado ',
  'principal.unRetiro': 'un retiro',
  'principal.esOtra': 'No, la principal es otra',
  'principal.enRealidadEs': 'En realidad la principal es {quien}',

  // ── Ficha: tarjeta de tarea, workflow, consumo y auditoría ──
  'tarea.faltaPasoPrevio': 'Falta un paso previo',
  'tarea.yaLoCompleté': 'Ya lo completé',
  'tarea.revalidar': 'Revalidar',
  'tarea.verInstrucciones': 'Ver las instrucciones de este paso',
  'tarea.queHacer': 'Qué hacer',
  'tarea.aliasSobreCasilla': 'Alias sobre tu casilla principal',
  'tarea.direccionCreada': 'Dirección que quedó creada',
  'tarea.usarEstaDireccion': 'Usar esta dirección',
  'tarea.guardarYCompletar': 'Guardar y completar',
  'tarea.nadaQueCrear': 'No hay que crear nada: los correos de esta dirección llegan a tu bandeja.',
  'tarea.seGuardaParaTodas':
    'Se guarda para todas las plataformas de esta influencer. Si configurás una casilla principal ' +
    'en Credenciales, este paso se resuelve solo.',
  'tarea.usuarioEnFanvue': 'Usuario que quedó en Fanvue',
  'tarea.usuarioEnFanvueQue':
    'El usuario es el del perfil público, sin la arroba. La contraseña es la de SU cuenta de ' +
    'Fanvue, no la del manager ni la tuya: el paso siguiente pide entrar con ella para invitar al ' +
    'manager.',
  'tarea.identificadorPagina': 'Identificador de la Página',
  'tarea.identificadorPaginaQue':
    'Cómo se consigue: andá a Plataformas y pulsá «Conectar Facebook». Va a fallar a propósito y ' +
    'te va a listar tus Páginas con su identificador; copiá el de ésta, pegalo acá y volvé a ' +
    'conectar. NO es el número de «profile.php?id=»: en las Páginas nuevas son dos distintos, y el ' +
    'que usa la API sólo lo sabe Meta.',
  'tarea.usuarioRegistradoEn': 'Usuario que quedó registrado en {plataforma}',
  'tarea.usuarioRegistradoQue':
    'Poné el que conseguiste, no el sugerido: la disponibilidad no se verifica y lo más probable ' +
    'es que hayas tenido que usar otro.',
  'tarea.porQueCerrado': '▸ Por qué hace falta, y qué no compartir',
  'tarea.porQueAbierto': '▾ Por qué hace falta',
  'tarea.queDatosUsar': 'Qué datos usar',
  'tarea.queNoCompartir': 'Qué NO compartir nunca',
  'tarea.hecho': '✓ Hecho',
  'tarea.marcadaCompletada': 'Marcada como completada',

  'wf.sinCorridas': 'Sin corridas',
  'wf.sinCorridasQue': 'No hay workflows registrados.',
  'wf.corrida': 'Corrida {id}',
  'wf.creditos': '{n} créditos',

  'uso.candidatas': 'Candidatas frontales',
  'uso.candidatasQue': 'se genera un lote y se elige una; el resto se descarta',
  'uso.hoja': 'Hoja de identidad',
  'uso.hojaQue': 'los ángulos que anclan la cara en todo lo demás',
  'uso.tomas': 'Tomas de contenido',
  'uso.tomasQue': 'las que se ven en Contenido, incluidas las descartadas',
  'uso.titulo': 'Consumo de esta influencer',
  'uso.creditosEn': 'créditos estimados en {n} imágenes, contando todas las etapas',
  'uso.imagenes': '{n} imágenes',
  'uso.otras': 'Otras',
  'uso.publicacionesExternas': 'Publicaciones externas',
  'uso.nadaPublicado': 'Todavía no se publicó nada.',
  'uso.conMetricas': 'con métricas',
  'uso.sinMetricas': 'sin datos de métricas',

  'audit.titulo': 'Registro de auditoría',
  'audit.sinEventos': 'Sin eventos.',

  // ── Ficha: aviso de error de creación ──
  'errcre.noSePudoRetomar': 'No se pudo retomar.',
  'errcre.descartar': 'Descartar este aviso',
  'errcre.irAConsumo': 'Ir a Consumo',
  'errcre.retomando': 'Retomando…',
  'errcre.yaLoHabilite': 'Ya lo habilité, continuar',
  'errcre.gastoAntes': 'La ficha está bien: lo que frena es el ',
  'errcre.modoCero': 'modo USD 0',
  'errcre.gastoDespues':
    ', que viene activo para que nadie gaste sin querer. Este nivel de piel genera con un ' +
    'proveedor que cobra. Desactivalo en Consumo, o elegí un nivel que use el proveedor gratuito.',
  'errcre.reintentarAhora': 'Reintentar ahora',
  'errcre.cuotaQue':
    'Se acabó el presupuesto del día. No hay nada que corregir: se retoma solo cuando la cuota se ' +
    'reinicia, o podés forzarlo si ya subiste el tope.',
  'errcre.corregirFicha': 'Corregir la ficha',
  'errcre.yaLaCorregi': 'Ya la corregí, continuar',
  'errcre.politicaQue':
    'Esto no se arregla reintentando: hay que cambiar el texto que la política rechazó. Guardar ' +
    'la ficha la valida.',
  'errcre.reintentar': 'Reintentar',
  'errcre.reintentable': 'Se retoma con la misma ficha. Lo ya generado no se vuelve a pagar.',
  'errcre.noReintentable':
    'Reintentar puede no alcanzar: mirá el mensaje de arriba antes de insistir.',

  // ── Credenciales: Fanvue ──
  'cred.fanvueConectado': 'Fanvue conectado.',
  'cred.sinPermisoPublicar':
    'Conectó, pero falta el permiso de publicar. Volvé a conectar aceptando todo.',
  'cred.fanvueRechazo': 'Fanvue rechazó la conexión: {motivo}',
  'cred.sinDetalle': 'sin detalle',
  'cred.correoManagerGuardado': 'Correo del manager guardado.',
  'cred.noSePudoGuardar': 'No se pudo guardar.',
  'cred.pasoCerradoUna': 'Listo. Se cerró el paso en {n} influencer.',
  'cred.pasoCerradoVarias': 'Listo. Se cerró el paso en {n} influencers.',
  'cred.listo': 'Listo.',
  'cred.tuCuentaDeCreador': 'Tu cuenta de creador',
  'cred.tuCuentaDeCreadorQue':
    'Creála con TUS datos reales como persona propietaria. Las influencers van después, como ' +
    'perfiles vinculados dentro de ella — no son cuentas separadas.',
  'cred.verificacion': 'Verificación de identidad',
  'cred.verificacionQue':
    'Documento y selfie, dentro de Fanvue. Tarda unos días hábiles. Se pide aunque tu cara no ' +
    'aparezca nunca en el contenido: verifica a quien cobra, no al personaje.',
  'cred.dosPasosAntes': 'Estos dos pasos se hacen ',
  'cred.unaSolaVez': 'una sola vez',
  'cred.dosPasosDespues':
    ' y valen para todas tus influencers. Ocurren dentro de Fanvue: acá sólo se anota que ya están.',
  'cred.hecho': 'Hecho',
  'cred.yaLoHice': 'Ya lo hice',
  'cred.conexionDeAgencia': 'Conexión de agencia',
  'cred.agenciaConectada':
    'Conectada. {n} perfil(es) asignado(s); una sola autorización publica por todos.',
  'cred.agenciaSinPermiso':
    'Conectada, pero sin permiso de publicar. Antes de volver a conectar, revisá que la app de ' +
    'Fanvue declare read:media en su panel: un permiso que la app no declara hace fallar la ' +
    'autorización con invalid_scope, aunque lo pidamos.',
  'cred.agenciaSinConectar':
    'Autorizá una vez con tu cuenta de manager y queda para todas las influencers que la hayan ' +
    'invitado.',
  'cred.volverAConectar': 'Volver a conectar',
  'cred.conectarFanvue': 'Conectar Fanvue',
  'cred.tresCuentas': 'Vas a usar tres cuentas de Fanvue distintas',
  'cred.cuentaPrincipal': 'Cuenta de creador principal',
  'cred.cuentaPrincipalQue':
    ' — la que lleva tu verificación de identidad. Es también tu primera influencer, y de ella ' +
    'cuelgan las demás como cuentas vinculadas.',
  'cred.manager': 'Manager',
  'cred.managerQue': ' — la que autoriza esta app y publica en todas.',
  'cred.deCadaInfluencer': 'De cada influencer',
  'cred.deCadaInfluencerQue': ' — sólo para invitar al manager.',
  'cred.cadaPasoDiceAntes':
    'Cada paso dice con cuál hay que estar. Equivocarse no falla en el momento: deja todo atado a ' +
    'la cuenta que no es, y el correo de una cuenta de Fanvue ',
  'cred.noSeCambiaNunca': 'no se puede cambiar nunca',
  'cred.sesionPanel': 'En el panel',
  'cred.sesionPrincipal': 'Cuenta principal',
  'cred.sesionManager': 'Cuenta de manager',
  'cred.sesionInfluencer': 'Cuenta de cada influencer',
  'cred.paso1Antes': 'Pulsá ',
  'cred.paso1Boton': '«Asignar casilla propia»',
  'cred.paso1Medio':
    ', acá abajo. El panel te da una dirección real bajo su dominio y el correo que le llegue se ' +
    'lee en esta misma pantalla.',
  'cred.paso1Segunda': 'Hace falta porque Fanvue exige un correo ',
  'cred.nuncaRegistrado': 'nunca registrado antes',
  'cred.paso1Fin': ' y rechaza los alias con «+».',
  'cred.paso2Antes': 'Entrá a ',
  'cred.paso2Medio': ' y pulsá ',
  'cred.registrarseManager': '«Registrarse como Manager»',
  'cred.paso2Fin': ', con la casilla del paso anterior.',
  'cred.paso2SiTeLleva': 'Si te lleva a la pantalla de inicio de sesión, ',
  'cred.iniciaSesion': 'iniciá sesión',
  'cred.paso2Devuelve': ': te devuelve solo. ',
  'cred.noCreesCuenta': 'No crees una cuenta ahí',
  'cred.paso2Fin2': ' — esa pantalla es para entrar.',
  'cred.paso3': 'Verificá la cuenta desde el enlace que llega al panel, y volvé.',
  'cred.paso4Antes': 'Cerrá sesión y entrá con la cuenta ',
  'cred.deElla': 'de ella',
  'cred.paso4Medio': '. En Connect Management, en «Conecta un manager», pegá ',
  'cred.correoDelManagerFuerte': 'el correo del manager',
  'cred.paso4Fin': ' — no el de ella.',
  'cred.paso4Segunda':
    'Estás dentro de su cuenta y el correo que va es el otro: es el paso que más se presta a error.',
  'cred.paso5': 'Aceptá la invitación que le llegó.',
  'cred.paso6Antes': 'Volvé acá y pulsá ',
  'cred.paso6En': ', en ',
  'cred.paso6Seccion': '«2. Cuenta manager y perfiles»',
  'cred.paso6Medio': '. Te va a pedir entrar aunque tengas sesión: poné las credenciales ',
  'cred.delManager': 'del manager',
  'cred.paso6Fin': ', no las de tu cuenta principal ni las de la influencer.',
  'cred.paso6Privada': 'Hacelo en una ',
  'cred.ventanaPrivada': 'ventana privada',
  'cred.paso6PrivadaFin':
    ', o cerrá sesión en Fanvue antes: si queda abierta la de la influencer del paso anterior, ' +
    'autoriza a esa cuenta y no al manager.',
  'cred.correoDelManager': 'Correo del manager',
  'cred.casillaAsignada': 'Casilla asignada: {direccion}',
  'cred.asignando': 'Asignando…',
  'cred.asignarCasilla': 'Asignar casilla propia',

  // ── Credenciales: el catálogo de servicios ──
  'kind.cf.title': 'Cuentas de Cloudflare',
  'kind.cf.purpose':
    'Genera las imágenes con Workers AI. La cuota gratuita es por cuenta: 10.000 neuronas ' +
    'diarias cada una, con reinicio a las 00:00 UTC.',
  'kind.cf.console': 'Tokens de API de Cloudflare',
  'kind.cf.p1': 'Entrá al enlace y pulsá «Create Token» → «Create Custom Token».',
  'kind.cf.p2':
    'En «Permissions» agregá una fila por permiso, todas de tipo Account (ver la tabla de ' +
    'abajo). Si sólo querés generar imágenes, alcanza con el primero.',
  'kind.cf.p3': 'En «Account Resources» elegí la cuenta que querés usar.',
  'kind.cf.p4': 'Copiá el token y pegalo abajo. Sólo se muestra una vez.',
  'kind.cf.p5':
    'El identificador de cuenta está en la barra lateral del panel de Cloudflare, en «Account ID».',
  'kind.cf.permAi': 'Generar imágenes y texto. El único imprescindible.',
  'kind.cf.permWorkers': 'Desplegar. No hace falta para generar.',
  'kind.cf.permD1': 'Base de datos en producción.',
  'kind.cf.permR2': 'Las imágenes, la caché y la señal de cuota agotada.',
  'kind.cf.edit':
    'Si el token ya existe y sólo le faltan permisos, no hace falta crear otro: en la fila del ' +
    'token, menú de tres puntos → «Edit» → agregá los que falten → «Update token». Editar no ' +
    'cambia el valor, así que no hay que volver a pegarlo acá. «Roll», en cambio, sí genera uno ' +
    'nuevo y hay que actualizarlo.',
  'kind.cf.metaLabel': 'Identificador de cuenta',
  'kind.cf.metaHint': '32 caracteres. Está en la barra lateral del panel de Cloudflare.',
  'kind.cf.secret': 'Token de API',

  'kind.dominio.title': 'Dominio propio para los correos',
  'kind.dominio.purpose':
    'La opción más confiable: cada influencer recibe nombre@tudominio.com y todo se reenvía a tu ' +
    'casilla. Direcciones ilimitadas, sin «+» y sin teléfono. Funciona en todas las plataformas ' +
    'porque la dirección es indistinguible de cualquier otra — no hay nada que puedan rechazar. ' +
    'Gana sobre la casilla principal si están las dos.',
  'kind.dominio.console': 'Panel de Cloudflare',
  'kind.dominio.p1':
    'Necesitás un dominio en Cloudflare. Cuesta unos 10 USD al año; es lo único que no es gratis.',
  'kind.dominio.p2': 'En el dominio, entrá a «Email» → «Email Routing» y activalo.',
  'kind.dominio.p3':
    'Creá una regla catch-all que reenvíe a tu casilla, para no tener que crear una regla por ' +
    'influencer.',
  'kind.dominio.p4': 'Volvé acá y cargá el dominio y la casilla de reenvío.',
  'kind.dominio.metaHint': 'Suelto, sin https:// ni barras.',
  'kind.dominio.secret': 'Reenviar los correos a',
  'kind.dominio.fold':
    'Ya tenés casilla principal: esto es una mejora opcional, no algo que falte.',
  'kind.dominio.edit':
    'La activación de Email Routing no se comprueba desde acá: hacerlo exigiría un token con ' +
    'permiso sobre la zona, distinto del que usa el resto del sistema. Si no está activo los ' +
    'correos rebotan, y eso se nota en el primer alta.',

  'kind.casilla.purpose':
    'Alternativa gratuita al dominio propio: una sola casilla tuya de la que salen los correos de ' +
    'todas, con la forma tucorreo+nombre@gmail.com. Todo llega a tu bandeja. Con cualquiera de ' +
    'las dos configurada, el paso de «Crear el correo electrónico» deja de pedirte crear nada: la ' +
    'dirección se genera sola.',
  'kind.casilla.console': 'Cómo funcionan los alias con «+»',
  'kind.casilla.p1': 'Poné tu dirección tal cual, sin ninguna etiqueta: el sistema agrega la suya.',
  'kind.casilla.p2':
    'Sirve cualquier proveedor que soporte «+»: Gmail, Outlook, Proton y la mayoría.',
  'kind.casilla.p3':
    'No hay que crear nada ni verificar teléfonos. Los correos llegan a la casilla que pongas.',
  'kind.casilla.secret': 'Tu dirección de correo',
  'kind.casilla.fold': 'Ya tenés dominio propio, que gana sobre esto: no hace falta.',
  'kind.casilla.edit':
    'Ninguna plataforma documenta si acepta direcciones con «+», así que no se puede saber de ' +
    'antemano: se comprueba al registrarse. Si alguna la rechaza, la alternativa sin ese riesgo ' +
    'es un dominio propio con Cloudflare Email Routing — configurá EMAIL_DOMAIN y ' +
    'EMAIL_FORWARD_TO y cada influencer recibe nombre@tudominio.com, indistinguible de cualquier ' +
    'otra dirección.',

  'kind.fanvue.purpose':
    'Lo que permite publicar en Fanvue desde acá. Una sola app sirve para todas tus influencers: ' +
    'autorizás una vez con el manager y después asignás sus perfiles desde la ficha de cada ' +
    'influencer.',
  'kind.fanvue.console': 'Panel de desarrollador de Fanvue',
  'kind.fanvue.p1':
    'El acceso a la API está limitado a usuarios aprobados: si todavía no lo pediste, hacelo en ' +
    'el enlace de arriba. No hace falta empresa ni verificación — la política no pide entidad ' +
    'legal.',
  'kind.fanvue.p2':
    'La app tenés que crearla vos: Fanvue prohíbe expresamente crearla a pedido de un tercero, ' +
    'porque debe operarla quien la registra.',
  'kind.fanvue.p3':
    'En «Autenticación (OAuth)» vas a encontrar el Client ID y el Client Secret. El Client ID no ' +
    'es secreto —viaja en la URL de autorización—; la clave secreta sí.',
  'kind.fanvue.p4':
    'En «Redirecciones» agregá la URI que aparece abajo, tal cual. Si probás en los dos entornos, ' +
    'cargá las dos: la de producción y la de pruebas.',
  'kind.fanvue.p5':
    'En «Define permissions» marcá estos nueve: openid, offline_access, offline, write:post, ' +
    'write:media, read:post, read:creator, read:insights y read:agency. Los tres primeros son los ' +
    'que hacen que Fanvue devuelva un token de renovación: sin ellos la conexión dura una hora y ' +
    'se muere sin avisar. Los de chat NO — el sistema no responde mensajes, y pedir permisos que ' +
    'no se usan demora las revisiones.',
  'kind.fanvue.p6': 'Pegá el Client ID y la clave secreta abajo, y guardá.',
  'kind.fanvue.p7':
    'Los tokens de Fanvue duran una hora y se renuevan solos. A diferencia de Facebook, acá no ' +
    'hay token que dure para siempre.',
  'kind.fanvue.metaHint':
    'De «Autenticación (OAuth)». No es secreto: viaja en la URL de autorización.',
  'kind.fanvue.secretPh': 'De «Autenticación (OAuth)». No lo compartas por chat ni por correo.',

  'kind.fb.title': 'App de Meta — claves de Facebook',
  'kind.fb.purpose':
    'Lo que permite publicar en una Página de Facebook. Puede ser la MISMA app de Meta que la de ' +
    'Instagram, pero NO son las mismas claves: éstas son las de Configuración → Básica y aquéllas ' +
    'las del caso de uso de Instagram. Confundirlas rompe el inicio de sesión, y el error no dice ' +
    'cuál de las dos está mal.',
  'kind.meta.console': 'Apps de Meta for Developers',
  'kind.fb.p1':
    'Antes de esto, la influencer necesita una PÁGINA de Facebook creada desde tu perfil ' +
    'personal. No una cuenta propia: la API no publica en perfiles. Está en sus tareas.',
  'kind.fb.p2':
    'Podés reusar la app que ya creaste para Instagram. Entrá a esa app en el enlace de arriba.',
  'kind.fb.p3':
    'En el menú de la izquierda: Configuración → Básica. Ahí están el «Identificador de la app» y ' +
    'la «Clave secreta de la app». Son OTROS valores que los de Instagram, aunque sea la misma app.',
  'kind.fb.p4':
    'Agregá el producto «Inicio de sesión con Facebook» si no está: menú izquierdo → Agregar ' +
    'producto.',
  'kind.fb.p5':
    'En «Inicio de sesión con Facebook» → Configuración, agregá la URI de redirección que aparece ' +
    'abajo, tal cual. Es distinta de la de Instagram y las dos tienen que estar cargadas.',
  'kind.fb.p6': 'Pegá el identificador y la clave secreta abajo, y guardá.',
  'kind.fb.p7':
    'Mientras la app siga sin publicar, funciona sólo con tu propio perfil —el que tiene rol en ' +
    'la app— y con las Páginas que vos administrás. Para eso NO hace falta App Review: es el ' +
    'mismo mecanismo que ya usás en Instagram con el rol de evaluador. La revisión recién hace ' +
    'falta para publicar en Páginas de terceros.',
  'kind.fb.metaLabel': 'Identificador de la app',
  'kind.fb.metaHint': 'De Configuración → Básica. NO el «identificador de la app de Instagram».',
  'kind.fb.secret': 'Clave secreta de la app',
  'kind.fb.secretPh': '32 caracteres, de Configuración → Básica',

  'kind.ig.title': 'App de Meta — claves de Instagram',
  'kind.ig.purpose':
    'Lo que permite publicar desde acá en vez de descargar la imagen y subirla a mano. Una sola ' +
    'app sirve para todas tus influencers: mientras la app siga sin publicar y cada cuenta tenga ' +
    'el rol de evaluador, publicar no necesita App Review ni verificación de negocio.',
  'kind.ig.p1':
    'Antes de esto, la influencer necesita una cuenta de Instagram profesional (Creador o ' +
    'Empresa). No hace falta Página de Facebook. Está en sus tareas.',
  'kind.ig.p2':
    'Entrá al enlace y pulsá «Crear app». Se abre un asistente de cinco pasos: Detalles, Casos de ' +
    'uso, Negocio, Requisitos y Resumen.',
  'kind.ig.p3':
    'En «Casos de uso» elegí «Administrar mensajes y contenido en Instagram», el del ícono de ' +
    'Instagram. NO elijas «Otro»: Meta lo está retirando y lleva a la experiencia vieja.',
  'kind.ig.p4':
    'En «Negocio» elegí «Aún no quiero conectar un portfolio comercial». Sirve para acceder a ' +
    'cuentas de terceros y para publicar la app, y no hace falta para las tuyas. Se puede agregar ' +
    'después.',
  'kind.ig.p5':
    'Terminá el asistente. Lo que sigue ya es dentro del panel de la app, no del asistente.',
  'kind.ig.p6':
    'En el panel, primera fila: «Personalizar el caso de uso “Administrar mensajes y contenido en ' +
    'Instagram”». Pulsá la flecha de la derecha. Con el flujo nuevo NO hay un producto ' +
    '«Instagram» en el menú de la izquierda: todo cuelga del caso de uso.',
  'kind.ig.p7':
    'Se abre «Te damos la bienvenida a la API de Instagram». Ésta ya es la configuración con ' +
    'inicio de sesión de Instagram: si arriba dice «cambia a API setup with Facebook login», ' +
    'estás en la correcta y NO hay que cambiar nada.',
  'kind.ig.p8':
    'Arriba de todo están el «Identificador de la app de Instagram» y la «Clave secreta de la app ' +
    'de Instagram». Son los que van abajo. OJO: NO son los de Configuración → Básica; ésos son ' +
    'los de la app de Meta y con ellos el inicio de sesión de Instagram falla.',
  'kind.ig.p9':
    'FALTA UN PERMISO. El caso de uso trae instagram_business_basic, manage_comments y ' +
    'manage_messages, pero NO instagram_business_content_publish, que es el que permite publicar. ' +
    'Sin él el inicio de sesión funciona y la publicación falla.',
  'kind.ig.p10':
    'Agregalo desde el enlace «página de permisos y funciones» del punto 1, y pedí ' +
    'instagram_business_content_publish. Los dos de mensajes y comentarios podés dejarlos: no los ' +
    'usamos.',
  'kind.ig.p11':
    'Punto 4, «Configurar el inicio de sesión de empresa de Instagram»: agregá ahí la URI de ' +
    'redirección que aparece abajo, tal cual. Meta rechaza cualquier otra, incluso si difiere en ' +
    'una barra final.',
  'kind.ig.p12': 'Pegá el identificador y la clave secreta abajo, y guardá.',
  'kind.ig.p13':
    'Por cada influencer, una vez: en «Roles de la app» agregá su cuenta de Instagram como ' +
    'evaluador. Ella acepta la invitación desde Instagram; ese paso está en su tarea «Convertir a ' +
    'cuenta profesional», puntos 4 y 5, porque ocurre en la misma pantalla de Configuración. ' +
    'Mientras la app esté sin publicar, sólo las cuentas con ese rol pueden conectarse: no ' +
    'alcanza con que la cuenta sea tuya.',
  'kind.ig.metaLabel': 'Identificador de la app de Instagram',
  'kind.ig.metaHint':
    'Sólo números, arriba de «Te damos la bienvenida a la API de Instagram». No el de Meta.',
  'kind.ig.secret': 'Clave secreta de la app de Instagram',
  'kind.ig.secretPh': '32 caracteres, de esa misma pantalla',
  'kind.ig.edit':
    'La app se queda sin publicar y así está bien. El paso 5 de esa pantalla dice que hace falta ' +
    'revisión «para acceder a datos en vivo»: eso es para cuentas que no tienen rol en la app. ' +
    'Las que agregaste como evaluadoras funcionan sin revisión.\n\nLa comprobación mira la ' +
    'forma de los datos, no los consulta con Meta: el inicio de sesión de Instagram no expone ' +
    'manera de validar el par sin recorrer el flujo entero, que necesita una cuenta autorizando. ' +
    'La prueba real es la primera conexión.',

  'kind.runware.purpose':
    'Genera el contenido para adultos: las imágenes y los clips que sólo pueden publicarse en ' +
    'Fanvue, detrás del muro de pago. Se paga por generación y no tiene capa gratuita, así que ' +
    'sin esta clave el panel «Sólo Fanvue» se ve pero no genera. Si no vas a publicar contenido ' +
    'de ese tipo, no hace falta.',
  'kind.runware.console': 'Claves de Runware',
  'kind.runware.p1': 'Entrá al enlace y creá la cuenta.',
  'kind.runware.p2': 'Cargá saldo: cobra por uso, sin cuota mensual ni mínimo.',
  'kind.runware.p3': 'Pulsá «Create Key», ponele una descripción y copiala.',
  'kind.runware.p4':
    'Pegala abajo. Se guarda sin comprobar: estos proveedores no tienen forma gratuita de validar ' +
    'una clave, y hacerlo costaría una generación.',
  'kind.runware.secret': 'Clave de API',
  'stars.titulo': 'Telegram Stars',
  'stars.bajada':
    'Cuántas Stars cuesta desbloquear una foto explícita en el canal. Telegram la publica ' +
    'difuminada y la revela a quien paga.',
  'stars.imagenNsfw': 'Imagen explícita',
  'stars.referencia': '≈ USD {usd}, anotado el {fecha}',
  'stars.losOtros':
    'Los precios de set y video ya existen y todavía no se usan: el único camino que publica hoy ' +
    'es el de una imagen suelta.',
  'ventas.titulo': 'Vendido en Telegram',
  'ventas.deEsta': '· {n} ventas · {stars} ⭐',
  'ventas.avisoTitulo': 'Aviso de ventas',
  'ventas.avisoBajada':
    'Telegram cobra las Stars solo; para ENTERARSE de cada compra hace falta que avise a esta ' +
    'dirección. Sin esto el canal vende igual y no queda registrado en ningún lado. Ojo: ' +
    'registrarlo desconecta cualquier otro aviso que ese bot tuviera.',
  'ventas.avisoListo': 'Puesto: Telegram avisa cada compra.',
  'ventas.avisoFalta': 'Sin poner: las ventas no se están registrando.',
  'ventas.avisoDiagnostico': '{pendientes} avisos en cola. Último error: {error}',
  'ventas.avisoRegistrar': 'Registrar el aviso',
  'ventas.avisoRehacer': 'Volver a registrarlo',
  'ventas.avisoRegistrando': 'Registrando…',
  'ventas.avisoRegistrado': 'Telegram va a avisar cada compra.',
  'ventas.sinAtribuir': '{n} ventas sin poder atribuir a ninguna influencer.',
  'kind.telegram.purpose':
    'El bot que publica en los canales de tus influencers. Es uno solo para todas: un bot puede ' +
    'ser administrador de todos los canales que haga falta, y el mensaje sale con la identidad ' +
    'del canal, no la del bot. El saldo de Telegram Stars queda en la cuenta de este bot, sumado ' +
    'para todas.',
  'kind.telegram.console': 'BotFather, en Telegram',
  'kind.telegram.p1': 'Abrí @BotFather en Telegram y mandale /newbot.',
  'kind.telegram.p2': 'Ponele un nombre y un usuario que termine en «bot».',
  'kind.telegram.p3': 'Copiá el token que te devuelve. Si el bot ya existe, pedilo con /token.',
  'kind.telegram.p4':
    'Después, en cada influencer, agregá este bot como administrador de su canal con permiso ' +
    'de publicar. Sin eso el token sirve pero no puede escribir en ningún lado.',
  'kind.telegram.guia': 'Guía paso a paso',
  'kind.telegram.secret': 'Token del bot',
  'kind.civitai.purpose':
    'De acá salen los LoRA que usa tu GPU: los pesos de realismo, de piel y de estilo. Sin token, ' +
    'Civitai devuelve la página de inicio de sesión en vez del archivo, y la descarga queda en un ' +
    '.safetensors que ComfyUI rechaza mucho más tarde sin decir por qué.',
  'kind.civitai.console': 'Cuenta de Civitai',
  'kind.civitai.p1': 'Entrá al enlace con tu cuenta de Civitai.',
  'kind.civitai.p2': 'Bajá hasta «API Keys», al final de la página.',
  'kind.civitai.p3': 'Pulsá «Add API key», ponele un nombre y copiala: se muestra una sola vez.',
  'kind.civitai.p4': 'Sólo sirve para descargar pesos. No publica nada ni actúa en tu nombre.',
  'kind.civitai.secret': 'Token de API',

  // ── Credenciales: el resto de la pantalla ──
  'cred.correoCopiado': 'Correo copiado.',
  'cred.passwordManager': 'Contraseña del manager',
  'cred.guardadaSufijo': ' · guardada',
  'cred.escribiNueva': 'Escribí una nueva para reemplazarla',
  'cred.laDeTuCuenta': 'La de tu cuenta de Fanvue',
  'cred.passwordGuardada': 'Contraseña guardada y cifrada.',
  'cred.passwordQue':
    'Se guarda cifrada con la clave del entorno, igual que las de las influencers. No se ' +
    'comprueba: validarla exigiría iniciar sesión desde acá, que es lo que Fanvue trata como ' +
    'intento de abuso.',
  'cred.correoRecibido': 'Correo recibido · {n}',
  'cred.quincePerfilesAntes': 'Fanvue admite hasta ',
  'cred.quincePerfiles': '15 perfiles de creador de IA',
  'cred.quincePerfilesDespues':
    ' por identidad verificada, y se desbloquean después del primer pago: hay que cobrar la ' +
    'primera influencer antes de poder crear la segunda. ',
  'cred.abrirFanvue': 'Abrir Fanvue ↗',

  'cred.accesoAlPanel': 'Acceso al panel',
  'cred.noSePudoCambiar': 'No se pudo cambiar la contraseña.',
  'cred.passwordDeFabrica': 'El panel se abre con la contraseña de fábrica',
  'cred.passwordDeFabricaQue':
    'Está escrita en el Makefile del proyecto, así que no protege nada. Debajo hay tokens de ' +
    'Cloudflare, claves de API y contraseñas de cuentas: cambiala antes de exponer esto en ' +
    'internet.',
  'cred.passwordCambiada': 'Contraseña cambiada. Las sesiones ya abiertas siguen siendo válidas.',
  'cred.esLoUnico': 'Es lo único que protege todo lo que hay en esta pantalla.',
  'cred.passwordActual': 'Contraseña actual',
  'cred.passwordNueva': 'Contraseña nueva',
  'cred.minimo12': 'Mínimo 12 caracteres. Se guarda con PBKDF2, nunca en claro.',
  'cred.cambiando': 'Cambiando…',
  'cred.cambiarPassword': 'Cambiar contraseña',
  'cred.cambiarLaPassword': 'Cambiar la contraseña',

  'cred.titulo': 'Credenciales',
  'cred.cargando': 'Cargando credenciales…',
  'cred.accionFallo': 'No se pudo completar la acción.',
  'cred.bajadaAntes':
    'Todo se configura desde acá. Sólo hay que entrar al sitio del proveedor para ',
  'cred.generar': 'generar',
  'cred.bajadaDespues':
    ' la clave — cada bloque explica exactamente qué pulsar y tiene el enlace directo.',
  'cred.faltaClaveCifrado': 'Falta ',
  'cred.faltaClaveCifradoQue':
    'Sin clave de cifrado no se puede guardar ninguna credencial: se guardarían en claro en la ' +
    'base, y eso no se hace. Generala con ',
  'cred.faltaClaveCifradoO': ' o ',
  'cred.appYPermisos': '1. App y permisos de la API',
  'cred.porEntorno':
    'Hay una clave puesta por variable de entorno y está en uso. Lo que guardes acá la reemplaza; ' +
    'borrarlo vuelve a usar la del entorno.',
  'cred.sinConfigurar': 'Sin configurar',
  'cred.sinConfigurarQue': 'El sistema no puede usar {que} hasta que cargues una credencial.',
  'cred.configurar': 'Configurar',
  'cred.agregarOtra': 'Agregar otra cuenta',
  'cred.reemplazarEnUso': 'Reemplazar la que está en uso',
  'cred.reemplazarLa': 'Reemplazar la credencial',
  'cred.comoObtenerla': 'Cómo obtenerla',
  'cred.uriRedireccion': 'URI de redirección autorizada',
  'cred.permiso': 'Permiso',
  'cred.paraQue': 'Para qué',
  'cred.etiqueta': 'Etiqueta',
  'cred.etiquetaQue': 'Corta y estable. Es la que separa la cuota de cada cuenta en el consumo.',
  'cred.etiquetaEjemplo': 'principal',
  'cred.nombre': 'Nombre',
  'cred.nombreQue': 'Sólo para reconocerla acá.',
  'cred.nombreEjemplo': 'Cuenta personal',
  'cred.secretoPublico': 'Se guarda cifrada igual, aunque no sea un secreto.',
  'cred.secretoPrivado': 'Se guarda cifrado. No se vuelve a mostrar.',
  'cred.comprobando': 'Comprobando…',
  'cred.guardarYComprobar': 'Guardar y comprobar',
  'cred.alGuardarSeComprueba':
    'Al guardar se comprueba contra la API del proveedor. La comprobación no ejecuta ningún ' +
    'modelo, así que no gasta cuota.',
  'cred.imagenesConAntes': 'Las imágenes se generan con ',
  'cred.imagenesConDespues': '. El consumo de cada cuenta se lleva por separado — mirá ',
  'cred.consumo': 'Consumo',
  'cred.imagenesConFin': ' para verlo desglosado.',

  'cred.enUso': 'En uso',
  'cred.guardadaSinUsar': 'Guardada, sin usar',
  'cred.comprobada': 'Comprobada {cuando}',
  'cred.funcionaSinUsarAntes': 'Funciona, pero no se está usando. Pulsá ',
  'cred.usarEsta': 'Usar esta',
  'cred.funcionaSinUsarDespues': ' para que el sistema genere con ella.',
  'cred.probando': 'Probando…',
  'cred.probar': 'Probar',
  'cred.dejarDeUsar': 'Dejar de usar',
  'cred.activando': 'Activando…',
  'cred.borrar': 'Borrar',

  'cred.noSePudoMostrar': 'No se pudo mostrar la credencial.',
  'cred.seTapaSola': 'Se vuelve a tapar sola en 30 segundos. Queda anotado en la auditoría.',
  'cred.ver': 'Ver',
  'cred.tuPasswordDelPanel': 'Tu contraseña del panel',
  'cred.verificando': 'Verificando…',
  'cred.mostrar': 'Mostrar',

  'ext.titulo': 'Extensión del navegador',
  'ext.queHaceAntes':
    'La extensión muestra los referentes al costado de Instagram, navega al que elijas y cuenta a ' +
    'cuántos seguís. ',
  'ext.noSiguePorVos': 'No sigue por vos.',
  'ext.queHaceDespues':
    ' Un solo token para todas tus influencers: cuál se trabaja se elige en la propia extensión.',
  'ext.direccionDelPanel': 'Lo otro que pide la extensión: la dirección del panel',
  'ext.direccionQueAntes':
    'Ésta es la que conviene, aunque el panel también responda en su dirección de ',
  'ext.direccionQueDespues':
    ': si algún día cambia el nombre del Worker, la extensión sigue funcionando.',
  'ext.paraQueSirve': 'Para qué sirve',
  'ext.opcionExtension': 'Extensión del navegador — trabajar la captación',
  'ext.opcionMcp': 'MCP · sólo lectura — que una IA consulte el sistema',
  'ext.mcpQueAntes': 'Un cliente de IA va a poder ',
  'ext.leer': 'leer',
  'ext.mcpQueMedio':
    ' tus influencers, referentes, captación y consumo. No puede generar, publicar ni cambiar ' +
    'ajustes. Se conecta a ',
  'ext.mcpQueFin': ' con este token.',
  'ext.opcionMcpGeneracion': 'MCP · lectura y generación — que una IA además genere tandas',
  'ext.mcpGenera':
    'Además de leer, este token deja que un cliente de IA ARRANQUE TANDAS DE FOTOS, que cuestan ' +
    'dinero y consumen la cuota diaria. No puede publicar ni cambiar ajustes. Dáselo sólo a un ' +
    'cliente que uses vos.',
  'ext.mcpGeneracion': 'MCP · genera',
  'ext.copialoAhora': 'Copialo ahora: no se vuelve a mostrar.',
  'ext.paraReconocerlo': 'Para reconocerlo: «Chrome de casa»',
  'ext.creando': 'Creando…',
  'ext.crearToken': 'Crear token',
  'ext.sinNombre': 'sin nombre',
  'ext.mcpLectura': 'MCP · lectura',
  'ext.extension': 'extensión',
  'ext.usado': 'usado {cuando}',
  'ext.sinUsar': 'sin usar',
  'ext.regenerando': 'Regenerando…',
  'ext.regenerar': 'Regenerar',
  'ext.revocar': 'Revocar',
  'ext.tokenRegenerado': 'Token regenerado. El anterior dejó de servir.',
  'ext.noSePudoRegenerar': 'No se pudo regenerar el token.',
  'ext.noSePudoCrear': 'No se pudo crear el token.',

  // ── Consumo: perfiles de calidad ──
  'perfil.economyModelos': 'FLUX.1 schnell en las tres etapas. No acepta imágenes de referencia.',
  'perfil.balancedModelos': 'FLUX.2 Klein 4B en las tres etapas',
  'perfil.premiumModelos': 'Nano Banana 2 Lite (gemini-3.1-flash-lite-image) en las tres etapas',
  'perfil.premiumOjo':
    'Necesita facturación habilitada en Google AI Studio — el tier gratuito rechaza estos ' +
    'modelos. Y rechaza los registros sensual y atrevido: para ésos hace falta fal. Los umbrales ' +
    'de compatibilidad también hay que recalibrarlos: salieron de medir caras de FLUX.',
  'perfil.seedreamModelos':
    'Seedream 4.5 de ByteDance (fal-ai/bytedance/seedream/v4.5) en las tres etapas',
  'perfil.seedreamOjo':
    'El mejor medido para los registros sensual y atrevido. Contra el centroide de identidad de ' +
    'una influencer real dio 0,71-0,81 en las cuatro tomas — el rango más alto de todo lo ' +
    'probado— y sale a 12 megapíxeles, así que el rostro nunca queda con muy pocos píxeles. Cobra ' +
    'por imagen y no por segundos de GPU: sin arranques en frío. A diferencia de FLUX no arrastra ' +
    'la moderación de licencia de Black Forest Labs.',
  'perfil.seedream5Modelos':
    'Seedream 5.0 [pro] en las tres etapas. Hasta 2K y hasta diez referencias.',
  'perfil.seedream5Ojo':
    'El doble de precio que la 4.5. Su ruta todavía no se ejerció contra la API.',

  'op.image': 'Imagen',
  'op.video': 'Video',
  'op.text': 'Texto',
  'op.vision': 'Visión',
  'op.moderation': 'Moderación',

  // ── Consumo: gasto y cuota ──
  'consumo.titulo': 'Consumo y ajustes',
  'uso.gasto': 'Gasto',
  'uso.esteMes': 'este mes',
  'uso.deTope': ' · de {monto}',
  'uso.sinTope': ' · sin tope',
  'uso.hoyConsumidos': 'hoy · {creditos} {unidad} consumidos',
  'uso.sinTramoGratuitoAntes':
    'Sin el tramo gratuito de Workers AI, lo consumido este mes habría costado ',
  'uso.sinTramoGratuitoMedio': '. Se cobra sólo lo que excede las 10.000 diarias, más ',
  'uso.sinTramoGratuitoFin': ' de proveedores que facturan desde la primera llamada.',
  'uso.loQueSeGasto': 'Lo que se gastó',
  'uso.soloParaMirar': 'Sólo para mirar: nada de acá cambia cómo funciona el sistema.',
  'uso.consumoDeHoy': 'Consumo de hoy',
  'uso.cuotaAgotada': 'Cuota diaria agotada',
  'uso.reinicia': 'Reinicia {cuando}',
  'uso.cloudflare': 'Cloudflare',
  'uso.elProveedor': 'El proveedor',
  'uso.dejoDeAceptarAntes': ' dejó de aceptar peticiones. Nuestro ledger había estimado ',
  'uso.dejoDeAceptarDe': ' de ',
  'uso.dejoDeAceptarFin': ', pero la facturación real fue mayor.',
  'uso.estimacionPrevia':
    'La estimación previa sirve para frenar a tiempo, no para llevar la cuenta exacta: una ' +
    'generación rechazada consume cuota igual y las tarifas publicadas no cubren todo el costo. ' +
    'Cuando el proveedor responde, su dato manda sobre el nuestro.',
  'uso.creditosHoy': '{creditos} {unidad} hoy · {usd}',
  'uso.sinTopeDiario': 'Sin tope diario: nada frena la generación por presupuesto.',
  'uso.sinTopeDiarioAntes': 'Lo elegiste vos más abajo, en ',
  'uso.topeDeGastoDiario': 'Tope de gasto diario',
  'uso.sinTopeDiarioDespues':
    '. Siguen valiendo los frenos que no son de dinero: el proveedor cuando reporta su cuota ' +
    'agotada, y el tope mensual si lo pusiste.',
  'uso.estimados': '{usados} / {limite} {unidad} estimados',
  'uso.creditosSonAntes':
    'Créditos son la unidad de este sistema, no de ningún proveedor: la barra suma todo lo ' +
    'generado hoy — ',
  'uso.proveedorActivo': 'el proveedor activo',
  'uso.creditosSonMedio': ' con el perfil en uso—. El tope de ',
  'uso.creditosSonFin': ' lo fijaste vos más abajo, en ',
  'uso.primeras10k': ' Las primeras 10.000 de Workers AI son gratuitas.',
  'uso.sinTramoGratuito':
    ' Este perfil no toca el tramo gratuito de Cloudflare: se factura desde la primera imagen.',
  'uso.porProveedor': 'Consumo por proveedor (7 días)',
  'uso.sinConsumo': 'Sin consumo registrado.',
  'uso.paraQue': 'Para qué',
  'uso.creditos': 'Créditos',
  'uso.llamadas': 'Llamadas',
  'uso.fallos': 'Fallos',
  'uso.almacenamiento': 'Almacenamiento de imágenes',
  'uso.imagenesPunto': ' imágenes · ',
  'uso.mbDe': ' MB de ',
  'uso.disponibles': ' disponibles',
  'uso.enKv':
    'Las imágenes están en KV, no en R2. Activar R2 exige cargar una tarjeta aunque su tier ' +
    'gratuito no cobre nada, así que no es un requisito para desplegar. KV entra en el plan ' +
    'gratuito de Workers y alcanza para unas 500 influencers.',
  'uso.siLlegasAlLimite':
    'Si llegás al límite, borrá influencers que ya no uses desde su ficha: se elimina también su ' +
    'media y el espacio vuelve.',
  'uso.losFrenos': 'Los frenos',
  'uso.losFrenosQue': 'Qué impide que un error cueste dinero. Conviene tocarlo poco y a propósito.',

  // ── Consumo: frenos y topes ──
  'freno.modoCero': 'Modo USD 0',
  'freno.estadoAntes': 'Estado: ',
  'freno.activo': 'activo',
  'freno.desactivado': 'desactivado',
  'freno.modoCeroQue':
    'Con el modo activo, ninguna operación que pueda generar un cargo se ejecuta. Al agotarse la ' +
    'cuota gratuita, el trabajo se difiere al reinicio en vez de gastar. Desactivarlo requiere ' +
    'confirmación explícita y nunca ocurre de forma automática.',
  'freno.confirmarDesactivar':
    'Desactivar el modo USD 0 permite operaciones que pueden generar cargos reales. ¿Continuar?',
  'freno.desactivarModoCero': 'Desactivar el modo USD 0',
  'freno.confirmarVolver':
    'Volver al modo gratis:\n\n· Se activa el modo USD 0\n· El perfil de calidad pasa a ' +
    '«balanced» (Cloudflare)\n· El tope diario vuelve al tramo gratuito\n· Se DESACTIVAN las ' +
    'credenciales de fal y Gemini\n\nLas claves no se borran: vuelven con un clic desde ' +
    'Credenciales. Desde ese momento no hay ninguna operación que pueda generar un cargo. ' +
    '¿Continuar?',
  'freno.volviendo': 'Volviendo…',
  'freno.volverAlModoGratis': 'Volver al modo gratis',
  'freno.volverQueAntes':
    'Deja el sistema como venía de fábrica: sólo modelos gratuitos de Workers AI, sin ningún cargo ' +
    'posible. Desactiva también las credenciales de ',
  'freno.volverQueY': ' y ',
  'freno.volverQueDespues':
    ' —no las borra: vuelven con un clic— para que el estado se vea y no haya que confiar en un ' +
    'interruptor de otra pantalla. Las influencers con nivel de piel «sensual» o «atrevido» dejan ' +
    'de poder generar: ese nivel no tiene versión gratuita.',
  'freno.topeDiarioQue':
    'Lo aplica este sistema, sobre todos los proveedores. Ningún proveedor ofrece un corte de ' +
    'gasto propio, así que es el único freno que existe: al alcanzarlo, la generación se detiene ' +
    'hasta el día siguiente.',
  'freno.escribiEnDolares': 'O escribí el tope diario en dólares',
  'freno.confirmarQuitarTope':
    'Sin tope diario nada frena la generación por presupuesto. Este mes llevás USD {gasto}. ' +
    '¿Quitar el tope?',
  'freno.sinTopeActivo': 'Sin tope (activo)',
  'freno.sinTope': 'Sin tope',
  'freno.sinTopeDiarioAntes': 'Sin tope diario. Con el perfil ',
  'freno.cadaInfluencerCuesta': ' cada influencer cuesta ',
  'freno.yNadaLimita': ' — {usd} — y nada limita cuántas se generan por día.',
  'freno.hoyAntes': 'Hoy: ',
  'freno.porDiaConPerfil': ' por día. Con el perfil ',
  'freno.entranPorDia': 'Entran {n} por día.',
  'freno.noAlcanza': 'No alcanza ni para una: subí el tope o la generación va a quedar a medias.',
  'freno.topeMensual': 'Tope mensual',
  'freno.topeMensualQue':
    'El diario evita un día malo. Éste evita que treinta días normales sumen una factura que nadie ' +
    'decidió.',
  'freno.sinTopeMinuscula': 'sin tope',
  'freno.oEscribilo': 'o escribilo',
  'freno.sinNingunFreno':
    'Sin tope mensual y sin tope diario: no queda ningún freno de gasto. Lo único que puede ' +
    'detener la generación es que un proveedor reporte su cuota agotada.',
  'freno.sinTopeMensual':
    'Sin tope mensual: lo único que frena es el diario, y treinta días seguidos al máximo suman ' +
    '~USD {monto}.',
  'freno.herramientas': 'Herramientas',
  'freno.herramientasQue': 'Cosas que se configuran una vez y se olvidan.',
  'freno.featureFlags': 'Feature flags',
  'freno.inactivo': 'inactivo',
  'freno.puedeGenerarCargos': 'puede generar cargos',

  // ── Leads / referencias ──
  'ref.catModelos': 'Modelos',
  'ref.catMeme': 'Meme',
  'ref.catComic': 'Cómic',
  'ref.catTodas': 'Todas',
  'ref.titulo': 'Leads',
  'ref.cargando': 'Cargando cuentas…',
  'ref.bajadaIg': 'Cuentas ajenas que seguís para inspirarte o comparar, ordenadas por seguidores.',
  'ref.bajadaFb':
    'Páginas, grupos y personas de Facebook para abrir y revisar. Sólo enlaces: acá no se sigue a ' +
    'nadie.',
  'ref.noSePudoAsignar': 'No se pudo asignar.',
  'ref.noSePudoAnotar': 'No se pudo anotar la vuelta.',
  'ref.noSePudoAgregar': 'No se pudo agregar la cuenta.',
  'ref.agregarUna': 'Agregar una cuenta',
  'ref.placeholderFb': 'https://www.facebook.com/… · una Página, un grupo, un perfil',
  'ref.placeholderIg': 'https://www.instagram.com/usuario/  ·  o @usuario',
  'ref.nombreOpcional': 'Nombre (opcional)',
  'ref.agregando': 'Agregando…',
  'ref.agregar': 'Agregar',
  'ref.porUsuarioAntes': 'Se guarda por usuario, no por enlace: ',
  'ref.porUsuarioY': ' y ',
  'ref.porUsuarioFin': ' son la misma cuenta y no se duplican.',
  'ref.seguidoresAMano': 'Los seguidores se cargan a mano.',
  'ref.seguidoresAManoQue':
    ' Leerlos de una cuenta ajena tiene un único camino oficial —Business Discovery— y exige ' +
    'Facebook Login: app de Meta revisada y Página vinculada. Este panel usa Instagram Login ' +
    'justamente para no pedir eso. Y raspar el perfil no funciona: Instagram responde con muro de ' +
    'sesión a las IP de centro de datos, que es lo que somos.',
  'ref.cuentas': 'Cuentas · {n}',
  'ref.filtrarPorPais': 'Filtrar por país…',
  'ref.filtrarPorPaisAria': 'Filtrar por país',
  'ref.quitarFiltro': 'Quitar filtro',
  'ref.ningunaAun': 'Todavía no agregaste ninguna.',
  'ref.editarPais': 'Pulsá para editar el país',
  'ref.sinPais': '— país',
  'ref.editar': 'Pulsá para editar',
  'ref.seg': 'seg.',
  'ref.quitar': 'Quitar',
  'ref.cuantos': '¿cuántos?',
  'ref.aCuantosSiguio': 'A cuántos siguió {quien} desde @{cuenta}',
  'ref.anotarVuelta': 'Pulsá para anotar una vuelta a mano',
  'ref.quitarDe': 'Quitar {quien} de @{cuenta}',
  'ref.asignarA': 'Asignar @{cuenta} a una influencer',
  'ref.elegiInfluencer': 'Elegí una influencer…',
  'ref.masAsignar': '+ Asignar',

  // ── Bienvenida ──
  'bien.titulo': 'Bienvenida',
  'bien.bajada':
    'Tu cuenta arranca vacía a propósito: lo que generes es tuyo y nadie más lo ve. Esta guía es ' +
    'lo que hay que hacer una sola vez.',
  'bien.queEs': 'Qué es este sistema',
  'bien.queEsAntes': 'Sirve para ',
  'bien.crearYOperar': 'crear y operar influencers virtuales',
  'bien.queEsDespues':
    ': personajes ficticios con una cara estable, que publican fotos y videos en redes.',
  'bien.loDificilAntes':
    'Lo difícil no es generar imágenes lindas —eso lo hace cualquier modelo— sino que ',
  'bien.mismaPersona': 'todas sean de la misma persona',
  'bien.loDificilMedio':
    '. Por eso crear una influencer son tres etapas con dos pausas: primero elegís su cara entre ' +
    'cuatro candidatas, después se genera una «hoja de identidad» de tres ángulos que vos aprobás, ' +
    'y recién entonces el contenido se genera ',
  'bien.conEsasImagenes': 'con esas imágenes adjuntas',
  'bien.loDificilFin': ' para que la cara no se vaya.',
  'bien.paso1': '1 · Tu cuenta de Cloudflare',
  'bien.paso1QueAntes':
    'El sistema genera con tu cuenta, no con la de otro. Es gratis y alcanza para empezar: ' +
    'Cloudflare da ',
  'bien.creditosPorDia': '10.000 créditos de IA por día',
  'bien.paso1QueDespues': ' sin cargo.',
  'bien.creaCuentaEn': 'Creá una cuenta en ',
  'bien.noPideTarjeta': '. No pide tarjeta.',
  'bien.entraA': 'Entrá a ',
  'bien.perfilTokens': 'Perfil → Tokens de API ↗',
  'bien.yCreaUnoAntes': ' y creá uno con permiso de ',
  'bien.yCreaUnoDespues': '. Copialo: se muestra una sola vez.',
  'bien.copiaTambienAntes': 'Copiá también tu ',
  'bien.idDeCuenta': 'ID de cuenta',
  'bien.copiaTambienDespues': ', que está en la barra lateral del panel de Cloudflare.',
  'bien.pegaLosDos': 'Pegá los dos en ',
  'bien.credencialesCloudflare': 'Credenciales → Cuenta de Cloudflare',
  'bien.paso2': '2 · El texto, que también es gratis',
  'bien.paso2QueAntes':
    'Los textos —la ficha del personaje, las escenas, los captions— los escribe un modelo aparte. ' +
    'Con una clave de ',
  'bien.oGroq': ' o ',
  'bien.paso2QueDespues': ' salen bastante mejor, y las dos tienen uso gratuito.',
  'bien.seSacaEn': 'Se saca en ',
  'bien.ySePegaEn': ' y se pega en ',
  'bien.sinEstoIgual': '. Sin esto igual funciona, con el modelo gratuito de Cloudflare.',
  'bien.paso3': '3 · Generá tu primera influencer',
  'bien.paso3QueAntes': 'En ',
  'bien.influencersNueva': 'Influencers → Nueva',
  'bien.paso3QueDespues':
    '. Elegís su estilo visual —cómo se ven sus fotos, qué ropa, qué escenas— y el sistema hace el ' +
    'resto. Va a frenarse dos veces esperándote: para que elijas su cara, y para que apruebes su ' +
    'hoja de identidad.',
  'bien.conLoGratuitoAntes':
    'Con lo gratuito las imágenes salen más simples y no se puede generar contenido sensual ni ' +
    'video: eso necesita una clave de ',
  'bien.conLoGratuitoDespues':
    ', que se paga por uso. Cuando quieras dar ese paso está explicado en Credenciales.',
  'bien.antesDeGastar': 'Antes de gastar',
  'bien.modoCeroAntes': 'El ',
  'bien.modoCero': 'modo USD 0',
  'bien.modoCeroDespues':
    ' viene activo: ninguna operación que pueda generar un cargo se ejecuta. Al agotarse lo ' +
    'gratuito, el trabajo se difiere al día siguiente en vez de gastar.',
  'bien.siLoDesactivasAntes': 'Si lo desactivás, poné un ',
  'bien.topeDiarioYMensual': 'tope diario y uno mensual',
  'bien.siLoDesactivasDespues':
    ' en Consumo y ajustes. Ningún proveedor ofrece un corte de gasto propio: ese tope es el único ' +
    'freno que existe.',
  'bien.videoCuestaMas': 'Un video cuesta más que una influencer entera de imágenes.',
  'bien.videoCuestaMasQue': ' No es una imagen más cara: es otro orden de gasto.',
  'bien.manualCompleto': 'El manual completo',
  'bien.manualCompletoQue':
    'Esta guía es la puesta en marcha. Si buscás qué hace cada pantalla, cómo se conectan las ' +
    'redes, qué significa cada estado de una publicación o por qué algo no salió, está todo en el ' +
    'manual.',
  'bien.abrirManual': 'Abrir el manual ↗',
  'bien.listo': 'Listo ✓',
  'bien.entendido': 'Entendido, no la muestres más',
  'bien.quedaEnElMenu': 'Queda en el menú por si querés volver a leerla.',

  // ── Modelos ──
  'mod.perfilGratis': 'Gratis — Klein 4B, dentro de la cuota de Cloudflare',
  'mod.perfilPago': 'De pago — Seedream 4.5 por Runware',
  'mod.titulo': 'Modelos',
  'mod.bajada': 'Con qué se genera cada cosa. Lo que se gastó se mira en Consumo.',
  'mod.faltaCredencial': ' — falta la credencial',
  'mod.publicableEnTodas': 'Publicable en todas',
  'mod.publicableEnTodasQue': 'Lo que sale a Instagram, a Facebook y al perfil de Fanvue.',
  'mod.texto': 'Texto',
  'mod.modeloDeLenguaje': 'Modelo de lenguaje',
  'mod.modeloDeLenguajeQue':
    'Escribe las fichas, los captions y el plan de contenido. Los de Cloudflare son gratis dentro ' +
    'de la cuota diaria; los de Runware se cobran por millón de tokens. Elegir uno gratis pone a ' +
    'Cloudflare primero en la cascada.',
  'mod.modeloDeLenguajeAdultos':
    'Para las escenas y los textos de Fanvue. Un modelo con moderación no los escribe peor: contesta que no. Conviene el local, sin restricciones.',
  'mod.elMismoQueLoApto': 'El mismo que lo publicable',
  'mod.recomendado': 'Recomendado — {cual}',
  'mod.recomendadoSolo': 'Recomendado',
  'mod.descripcionDeImagenes': 'Descripción de imágenes',
  'mod.modeloQueDescribe': 'Modelo que describe',
  'mod.modeloQueDescribeQue':
    'Escribe el caption de cada imagen del set de entrenamiento del LoRA. Se cobra por imagen y el ' +
    'resultado queda cacheado: sólo se vuelve a describir si cambia el modelo.',
  'mod.perfilDeCalidad': 'Perfil de calidad',
  'mod.perfilDeCalidadHoy': 'Lo que se usa cuando no elegís nada abajo. Hoy: {cual}.',
  'mod.perfilDeCalidadQue': 'Lo que se usa cuando no elegís nada abajo.',
  'mod.canonica': 'Canónica',
  'mod.canonicaQue':
    'Las cuatro caras entre las que elegís. Es la única que parte de un prompt y nada más: de acá ' +
    'sale la persona.',
  'mod.elQueDigaElPerfil': 'El que diga el perfil de calidad',
  'mod.hojaDeIdentidad': 'Hoja de identidad',
  'mod.hojaDeIdentidadQue':
    'Los tres ángulos, generados adjuntando la canónica. Sin esa referencia serían tres personas ' +
    'distintas.',
  'mod.contenidoHastaPlaya': 'Contenido, hasta playa',
  'mod.contenidoHastaPlayaQue':
    'Calle, deportivo y playa. Cualquier modelo las hace bien, incluidos los gratuitos de ' +
    'Cloudflare.',
  'mod.contenidoSensual': 'Contenido sensual y atrevido',
  'mod.contenidoSensualQue':
    'Desde acá un filtro del fabricante estorba: rechaza la toma cobrando el intento, o la ' +
    'devuelve vestida. Conviene un modelo de pesos abiertos.',
  'mod.elMismoQueContenido': 'El mismo que el contenido',
  'mod.video': 'Video',
  'mod.videoQue':
    'Se anima una toma ya aprobada, desde la pestaña Contenido de cada influencer. El clip nace de ' +
    'una imagen que ya pasó por la cadena de identidad, así que es la misma persona. Un clip cuesta ' +
    'más que una influencer entera de imágenes: es otro orden de gasto.',
  'mod.hastaPlaya': 'Hasta playa',
  'mod.hastaPlayaQue': 'Calle, deportivo y playa. El mismo corte que en imagen.',
  'mod.sensualYAtrevido': 'Sensual y atrevido',
  'mod.sensualYAtrevidoQue':
    'Mismo criterio que en imagen: sin filtro ajeno que pueda rechazar lo que se pidió.',
  'mod.elMismoQueArriba': 'El mismo que el de arriba',
  'mod.soloFanvue': 'Sólo Fanvue',
  'mod.soloFanvueQue':
    'Material para adultos. Va al feed de suscriptores y el servidor impide publicarlo en cualquier ' +
    'otro lado.',
  'mod.imagenYVideo': 'Imagen y video',
  'mod.identidadPorMecanismo':
    'La identidad la sostiene un mecanismo del modelo, no el prompt: los que no lo tienen generan ' +
    'bien y devuelven a otra persona.',
  'mod.porGeneracion': 'Se cobra por generación, sin cuota gratuita.',
  'mod.oPegaOtro': 'O pegá el identificador de otro modelo',
  'mod.loraAdulto': 'LoRA de contenido adulto',
  'mod.loraAdultoQue':
    'Se apila sobre el LoRA de identidad de cada influencer. Hace falta cuando el modelo base no ' +
    'genera desnudez por su cuenta, que es el caso de toda la familia FLUX.',
  'mod.disparadorEjemplo': 'Su palabra disparadora, si tiene — ej. aidmaNSWFunlock',
  'mod.arquitectura': 'Arquitectura',
  'mod.arquitecturaQue':
    'Sobre qué modelos se apila. Apilarlo donde no corresponde no da un resultado peor: no hace ' +
    'nada, y el gasto es el mismo.',
  'mod.cambioDeCara': 'Cambio de cara, en un segundo paso',
  'mod.cambioDeCaraQue':
    'Runware no tiene face swap: lo que hay son modelos que editan por instrucción, a los que se ' +
    'les da la imagen generada más una foto de ella y se les pide el reemplazo. Lo único que decide ' +
    'cuál sirve es si el fabricante le puso filtro — con filtro, una entrada explícita se rechaza o ' +
    'vuelve sin tocar, y se cobra igual.',
  'mod.conQueSeCambia': 'Con qué se cambia la cara',
  'mod.conQueSeCambiaQue':
    'Se usa sólo cuando se genera con el botón «Con cambio de cara». Cuesta aparte de la generación.',
  'mod.sinEditores': 'Sin editores disponibles',
  'mod.porImagen': '{cual} · USD {usd} por imagen',
  'mod.conFiltro': ' · con filtro del fabricante',

  // ── Nueva influencer: lo que faltaba ──
  'nueva.sinServidor': 'No se pudo conectar con el servidor.',
  'nueva.topeNoAlcanza': 'El tope diario que fijaste no alcanza para una influencer completa.',
  'nueva.cuotaNoAlcanza': 'La cuota gratuita de hoy ya no alcanza para una influencer completa.',
  'nueva.conElPerfil': 'Con el perfil ',
  'nueva.cadaInfluencerCuesta': ' cada influencer cuesta ',
  'nueva.yTuTopeEsta': ' y tu tope está en ',
  'nueva.subiloEn': '. Subilo en ',
  'nueva.consumoYAjustes': 'Consumo y ajustes',
  'nueva.oGeneraIgual':
    ' o generá igual: lo que no entre hoy queda en la cola y se completa al reiniciarse el día.',
  'nueva.podesGenerarIgual':
    'Podés generarla igual: lo que no entre hoy queda en la cola diferida y se completa solo ' +
    'cuando la cuota se reinicie.',
  'nueva.imagenesIniciales': 'Imágenes iniciales: ',
  'nueva.unaSolaToma': 'Una sola toma: lo más barato para ver cómo queda antes de pedir una tanda.',
  'nueva.menosImagenes':
    'Menos imágenes consumen menos cuota y permiten crear más influencers hoy.',
  'nueva.estiloVisual': 'Estilo visual',
  'nueva.conModoCeroActivo': ' — con el modo USD 0 activo',
  'nueva.trajeDeBanio':
    'El traje de baño sale sólo donde el entorno lo justifica —playa, pileta— y como mucho en una ' +
    'toma del set.',
  'nueva.necesitaSinFiltroAntes': 'Este nivel necesita un ',
  'nueva.modeloSinFiltro': 'modelo sin filtro del fabricante',
  'nueva.necesitaSinFiltroDespues':
    ': uno con filtro rechaza este registro y cobra el intento igual.',
  'nueva.gpuPropiaAntes': 'Lo vas a generar con ',
  'nueva.tuPropiaGpu': 'tu propia GPU',
  'nueva.gpuPropiaDespues':
    ', que es el caso ideal para este registro: no tiene filtro de nadie y no cuesta dinero, así ' +
    'que el modo USD 0 no lo frena. Lo único que hace falta es que la máquina esté prendida — si ' +
    'no está, las tomas fallan con un error de conexión.',
  'nueva.runwareMedio1': ', que sirve modelos de pesos abiertos sin filtro ajeno y ',
  'nueva.cobraPorImagen': 'cobra por imagen',
  'nueva.runwareMedio2': '. Por eso hace falta tener ',
  'nueva.desactivadoModoCero': 'desactivado el modo USD 0',
  'nueva.runwareFin':
    ' en Consumo: viene activo por defecto, así que si no lo tocaste, la generación se frena ahí.',
  'nueva.sinGastarAntes':
    'Si querés generarlo sin gastar, elegí la GPU propia como modelo de contenido en ',
  'nueva.todaviaNoPodes': 'Con esta cuenta todavía no podés generar este nivel:',
  'nueva.modoCeroActivoEn': ' el modo USD 0 está activo. Se arregla en ',
  'nueva.oEligiendoGpu': ', o eligiendo la GPU propia en ',
  'nueva.rechazaEscenasAntes': 'El proveedor también rechaza parte de las escenas y ',
  'nueva.cadaRechazoSePaga': 'cada rechazo se paga',
  'nueva.rechazaEscenasDespues':
    '. Una toma rechazada ya no arrastra al resto del set: las demás se generan igual y «Completar ' +
    'set» rellena los huecos.',
  'nueva.dondeMasPasa': ' Es el registro donde más pasa.',
  'nueva.desnudezProhibida':
    'Desnudez total o parcial sigue prohibida por las políticas del sistema para lo que sale a las ' +
    'redes, con independencia de lo que el modelo permita. El material explícito se genera en la ' +
    'pestaña de Fanvue de cada influencer, y sólo va ahí.',
  'nueva.plataformas': 'Plataformas',
  'nueva.plataformasQue': 'Se generan las tareas de alta y las variantes de contenido de cada una.',
  'nueva.modoCeroEstaAntes': 'El modo USD 0 está ',
  'nueva.desactivado': 'desactivado',
  'nueva.activo': 'activo',
  'nueva.modoCeroEstaDespues': ' y sólo se cambia desde Ajustes, con confirmación explícita.',
  'nueva.tonoDePiel': 'Tono de piel',
  'nueva.colorDePelo': 'Color de pelo',
  'nueva.texturaDePelo': 'Textura de pelo',
  'nueva.colorDeOjos': 'Color de ojos',
  'nueva.sinPreferenciaAntes': 'Lo que dejes en «sin preferencia» lo elige el modelo',
  'nueva.segunLaUbicacion': ' según la ubicación ({pais})',
  'nueva.alEscribirLaFicha': ' al escribir la ficha',
  'nueva.sinPreferenciaDespues':
    '. Lo que fijes acá manda sobre eso, aunque no sea lo típico del lugar.',

  'nueva.estiloVisualQue':
    'Condiciona todas las imágenes de esta influencer, desde la primera. No se puede cambiar lo ' +
    'ya generado.',

  // ── Manual ──
  'man.titulo': 'Manual',
  'man.bajada': 'Todo lo que hace el panel, opción por opción. Para leer salteado.',
  'man.secComoFunciona': 'Cómo funciona',
  'man.secCredenciales': 'Credenciales',
  'man.secAjustes': 'Consumo y ajustes',
  'man.secCrear': 'Crear una influencer',
  'man.secEstilo': 'El estilo visual',
  'man.secFicha': 'La ficha, pestaña por pestaña',
  'man.secRedes': 'Conectar las redes',
  'man.secContenido': 'Generar y publicar',
  'man.secPantallas': 'Las demás pantallas',
  'man.secCuentas': 'Cuentas y roles',
  'man.secProblemas': 'Cuando algo no sale',

  'man.problemaTitulo': 'El problema que resuelve',
  'man.problemaP1Antes': 'Generar una imagen linda lo hace cualquier modelo. Lo difícil es que ',
  'man.mismaPersona': 'todas sean de la misma persona',
  'man.problemaP1Despues':
    ': pedir «una mujer morena de 25 años» diez veces devuelve diez mujeres distintas.',
  'man.problemaP2Antes':
    'Por eso una influencer no se crea de una sola vez. Son tres etapas con dos pausas donde ' +
    'decidís vos, y todo el contenido posterior se genera ',
  'man.adjuntando': 'adjuntando las imágenes aprobadas',
  'man.problemaP2Despues': ' como referencia. Sin ese anclaje la cara se va a los pocos días.',
  'man.tresEtapas': 'Las tres etapas',
  'man.etapa1': 'Candidatas.',
  'man.etapa1Que':
    ' Cuatro caras posibles a partir del estilo elegido. Elegís una y pasa a ser la cara canónica. ' +
    'Es la única etapa que un modelo sin referencias puede hacer, porque parte de un texto y nada más.',
  'man.etapa2': 'Hoja de identidad.',
  'man.etapa2Antes': ' Tres ángulos —tres cuartos, perfil y plano medio— generados ',
  'man.aPartirDeLaCandidata': 'a partir de la candidata',
  'man.etapa2Despues': ', adjuntándola. Los aprobás o se regeneran.',
  'man.etapa3': 'Contenido.',
  'man.etapa3Que': ' Cada publicación se genera con la hoja adjunta.',
  'man.pausasAProposito':
    'Las pausas son a propósito. Aprobar una cara que no convence sale caro: todo lo que se genere ' +
    'después va a tener esa cara.',

  'man.cadaClaveEsTuya': 'Cada clave es tuya',
  'man.cadaClaveAntes': 'Lo que cargues acá ',
  'man.soloLoVesVos': 'sólo lo ves vos',
  'man.cadaClaveDespues':
    '. El sistema genera con tu cuenta y el gasto es tuyo. Se guardan cifradas y nunca vuelven a ' +
    'mostrarse enteras: hay un botón para revelarlas cuando hace falta, y otro para probar cada ' +
    'una contra su proveedor.',
  'man.lasQuePodesCargar': 'Las que podés cargar',
  'man.cuentaCloudflare': 'Cuenta de Cloudflare',
  'man.imprescindible': 'Imprescindible.',
  'man.cfAntes':
    ' Genera las imágenes. Hacen falta un token de API con permiso de Workers AI —desde ',
  'man.cfEnlace': 'Perfil → Tokens de API',
  'man.cfDespues':
    ', se muestra una sola vez— y el ID de cuenta, que está en la barra lateral. Gratis: 10.000 ' +
    'créditos de IA por día, sin tarjeta.',
  'man.geminiAntes':
    'Escribe los textos: la ficha del personaje, las escenas y los pies de foto. Tiene uso ' +
    'gratuito y sale bastante mejor que el modelo incluido. Se saca en ',
  'man.geminiDespues': '. También genera video.',
  'man.groqQue': 'Alternativa a Gemini para los textos. También tiene uso gratuito.',
  'man.falQue':
    'Se paga por uso. Es lo único que habilita el nivel de piel sensual y el video de mayor ' +
    'calidad: el modelo gratuito de Cloudflare los rechaza.',
  'man.casillaDeCorreo': 'Casilla de correo',
  'man.casillaQue':
    'La dirección base desde la que se derivan los correos de cada influencer, con alias del tipo ' +
    'tucasilla+influencer@…',
  'man.dominioDeCorreo': 'Dominio de correo',
  'man.dominioQue':
    'Alternativa a lo anterior si tenés dominio propio: alias reales en vez de sufijos.',
  'man.appDeMeta': 'App de Meta',
  'man.appDeMetaQue':
    'No aparece en tu pantalla, y es correcto: es una sola para todo el panel, la configura quien ' +
    'administra y ya está puesta. Para publicar en Instagram o Facebook no tenés que cargar nada — ' +
    'sólo conectar tu cuenta desde la ficha de la influencer.',

  'man.frenosDeGasto': 'Los frenos de gasto',
  'man.vieneActivo': 'Viene activo.',
  'man.modoCeroQue':
    ' Ninguna operación que pueda generar un cargo se ejecuta. Al agotarse lo gratuito, el trabajo ' +
    'se difiere al día siguiente en vez de gastar. Nunca se desactiva solo: sólo por decisión humana.',
  'man.topeDiario': 'Tope diario',
  'man.topeDiarioQue':
    'Cuántos créditos de Cloudflare se pueden consumir por día. Viene en 9.500 sobre los 10.000 ' +
    'gratuitos, dejando margen.',
  'man.topeMensual': 'Tope mensual en USD',
  'man.topeMensualQue':
    'El único freno real cuando el modo USD 0 está apagado. Ningún proveedor ofrece un corte de ' +
    'gasto propio.',
  'man.diferidas': 'Operaciones diferidas',
  'man.diferidasQue':
    'Lo que quedó esperando por falta de cuota. Se retoma solo al día siguiente; se ve en Inicio.',
  'man.calidadDeImagen': 'Calidad de imagen',
  'man.economia': 'Economía',
  'man.economiaQue': 'Lo más barato que da resultados usables.',
  'man.equilibrado': 'Equilibrado',
  'man.equilibradoQue': 'El que viene puesto. Es el que conviene salvo que tengas un motivo.',
  'man.premium': 'Premium',
  'man.premiumQue': 'Mejor calidad, más caro.',
  'man.sinRestricciones': 'Sin restricciones',
  'man.sinRestriccionesQue':
    'Dos variantes de Seedream, para registros que los filtros de los otros modelos rechazan. ' +
    'Necesitan fal.',
  'man.avanzado': 'Avanzado: un modelo por etapa.',
  'man.avanzadoAntes':
    ' Permite elegir el modelo de cada una de las tres etapas por separado. La etapa 1 admite ' +
    'cualquiera —parte de un texto—, pero las etapas 2 y 3 ',
  'man.soloAdmitenReferencias': 'sólo admiten modelos que acepten imágenes de referencia',
  'man.avanzadoDespues':
    ', porque adjuntan las anteriores. Vacío significa «el que diga el perfil», que es lo correcto ' +
    'si no tenés una razón concreta.',
  'man.videoYResto': 'Video, almacenamiento y el resto',
  'man.proveedorDeVideo': 'Proveedor de video',
  'man.proveedorDeVideoQue':
    'Automático, Gemini, fal, o LTX en dos versiones. Automático elige según lo disponible y el ' +
    'modo de gasto.',
  'man.almacenamiento': 'Almacenamiento de imágenes',
  'man.almacenamientoQue': 'Dónde se guardan los archivos generados y cuánto ocupan.',
  'man.avisoEnPies': 'Aviso en los pies de foto',
  'man.avisoEnPiesQue':
    'Agrega una línea declarando que el contenido es generado. Apagado por defecto; algunas ' +
    'plataformas lo exigen.',
  'man.consumoPorProveedor': 'Consumo por proveedor',
  'man.consumoPorProveedorQue':
    'Los últimos siete días, desglosado. Sirve para ver qué se está llevando el presupuesto.',
  'man.extensionQue':
    'Genera un token para conectar la extensión de Chrome, que asiste en el alta manual de cuentas.',
  'man.featureFlagsQue':
    'Interruptores de funciones en prueba. Si no sabés qué hace uno, no lo toques.',

  'man.loQueSeCompleta': 'Lo que se completa',
  'man.nombreYPais': 'Nombre y país',
  'man.nombreYPaisQue': 'De dónde es narrativamente. Condiciona escenarios y idioma.',
  'man.edad': 'Edad',
  'man.edadQue': 'Hay un piso y el panel avisa cuando te acercás a él.',
  'man.deQueHabla': 'De qué habla',
  'man.deQueHablaQue': 'Su nicho. Hay sugerencias, y podés escribir uno libre.',
  'man.rasgos': 'Rasgos',
  'man.rasgosQue': 'Cómo es de carácter. Alimenta los textos, no las imágenes.',
  'man.idiomas': 'Idiomas de publicación',
  'man.idiomasQue': 'En qué idiomas se escriben los pies de foto.',
  'man.fichaDelPersonaje': 'Ficha del personaje',
  'man.fichaDelPersonajeQue':
    'La escribe un modelo a partir de lo anterior y es editable. Sin ficha no se puede generar ' +
    'contenido.',
  'man.alAzar': 'Al azar',
  'man.alAzarQue':
    'Completa todo con una combinación coherente. Sirve para arrancar y después ajustar.',

  'man.queDefineCada': 'Qué define cada opción',
  'man.registroQue':
    'Editorial, natural, amateur o mixto. Es lo que más cambia el aspecto: editorial parece ' +
    'producción; amateur, un teléfono.',
  'man.nivelDePielQue':
    'Calle, deportivo, playa, sensual o atrevido. Define cuánto muestra y cómo se compone la toma.',
  'man.escenarioDeVestuario': 'Escenario de vestuario',
  'man.escenarioDeVestuarioQue':
    'Agua, gimnasio, interior o calle. La ropa se deduce de acá: un microbikini en el agua, ropa ' +
    'deportiva en el gimnasio.',
  'man.tipoDeCuerpo': 'Tipo de cuerpo',
  'man.tipoDeCuerpoQue': 'Sin preferencia, atlético, curvilíneo o delgado.',
  'man.tonoDelFeedQue': 'Cálido, frío, neutro o contrastado. La paleta general.',
  'man.escenarios': 'Escenarios',
  'man.escenariosQue': 'Interiores, exteriores o mezcla.',
  'man.sobreAtrevido': 'Sobre «atrevido»:',
  'man.sobreAtrevidoQue':
    ' la mayoría de las tomas salen de espaldas y con ropa mínima, salvo cuando la escena no lo ' +
    'permite —gimnasio, calle—. Al menos una toma de cada tanda muestra la cara, para que el ' +
    'personaje siga siendo reconocible.',
  'man.nivelesNecesitanFal': 'Los niveles sensual y atrevido necesitan una clave de fal cargada.',

  'man.lasNuevePestanias': 'Las nueve pestañas',
  'man.pesResumenQue':
    'Estado, próxima acción humana pendiente, la ficha del personaje y el estilo visual.',
  'man.pesIdentidadQue':
    'Las cuatro candidatas, la frontal elegida y la hoja de identidad. Acá se aprueba o se regenera.',
  'man.pesGaleriaQue': 'Todas las imágenes generadas, por etapa.',
  'man.pesContenidoQue':
    'Las publicaciones: su texto, su estado, su fecha, y los botones de publicar, reescribir el pie ' +
    'o borrar.',
  'man.pesPlataformasQue':
    'Las cuentas conectadas de esta influencer, con el estado de cada conexión.',
  'man.pesTareasQue':
    'El alta manual de cada red: qué crear, con el usuario sugerido, la biografía y las fotos listas.',
  'man.pesWorkflowQue': 'Las corridas de generación, con su avance y sus errores.',
  'man.pesConsumoQue': 'Lo que gastó esta influencer en particular.',
  'man.pesAuditoriaQue': 'Quién hizo qué y cuándo. No se edita.',

  'man.primeroSeCrea': 'Primero se crea, después se conecta',
  'man.primeroSeCreaAntes': 'En ',
  'man.tareas': 'Tareas',
  'man.primeroSeCreaMedio':
    ' está todo lo que hay que crear a mano —la cuenta, la Página, la biografía, la foto de perfil ' +
    'y la de portada— con el texto listo para copiar y las imágenes listas para descargar. Recién ' +
    'después aparece el botón ',
  'man.conectar': 'Conectar',
  'man.primeroSeCreaFin': ', que abre el diálogo de Meta.',
  'man.tokenNoVence':
    'El token de Página no vence, así que se hace una sola vez. Si el diálogo se completa sin ' +
    'marcar algún permiso, el panel lo avisa y hay que volver a conectar.',
  'man.requisitosDeCada': 'Los requisitos de cada plataforma',
  'man.igQue': 'Cuenta profesional vinculada a una Página de Facebook. Publica y lee métricas.',
  'man.fbQue':
    'Publica en Páginas, nunca en perfiles personales. Es un límite de la API, no del panel.',
  'man.fanvueQue': 'Sin integración: no tiene API. El panel prepara el paquete y lo subís a mano.',

  'man.estadosDeUna': 'Los estados de una publicación',
  'man.borrador': 'Borrador.',
  'man.borradorQue': ' Se generó la imagen o el video y su texto.',
  'man.aprobada': 'Aprobada.',
  'man.aprobadaQue': ' La leíste y sirve. Hasta que no lo esté no se publica sola.',
  'man.programada': 'Programada.',
  'man.programadaQue': ' Tiene fecha y hora.',
  'man.publicada': 'Publicada.',
  'man.publicadaQue': ' Salió en al menos una plataforma.',
  'man.publicacionAutomatica': 'La publicación automática',
  'man.automaticaAntes': 'Un proceso revisa periódicamente qué hay vencido y lo publica en ',
  'man.igYFb': 'Instagram y Facebook',
  'man.automaticaDespues':
    '. Las dos son independientes: si algo ya salió en Instagram y todavía no en Facebook, se ' +
    'publica sólo lo que falta. Que figure como publicada no bloquea el botón «Publicar ahora» de ' +
    'las otras.',
  'man.soloAprobadas':
    'Sólo se publican las que estén aprobadas. Los borradores nunca salen solos, aunque tengan la ' +
    'hora vencida.',

  'man.elMenu': 'El menú',
  'man.menuInicioQue':
    'Qué influencers tenés, qué está esperando algo tuyo, la cuota gratuita del día, los errores ' +
    'recientes y lo diferido.',
  'man.menuNueva': 'Nueva influencer',
  'man.menuNuevaQue': 'El alta.',
  'man.menuPlataformasQue':
    'Métricas de todas las cuentas conectadas: un resumen de Instagram y Facebook juntos, y una ' +
    'pestaña por plataforma.',
  'man.menuLeadsQue':
    'Cuentas de Instagram que seguís como referencia —con sus seguidores y su evolución— y enlaces ' +
    'de Facebook para abrir.',
  'man.menuConsumoQue': 'Todo lo de gasto y ajustes.',
  'man.menuServicios': 'Servicios',
  'man.menuServiciosQue': 'Qué proveedor está activo para cada cosa y si responde.',
  'man.menuCredencialesQue': 'Tus claves.',
  'man.menuGuia': 'Guía',
  'man.menuGuiaQue': 'La puesta en marcha.',
  'man.menuLaboratorio': 'Laboratorio',
  'man.menuLaboratorioQue':
    'Sólo si está habilitado, y sólo para quien administra: comparar modelos de imagen, probar ' +
    'checkpoints propios en fal y generar video con Gemini o Wan. Queda sujeto al modo USD 0 como ' +
    'todo lo demás.',
  'man.ademasEstadisticasAntes': 'Además, cada influencer tiene su pantalla de ',
  'man.estadisticas': 'Estadísticas',
  'man.ademasEstadisticasDespues': ', con la evolución de seguidores, alcance e interacciones.',

  'man.quienPuedeQue': 'Quién puede qué',
  'man.rolAdminQue':
    'Todo, más crear cuentas y ver el laboratorio. Es quien configura la app de Meta para el resto.',
  'man.rolOperadorQue': 'Crea y opera sus propias influencers. Es el rol normal.',
  'man.soloLectura': 'Sólo lectura',
  'man.rolLecturaQue': 'Ve todo lo suyo pero no modifica nada.',
  'man.cadaCuentaVacia': 'Cada cuenta arranca vacía y lo que genera es suyo.',
  'man.cadaCuentaVaciaQue':
    ' Las influencers, las credenciales, el contenido y el gasto pertenecen a quien los creó: ' +
    'nadie ve los de otro, ni siquiera por enlace directo.',

  'man.loQueMasAparece': 'Lo que más aparece',
  'man.pCuota': '«Cuota diaria agotada» o gasto bloqueado',
  'man.pCuotaQue':
    'Se agotó lo gratuito del día y el modo USD 0 está activo. El trabajo no se pierde: se difiere ' +
    'al día siguiente. Si querés que siga, hay que desactivarlo y poner topes.',
  'man.pPermiso': '«Conectada sin permiso de publicar»',
  'man.pPermisoQue':
    'La autorización se completó pero faltó marcar algún permiso en el diálogo de Meta. Se arregla ' +
    'volviendo a conectar y aceptando todo.',
  'man.pMuro': 'La publicación de Facebook no aparece en el muro',
  'man.pMuroQue':
    'La app de Meta todavía no está publicada del lado de Meta. La entrada existe y es pública, ' +
    'pero sólo la ven quienes tienen un rol en la app. Lo resuelve quien administra el panel; no ' +
    'es algo de tu cuenta.',
  'man.pDistintas': 'Las imágenes salen distintas entre sí',
  'man.pDistintasQue':
    'Suele ser una hoja de identidad aprobada a las apuradas. Conviene regenerarla: todo lo que ' +
    'venga después se ancla en ella.',
  'man.pSinBoton': 'No aparece el botón de conectar',
  'man.pSinBotonQue':
    'Falta la tarea de alta previa: primero se crea la cuenta o la Página, después se conecta.',
  'man.pSinContenido': 'No se puede generar contenido',
  'man.pSinContenidoQue':
    'Falta la ficha del personaje, o falta aprobar la hoja de identidad. Las dos son requisito.',
  'man.pSensual': 'El nivel sensual no está disponible',
  'man.pSensualQue':
    'Necesita una clave de fal. El modelo gratuito de Cloudflare rechaza ese registro.',
  'man.pModeloFalta': 'Un modelo no aparece en las etapas 2 o 3',
  'man.pModeloFaltaQue':
    'Porque no acepta imágenes de referencia, y esas etapas las adjuntan. Sin referencia, cada ' +
    'imagen sería otra persona.',
  'man.antesDeGastar': 'Antes de gastar',
  'man.videoCuesta': 'Un video cuesta más que una influencer entera de imágenes.',
  'man.videoCuestaQue': ' No es una imagen más cara: es otro orden de magnitud.',
  'man.regenerarCuesta':
    'Regenerar candidatas u hojas de identidad cuesta cada vez. Es barato comparado con publicar ' +
    'meses con una cara que no convence, pero no es gratis.',
} as const;

export type Clave = keyof typeof ES;

/**
 * El inglés, tipado contra las claves del español.
 *
 * Si falta una, TypeScript lo dice acá y no un usuario en Londres viendo «Rasgos distintivos».
 */
const EN: Record<Clave, string> = {
  'comun.cancelar': 'Cancel',
  'comun.guardar': 'Save',
  'comun.guardando': 'Saving…',
  'comun.volver': 'Back',
  'comun.editar': 'Edit',
  'comun.opcional': 'Optional',
  'comun.idioma': 'Language',
  'comun.cerrarSesion': 'Sign out',
  'comun.cerrar': 'Close',
  'comun.salir': 'Sign out',
  'comun.reintentar': 'Retry',

  'comun.cargando': 'Loading…',
  'comun.codigo': 'Code: {code}',
  'comun.temaClaro': 'Switch to light theme',
  'comun.temaOscuro': 'Switch to dark theme',

  'estado.DRAFT': 'Draft',
  'estado.GENERATING_IDENTITY': 'Writing identity',
  'estado.GENERATING_MEDIA': 'Generating images',
  'estado.AWAITING_REVIEW': 'Waiting for review',
  'estado.WAITING_FOR_ACCOUNT_CREATION': 'Account not created yet',
  'estado.WAITING_FOR_EMAIL_VERIFICATION': 'Email not verified yet',
  'estado.WAITING_FOR_KYC': 'KYC pending',
  'estado.WAITING_FOR_OAUTH': 'OAuth not connected yet',
  'estado.READY': 'Ready',
  'estado.ACTIVE': 'Active',
  'estado.PAUSED': 'Paused',
  'estado.QUOTA_EXHAUSTED': 'Quota exhausted',
  'estado.BLOCKED': 'Not available yet',
  'estado.ERROR': 'Error',

  'tarea.CREATE_ACCOUNT': 'Create the account',
  'tarea.CREATE_ACCOUNT.facebook': 'Create the Page',
  'tarea.VERIFY_EMAIL': 'Verify the email',
  'tarea.VERIFY_PHONE': 'Verify the phone',
  'tarea.COMPLETE_KYC': 'Complete KYC',
  'tarea.ACCEPT_TERMS': 'Accept the terms',
  'tarea.CONVERT_TO_PROFESSIONAL': 'Convert to a professional account',
  'tarea.CONNECT_OAUTH': 'Connect via OAuth',
  'tarea.APPROVE_PROFILE': 'Approve the profile',
  'tarea.CREATE_DEVELOPER_APP': 'Create the developer app',
  'tarea.CREATE_EMAIL_ACCOUNT': 'Create the email account',
  'tarea.COMPLETE_WITHDRAWAL': 'Complete a withdrawal',
  'tarea.SETUP_AUTOREPLY': 'Set up automated replies',

  'nav.principal': 'Main',
  'nav.inicio': 'Home',
  'nav.nueva': 'New influencer',
  'nav.nuevaCorta': 'New',
  'nav.plataformas': 'Platforms',
  'nav.referencias': 'Leads',
  'nav.consumo': 'Usage',
  'nav.paginas': 'Pages',
  'nav.modelos': 'Models',
  'nav.servicios': 'Services',
  'nav.credenciales': 'Credentials',
  'nav.guia': 'Guide',
  'nav.mas': 'More',
  'nav.cuentas': 'Accounts',
  'nav.laboratorio': 'Lab',
  'nav.menu': 'Menu',

  'app.verificandoSesion': 'Checking the session…',
  'app.paginaInexistente': 'No such page.',

  'login.titulo': 'Virtual Creator OS',
  'login.subtitulo': 'Sign in to manage your influencers.',
  'login.usuario': 'Username',
  'login.contrasena': 'Password',
  'login.entrar': 'Sign in',
  'login.entrando': 'Signing in…',
  'login.panelAdmin': 'Admin panel',
  'login.correo': 'Email',
  'login.correoVacio': 'leave empty to sign in as administrator',
  'login.correoPlaceholder': 'you@email.com',
  'login.verificando': 'Checking…',
  'login.detrasDeAccess': 'Behind Cloudflare Access this screen never shows.',
  'login.noSePudoConectar': 'Could not connect.',

  'comun.recargar': 'Reload the page',
  'error.seccionFallo': 'This section failed',
  'error.seccionDetalle':
    'The rest of the panel still works: you can navigate elsewhere from the menu. Nothing was ' +
    'lost — this is a failure drawing the screen, not saving data.',

  'servicios.cargando': 'Loading services…',
  'servicios.titulo': 'External services',
  'servicios.subtitulo': 'Everything this system uses outside, with its console and real status.',
  'servicios.activos': 'Active',
  'servicios.sinConfigurar': 'Not configured',
  'servicios.sumanCapacidad': 'Optional, but they add capacity',
  'servicios.fase3': 'Phase 3',
  'servicios.previstos': 'Planned for later',
  'servicios.estado.configured': 'Configured',
  'servicios.estado.missing': 'Not configured',
  'servicios.estado.phase_3': 'Phase 3',
  'servicios.estado.built_in': 'Built in',
  'servicios.costo': 'Cost: ',
  'servicios.comoSeCargan': 'How keys are loaded',
  'servicios.dondeVan':
    'In development they live in apps/worker/.dev.vars, outside the repository. In production they ' +
    'are loaded as secrets:',
  'servicios.nuncaMuestra': 'This panel never shows the value of a key, only whether it is set.',

  'corrida.iniciando': 'Starting the workflow…',
  'corrida.titulo': 'Creating influencer',
  'corrida.cuotaAgotada': 'The daily quota ran out. The work resumes on its own when it resets.',
  'corrida.reinicio': 'Resets: {cuando}',
  'corrida.noSeReintenta':
    'This error is not retried automatically: retrying would not change the outcome.',
  'corrida.pasos': 'Steps · {hechos} of {total}',
  'corrida.consumoEstimado': 'Estimated usage: {n} credits',
  'corrida.verInfluencer': 'Open the influencer',

  'nueva.empecemos': 'Let us start',
  'nueva.deQueHabla': 'What is she about?',
  'nueva.deQueHablaAyuda': 'Pick one or write your own. Leave it empty and the AI picks.',
  'nueva.masOpciones': 'More options',
  'nueva.masOpcionesDetalle': 'features, style, platforms — every one has a default that works',
  'nueva.rasgosPocos': 'Browse the {n} features',
  'nueva.oBien': 'or',
  'nueva.listaVacia': 'The AI decides',

  'nueva.opcionesCambiaron':
    'You changed something above after this profile was written, so the profile is still the ' +
    'previous one.',
  'nueva.volverAEscribir': 'Write it again',
  'nueva.antesDeEscribir': 'Adjust whatever you like, then write the profile.',

  'nueva.titulo': 'Create a new influencer',
  'nueva.subtitulo':
    'Pick a topic and an age, and the AI writes the rest. You can read it and fix it before a ' +
    'single image is generated.',
  'nueva.paso2': 'Her profile',

  'nueva.queCrear': 'What to create',
  'nueva.kind.human': 'Influencer',
  'nueva.kind.humanDetalle': 'A virtual person. You pick niche, age, features and style.',
  'nueva.kind.character': 'Non-human character',
  'nueva.kind.characterDetalle':
    'A creature, a being, a shape. Nothing is asked: you press and it comes out.',
  'nueva.kind.characterAviso':
    'The chain is the same: candidates, you pick the reference, identity sheet and content. What ' +
    'will not work is the consistency label: the meter looks for human faces and finds none in a ' +
    'character, so the shots stay «unmeasured».',

  'nueva.nicho': 'Niche',
  'nueva.nichoAyuda':
    'Pick from the list or write your own. Leave it empty and the model picks, and it tends to ' +
    'invent topics nobody follows.',
  'nueva.nichoLibre': 'Add your own',
  'nueva.nichoPlaceholder': 'Type it and press Enter',
  'nueva.nichoVerMas': 'Show every niche',
  'nueva.nichoVerMenos': 'Show fewer',

  'nueva.edad': 'Age',
  'nueva.edadAyuda':
    'Used as given: the profile and the images will look like someone of this age.',
  'nueva.edadAnios': '{n} years old',
  'nueva.edadCercaDelPiso':
    'Near the minimum the image provider rejects more often —it moderates on appearance, not on ' +
    'the number in the profile— and every rejection is billed.',

  'nueva.pais': 'Narrative country',
  'nueva.paisPlaceholder': 'Buenos Aires, Argentina',
  'nueva.idiomasDePublicacion': 'Languages she posts in',

  'nueva.rasgos': 'Distinguishing features',
  'nueva.rasgosAyuda':
    'What makes her recognisable at a glance. Asked for nothing, the model always returns a mole ' +
    'and some freckles: the average of what it learned. You can pick several.',
  'nueva.rasgosElegidos': '{n} selected',
  'nueva.rasgosLimpiar': 'Clear all',

  'nueva.fichaTitulo': 'Her profile',
  'nueva.fichaVacia':
    'No profile yet. Press «Write the profile» and the model drafts it from the choices above.',
  'nueva.escribirFicha': 'Write the profile',
  'nueva.reescribirFicha': 'Write another',
  'nueva.escribiendo': 'Writing…',
  'nueva.alAzar': 'Surprise me',
  'nueva.alAzarAyuda': 'Fills the choices above at random and writes the profile.',
  'nueva.fichaEditable': 'All of this is editable. What you leave here is what gets generated.',
  'nueva.fichaViolaciones': 'As it stands, creation will be rejected. Fix this before continuing:',
  'nueva.fichaAdvertencias': 'This does not block, but it is worth a look:',

  'ficha.publicName': 'Public name',
  'ficha.fictionalAge': 'Age',
  'ficha.birthday': 'Birthday',
  'ficha.countryNarrative': 'City and country',
  'ficha.niche': 'Niches',
  'ficha.languages': 'Languages',
  'ficha.personalityTraits': 'Personality traits',
  'ficha.backstory': 'Backstory',
  'ficha.values': 'Values',
  'ficha.interests': 'Interests',
  'ficha.toneOfVoice': 'Tone of voice',
  'ficha.catchphrases': 'Catchphrases',
  'ficha.prohibitedClaims': 'Prohibited claims',
  'ficha.prohibitedClaimsAyuda':
    'Topics she will never speak about with authority. They are honoured when writing every post.',

  'ficha.visualIdentity': 'Visual identity',
  'ficha.skinTone': 'Skin tone',
  'ficha.faceShape': 'Face shape',
  'ficha.eyeColor': 'Eye colour',
  'ficha.hairColor': 'Hair colour',
  'ficha.hairStyle': 'Hair style',
  'ficha.bodyDescription': 'Build',
  'ficha.distinguishingFeatures': 'Distinguishing features',
  'ficha.defaultWardrobe': 'Usual wardrobe',
  'ficha.colorPalette': 'Colour palette',
  'ficha.photographyStyle': 'Photography style',

  'ficha.paraElGenerador': 'Instructions for the generator',
  'ficha.paraElGeneradorDetalle': 'in English · only if you want to tune the prompt',
  'ficha.paraElGeneradorPorQue':
    'These four fields do not describe the influencer: they tell the image model what to draw. ' +
    'They are in English because that is the language it works in, and translating them makes ' +
    'the result measurably worse. If you are not tuning the prompt, you do not need to touch them.',

  'ficha.basePrompt': 'Physical description',
  'ficha.bodyPrompt': 'Full-body only',
  'ficha.bodyPromptAyuda':
    'What does not fit in a portrait: prostheses, a wheelchair, arm tattoos. It lives apart ' +
    'because the physical description is repeated in close-ups too, and there a feature that is ' +
    'out of frame is not dropped — the generator relocates it.',
  'ficha.negativePrompt': 'What to avoid',
  'ficha.listaSeparada': 'One per line',

  'nueva.generar': 'Create influencer',
  'nueva.generando': 'Creating…',
  'nueva.necesitaFicha': 'Write the profile before creating.',
  'panel.titulo': 'Dashboard',
  'panel.cargando': 'Loading the dashboard…',
  'panel.faltaConfig': 'Required configuration is missing',
  'panel.modoCero': 'USD 0 mode',
  'panel.perfil': 'profile',
  'panel.modoGasto': 'Spending enabled · USD {monto} budget',
  'panel.generarNueva': 'Create new influencer',

  'panel.activas': 'Active influencers',
  'panel.esperandoVerificacion': 'Awaiting verification',
  'panel.tareasPendientes': 'Pending tasks',
  'panel.requierenAccion': 'Need a person',
  'panel.nadaPendiente': 'Nothing pending',
  'panel.programadas': 'Scheduled posts',
  'panel.proxima': 'Next {cuando}',
  'panel.ninguna': 'None',

  'panel.trabadaUna': '{n} post has a time set but is still a draft',
  'panel.trabadasVarias': '{n} posts have a time set but are still drafts',
  'panel.vencidaUna': ', and one is already overdue',
  'panel.vencidasVarias': ', and {n} are already overdue',
  'panel.trabadasPorQue':
    'The cron only publishes what has been approved, so they will sit there until you approve ' +
    'them from each influencer’s Content tab.',

  'panel.borradas': 'Deleted',
  'panel.borradasDetalle':
    'They are still stored with their images and their profile. Restoring one brings it back ' +
    'exactly as it was — unless the images were deleted too, which cannot be undone.',
  'panel.borradaEl': 'Deleted {cuando}',
  'panel.recuperar': 'Restore',

  'panel.cuotaHoy': 'Today’s free quota',
  'panel.cuotaAgotada': 'Daily quota exhausted',
  'panel.seReinicia': 'Resets {cuando} ({fecha})',
  'panel.cuotaAgotadaDetalle':
    'Cloudflare stopped accepting requests. The ledger had estimated {usados} of {limite} ' +
    'credits, but the real billing was higher: a rejected generation consumes quota all the ' +
    'same, and the published rates do not cover the whole cost.',
  'panel.sinTope': '{usados} credits today, no daily cap',
  'panel.contadorSeReinicia': 'The counter resets {cuando}',
  'panel.creditosEstimados': '{usados} of {limite} estimated credits',

  'panel.costo':
    'On the {perfil} profile each image costs {porImagen} credits, and a complete influencer ' +
    'costs {porInfluencer}.',
  'panel.noEntraNinguna':
    'There is no room for another one today. Any run you start goes to the deferred queue and ' +
    'begins on its own when the quota resets.',
  'panel.entraUna': 'Room for {n} more influencer today.',
  'panel.entranVarias': 'Room for {n} more influencers today.',
  'panel.desglose':
    'Breakdown: {frontales} front shots · {hoja} identity sheet · {set} final set · {texto} text',

  'panel.modelosDePago': 'Paid models',
  'panel.creditosDe': '· {usados} of {limite} credits',
  'panel.bolsaAparte':
    'A separate purse: what is spent here does not consume the Cloudflare free quota.',

  'panel.erroresRecientes': 'Recent errors',
  'panel.sinErrores': 'No errors recorded.',
  'panel.diferidas': 'Deferred operations',
  'panel.nadaEnEspera': 'Nothing waiting.',
  'panel.proveedoresTexto': 'Text providers',
  'panel.disponible': 'available',
  'panel.sinConfigurar': 'not configured',

  'panel.influencers': 'Influencers',
  'panel.mostrarFallidaUna': 'Show {n} failed or incomplete',
  'panel.mostrarFallidas': 'Show {n} failed or incomplete',
  'panel.ocultarFallidaUna': 'Hide {n} failed or incomplete',
  'panel.ocultarFallidas': 'Hide {n} failed or incomplete',
  'panel.sinInfluencers': 'No influencers yet',
  'panel.ningunaCompleta': 'No complete influencers',
  'panel.pulsaGenerar': 'Press “Create new influencer” to make the first one.',
  'panel.todasIncompletas':
    'All {n} of them failed or were left half-finished. Show them with the link above.',
  'panel.seguidores': 'followers',
  'panel.sinNicho': 'No niche',
  'panel.abrirPerfil': 'Open @{usuario} on {plataforma}',
  'panel.tareaUna': '{n} task',
  'panel.tareas': '{n} tasks',
  'tiempo.nunca': 'never',
  'tiempo.recien': 'just now',
  'tiempo.haceMin': '{n} min ago',
  'tiempo.haceH': '{n} h ago',
  'tiempo.ayer': 'yesterday',
  'tiempo.haceDias': '{n} days ago',
  'tiempo.haceUnMes': 'a month ago',
  'tiempo.haceMeses': '{n} months ago',
  'tiempo.yaDisponible': 'available now',
  'tiempo.enHM': 'in {h} h {m} min',
  'tiempo.enM': 'in {m} min',

  'comun.copiar': 'Copy',
  'comun.cuotaUsada': 'Quota used: {n} per cent',
  'comun.cerrarAviso': 'Dismiss',
  'correo.soloPublicidad': 'Only marketing arrived. What is expected here is a confirmation link.',
  'correo.publicidad': 'Marketing · {n}',
  'correo.publicidadDetalle': 'platform campaigns, not needed for any step',
  'correo.sinAsunto': '(no subject)',
  'correo.ocultarMensaje': 'Hide the message',
  'correo.verMensaje': 'Read the full message',
  'correo.descartar': 'Discard',

  'stats.leyendo': 'Reading metrics…',
  'stats.sinMetricas': 'No metrics yet.',
  'stats.ratio': 'Followers per account followed',
  'stats.ultimos7': 'Last 7 days',
  'stats.evolucion': 'How it is moving',
  'stats.sinMediciones':
    'No measurement has been stored yet. One is taken per day, so the first chart appears in a ' +
    'couple of days.',
  'stats.unaMedicion':
    'There is only one measurement. Two are enough to draw the line: come back tomorrow.',
  'stats.referencias': 'Accounts you follow · {n}',
  'stats.agregalas': 'Add them here.',

  'paginas.titulo': 'Public pages',
  'paginas.bajada': 'Who lands on {dominio}, and where they go from there.',
  'paginas.abiertas': 'Page views',
  'paginas.distintos': 'Unique, per day',
  'paginas.clics': 'Clicks to the networks',
  'paginas.nadieEntro': 'Nobody has arrived yet',
  'paginas.ningunaPublicada':
    'No influencer has her page published. It is created from her Tasks tab.',
  'paginas.nadieLasAbrio':
    'The pages are published but nobody opened them in this period. The address goes in each ' +
    'network’s bio.',
  'paginas.porDia': 'Per day',
  'paginas.todasLasInfluencers': 'All influencers',
  'paginas.experimento': 'A/B/C experiment',
  'paginas.experimentoQue':
    'Which destination is worth sending people to. What gets compared is the share who ' +
    'clicked, not the clicks: a branch with more visits has more clicks without being better.',
  'paginas.ramaA': 'A · Fanvue only',
  'paginas.ramaB': 'B · Telegram only',
  'paginas.ramaC': 'C · both',
  'paginas.rama-': 'Before the experiment',
  'paginas.ramaCuenta': '{visitas} visits · {clics} clicks',
  'paginas.porInfluencer': 'Per influencer',
  'paginas.deDondeVienen': 'Where they come from',
  'paginas.directo': 'Direct or no referrer',
  'paginas.sinDatos': 'No data yet.',
  'paginas.desdeQuePais': 'From which country',
  'paginas.sinDeterminar': 'Undetermined',
  'paginas.adondeVan': 'Where they go',
  'paginas.nadieSiguio': 'Nobody has followed a link yet.',
  'stats.volverPlataformas': '← Platforms',
  'stats.sinMetricasTitulo': 'No metrics yet.',
  'stats.sinMetricasDetalle':
    'Either no account is connected, or they are so new that Meta has nothing to measure. Both ' +
    'fix themselves: connect them under Platforms and publish.',
  'stats.seguidores': 'Followers',
  'stats.seguidos': 'Following',
  'stats.publicaciones': 'Posts',
  'stats.ratioTitulo': 'Ratio',
  'stats.alcance': 'Reach',
  'stats.vistas': 'Views',
  'stats.interacciones': 'Interactions',
  'stats.cuentasAlcanzadas': 'Accounts reached',
  'stats.totalesSuman':
    'The totals add up Instagram and Facebook. Facebook reports fewer fields than Instagram —it ' +
    'does not say how many accounts a Page follows, nor its reach— so where you see “—” the ' +
    'figure does not exist; it is not zero.',
  'stats.pasadoNoVuelve':
    'The past cannot be recovered —Meta does not provide it— so the series starts on the day ' +
    'measuring began.',
  'stats.lineasSuman':
    'The lines add up both platforms. A day where only one account could be measured shows only ' +
    'that one: hence the steps.',
  'stats.sinReferencias': 'You have not added any reference account yet.',
  'stats.deDondeSon':
    'Where the accounts you watch are from. Follower overlap cannot be computed: Instagram does ' +
    'not expose any account’s follower list, not even your own.',
  'stats.seg': 'flw.',

  'paginas.dias': '{n} days',
  'paginas.visitas': 'Visits',
  'paginas.visitantes': 'Visitors',
  'paginas.filaDia': '{visitas} · {personas} people · {clics} clicks',
  'paginas.filaInfluencer': '{visitas} visits · {personas} people · {clics} clicks',
  'paginas.queSeGuarda':
    'No IP address, user agent or cookie is stored. “Visitors” comes from an irreversible ' +
    'fingerprint that changes every day, so it tells two of today’s visits apart and cannot ' +
    'follow anyone across days. The country is set by Cloudflare at the edge, and only the ' +
    'domain of the referrer is kept. Everything is deleted after 90 days.',

  'lab.titulo': 'Lab',
  'lab.bajada':
    'Probes against providers. {gastan}, and they do not go through the spending caps: they are measurements, not production.',
  'lab.gastan': 'They spend real money',
  'lab.comparar': 'Lab · compare image models',
  'lab.mandaAntes': 'Sends ',
  'lab.mandaFuerte': 'the same prompt, the same identity references and the same seed',
  'lab.mandaDespues':
    ' to several models. The model is the only variable, so any difference you see is its own. ' +
    'Comparing them one at a time in separate runs proves nothing: the scene changes, the seed ' +
    'changes, and so does the memory of whoever is looking.',
  'lab.cuestaAntes':
    'It costs one image per model, four to six cents each — the Cloudflare ones fall inside the ' +
    'free quota. The images are stored but ',
  'lab.noEnGaleria': 'do not show up in the gallery',
  'lab.cuestaDespues': ': they are measurements, not content anyone chose.',
  'lab.influencer': 'Influencer',
  'lab.elegiUna': 'Pick one…',
  'lab.pegarOtro': 'runware/civitai:257749@290640 (paste any other)',
  'lab.agregar': 'Add',
  'lab.generando': 'Generating…',
  'lab.compararBoton': 'Compare',

  'cuentas.titulo': 'Accounts',
  'cuentas.bajada':
    'Each account sees only its own influencers and uses only its own credentials. They share the ' +
    'panel, not the balance.',
  'cuentas.cargando': 'Loading accounts…',
  'cuentas.crear': 'Create account',
  'cuentas.nueva': 'New account',
  'cuentas.nombre': 'Name',
  'cuentas.nombreEjemplo': 'First and last name',
  'cuentas.password': 'Password · at least 12 characters',
  'cuentas.passwordEjemplo': 'the one you will hand over',
  'cuentas.generarTitulo': 'Generates a strong, readable password',
  'cuentas.generar': 'Generate',
  'cuentas.copiarTitulo': 'Copy to clipboard',
  'cuentas.seGuardaCifrada':
    'It is stored encrypted and cannot be shown again: copy it before creating the account.',
  'cuentas.creando': 'Creating…',
  'cuentas.crearBoton': 'Create',
  'cuentas.desde': '{correo} · since {cuando}',
  'cuentas.sinGuia': ' · has not finished the walkthrough',
  'cuentas.confirmarPassword':
    'New password for {quien}:\n\n{clave}\n\nCopy it now: it cannot be shown again. Confirm the ' +
    'change?',
  'cuentas.passwordCambiada': 'Password changed, and copied to the clipboard.',
  'cuentas.nuevaPassword': 'new password',
  'cuentas.confirmarQuitar': 'Remove {quien}’s account?',
  'cuentas.quitar': 'remove',
  'cuentas.correoEsIdAntes':
    'The email address is only the identifier used to sign in: nothing is sent to it and it is ' +
    'not verified. That is why ',
  'cuentas.sinRecuperacion': 'there is no password recovery',
  'cuentas.correoEsIdDespues':
    ' — if someone forgets theirs, use “new password”, which keeps their account and everything ' +
    'they did.',
  'cuentas.quitarNoBorraAntes':
    'Removing an account does not delete its influencers or images: they are left with no owner ' +
    'who can sign in, so they stop being visible. And creating it again with the same email ',
  'cuentas.noLasRecupera': 'does not bring them back',
  'cuentas.quitarNoBorraDespues':
    ': the identifier is new. That is why changing the password beats deleting and recreating.',

  'rol.operator': 'Operator',
  'rol.operatorQue': 'Creates and runs their own influencers. Does not see the lab.',
  'rol.admin': 'Administrator',
  'rol.adminQue': 'All of the above, plus the lab and these accounts.',
  'rol.viewer': 'Read-only',
  'rol.viewerQue': 'Looks, nothing more. Meant for someone reviewing without touching.',

  'plat.titulo': 'Platforms',
  'plat.bajada': 'Every influencer side by side, to see which one is doing better.',
  'plat.resumen': 'Summary',
  'plat.medirAhora': 'Measure now',
  'plat.midiendo': 'Measuring…',
  'plat.medidas': '{n} accounts measured.',
  'plat.noSePudoMedir': 'Could not read the metrics.',
  'plat.alcance': 'Reach',
  'plat.piezas': 'Pieces on the accounts',
  'plat.publicadas': 'Published',
  'plat.pendientes': 'Waiting to publish',
  'plat.seguidores': 'Followers',
  'plat.seguidos': 'Following',
  'plat.sumaDeCuentaUna': 'across {n} connected account',
  'plat.sumaDeCuentas': 'across {n} connected accounts',
  'plat.sinMetricas':
    '{cuantas} of {total} returned no Instagram metrics. Their follower and reach columns stay ' +
    'empty —in the summary too, which adds up only what answered—; published and pending come ' +
    'from our own database and remain exact.',
  'plat.comparativa': 'Side by side',
  'plat.ordenarPor': 'Sort by',
  'plat.confirmarQuitar': 'Remove {quien} from the comparison?',
  'plat.quitar': 'remove',
  'plat.externa': 'Not generated by this system: connected in order to measure it',
  'plat.cuenta': 'Account',
  'plat.laPagina': 'the Page',
  'plat.conectarOAuth': 'Connect with OAuth',
  'plat.ratio': 'Ratio',
  'plat.ratioTitulo': 'Followers per account followed',
  'plat.enFacebook': 'On Facebook',
  'plat.enInstagram': 'On Instagram',
  'plat.enSusCuentas': 'On their accounts',
  'plat.notaRatio':
    ' is followers per account followed: it tells the one that grew because people looked for ' +
    'her apart from the one that grew by following back. Above 1× it turns green.',
  'plat.notaFuentes':
    ' are returned by Meta at the moment this screen opens. Published and pending come from our ' +
    'own database: they are exact and exist even when the account is not connected.',
  'plat.agregarExterna': 'Add an outside influencer',
  'plat.externaQueAntes':
    'An account that already exists and that this system does not generate. It adds ',
  'plat.externaSoloFila': 'a row here and nothing else',
  'plat.externaQueDespues':
    ' —it does not appear in the influencer list and has no gallery, content or spending— and ' +
    'from that row you connect it with “Connect with OAuth”. Instagram only reports followers ' +
    'and reach for accounts whose owner authorises access, so it has to be one you administer. ' +
    'To follow somebody else’s account there is References.',
  'plat.nombrePublico': 'Public name',
  'plat.usuarioIg': 'Instagram @handle (optional)',
  'plat.agregando': 'Adding…',
  'plat.agregar': 'Add',

  'tab.resumen': 'Summary',
  'tab.identidad': 'Identity',
  'tab.galeria': 'Gallery',
  'tab.contenido': 'Content',
  'tab.lora': 'LoRA',
  'tab.plataformas': 'Platforms',
  'tab.estadisticas': 'Statistics',
  'nav.estadisticas': 'Statistics',
  'nav.herramientas': 'Tools',
  'tab.descargadores': 'Downloaders',
  'ventas.bajada':
    'Every paid photo someone unlocked on Telegram. The total says whether the channel works; ' +
    'the list says which photo made it work.',
  'ventas.porInfluencer': 'By influencer',
  'ventas.cuantasQue': 'photos unlocked',
  'ventas.compradoresQue': 'distinct people',
  'ventas.filaInfluencer': '{ventas} sales - {compradores} people',
  'herr.titulo': 'Downloaders',
  'herr.bajada':
    'Links to outside tools you use by hand. These are not leads: nothing is measured here, it is ' +
    'a drawer so you do not have to look for them again.',
  'herr.cargando': 'Fetching your tools...',
  'herr.agregar': 'Add one',
  'herr.agregarBoton': 'Add',
  'herr.guardando': 'Saving...',
  'herr.agregada': 'Saved.',
  'herr.nombre': 'Name',
  'herr.nombreEjemplo': 'Instagram downloader',
  'herr.url': 'Address',
  'herr.nota': 'Note',
  'herr.notaEjemplo': 'what you use it for',
  'herr.listaTitulo': 'Your tools',
  'herr.quitar': 'Remove',
  'herr.ninguna': 'Nothing saved yet',
  'herr.ningunaQue':
    'Paste the address of the one you use above and it stays here. They open in another tab; the ' +
    'system neither runs them nor sends them anything.',
  'tab.paginas': 'Pages',
  'tab.ventas': 'Sales',
  'ventas.contando': 'Counting sales…',
  'ventas.ninguna': 'Nothing sold yet',
  'ventas.ningunaQue':
    'Every purchase of a paid photo on Telegram shows up here: which one, who and when. If you ' +
    'published for Stars and nothing appears, check the sales notifications in Credentials → Telegram.',
  'ventas.cuantas': 'Sales',
  'ventas.enStars': 'Collected',
  'ventas.compradores': 'Buyers',
  'ventas.listaTitulo': 'One by one',
  'ventas.compradaPor': 'Bought by {quien} · {fecha}',
  'ventas.sinPublicacion': 'That post no longer exists',
  'tab.correo': 'Mail',
  'tab.tareas': 'Tasks',
  'tab.workflow': 'Workflow',
  'tab.consumo': 'Usage',
  'tab.auditoria': 'Audit',
  'tab.secciones': 'Sections',

  'ficha.cargando': 'Loading the influencer…',
  'ficha.edadFicticia': ' · {n} years old (fictional)',
  'ficha.reanudar': 'Resume',
  'ficha.pausar': 'Pause',
  'ficha.borrar': 'Delete',
  'ficha.borrarA': 'Delete {quien}',
  'ficha.borrarQueAntes': 'She disappears from the panel and from every list. ',
  'ficha.borrarSePuedeRecuperar': 'It can be undone',
  'ficha.borrarQueDespues':
    ': the images, the profile and the posts are kept, and she comes back from Home → “Deleted”. ' +
    'What was published on Instagram is left untouched.',
  'ficha.escribiParaConfirmarAntes': 'Type ',
  'ficha.escribiParaConfirmarDespues': ' to confirm',
  'ficha.recuperarEspacio': 'Do you need to reclaim storage space?',
  'ficha.borrarTambienImagenes': 'Delete the images too',
  'ficha.noSePuedeDeshacer': 'cannot be undone',
  'ficha.purgaQueDespues': ': she could still be restored, but she would come back with no photos.',

  'ficha.referenciaCanonica': 'Canonical reference',
  'ficha.referenciaVisualDe': 'Visual reference for {quien}',
  'ficha.seAdjunta': 'Attached as a reference image to every later generation',
  'ficha.sinReferencia': 'No reference chosen',
  'ficha.sinReferenciaQue':
    'Open the Gallery tab and pick which of the candidates becomes the canonical reference.',
  'ficha.estado': 'Status',
  'ficha.imagenesGeneradas': 'Images generated',
  'ficha.enCalendario': 'Pieces in the calendar',
  'ficha.tareasPendientes': 'Pending tasks',
  'ficha.versionDeLaFicha': 'Profile version',
  'ficha.proximaAccion': 'Next human action',
  'ficha.sinFicha': 'No character profile',
  'ficha.sinFichaQue': 'It has not been generated yet.',

  'ident.estiloVisual': 'Visual style',
  'ident.registro': 'Photographic register',
  'api.sinConexion': 'Could not reach the server.',
  // ── Opciones del perfil creativo ──────────────────────────────────────────
  'perfil.estilo.veinte': 'Early twenties, no styling',
  'perfil.estilo.veinte.det':
    'Bare face, freckles, unbrushed hair, clothes she wears at home. Like a real selfie.',
  'perfil.estilo.producida': 'Grown woman, styled',
  'perfil.estilo.producida.det': 'Makeup done, hair styled, a chosen outfit. The styling shows.',
  'perfil.reg.natural': 'Natural',
  'perfil.reg.natural.det':
    'A real camera, unretouched. You can see the skin. The least obviously generated.',
  'perfil.reg.editorial': 'Editorial',
  'perfil.reg.editorial.det':
    'A fashion shoot: controlled light, styled wardrobe. More polished, more artificial.',
  'perfil.reg.amateur': 'Phone',
  'perfil.reg.amateur.det': 'Shot on a phone: direct flash, imperfect framing, some noise.',
  'perfil.piel.calle': 'Street',
  'perfil.piel.calle.det': 'Everyday clothes, always covered.',
  'perfil.piel.deportivo': 'Sportswear',
  'perfil.piel.deportivo.det': 'Crop top and leggings when the scene is a workout.',
  'perfil.piel.playa': 'Beach',
  'perfil.piel.playa.det': 'Swimwear where the setting calls for it.',
  'perfil.piel.sensual': 'Sensual',
  'perfil.piel.sensual.det':
    "Lingerie and interiors, suggested and never explicit. Generates with fal instead of Google, because Google's filter rejects it and charges for the attempt anyway.",
  'perfil.piel.atrevido': 'Bold',
  'perfil.piel.atrevido.det':
    'Thong and high cut, back views, full length. Same limit: no nudity, nothing explicit. It is the level the provider rejects most.',
  'perfil.cuerpo.sin': 'No preference',
  'perfil.cuerpo.sin.det': 'The model picks it while writing her bible.',
  'perfil.cuerpo.atletico': 'Athletic',
  'perfil.cuerpo.atletico.det': 'Toned, someone who trains.',
  'perfil.cuerpo.curvilineo': 'Curvy',
  'perfil.cuerpo.curvilineo.det': 'Narrow waist and marked hips, an hourglass figure. In shape.',
  'perfil.cuerpo.delgado': 'Slim',
  'perfil.cuerpo.delgado.det': 'Slender, long lines.',
  'perfil.tono.neutro': 'Neutral',
  'perfil.tono.neutro.det': 'True colour, no grading.',
  'perfil.tono.calido': 'Warm',
  'perfil.tono.calido.det': 'Golds and amber.',
  'perfil.tono.frio': 'Cool',
  'perfil.tono.frio.det': 'Blues and greys.',
  'perfil.tono.contraste': 'Contrast',
  'perfil.tono.contraste.det': 'Deep shadows, saturated colour.',
  'perfil.calidad.limpia': "Good",
  'perfil.calidad.limpia.det': "The usual one. Sharp, well exposed, vivid modern-phone colour.",
  'perfil.calidad.cruda': "Bad camera",
  'perfil.calidad.cruda.det': "Like a photo taken without thinking and sent straight away: grainy, a little blurred, slightly out of focus, low resolution and washed-out colour. It is what makes it look most real. Works with any light — with \"Indoors at night\" it is the photo from bed; with \"Window light\" or \"Golden hour\", the same bad camera but in daylight.",
  'perfil.escena.mezcla': 'Mixed',
  'perfil.escena.mezcla.det': 'Alternates indoors and outdoors.',
  'perfil.escena.interiores': 'Indoors',
  'perfil.escena.interiores.det': 'Home, studio, indoor gym.',
  'perfil.escena.exteriores': 'Outdoors',
  'perfil.escena.exteriores.det': 'Street, park, beach, terrace.',
  'perfil.luz.frontal': "Selfie front light",
  'perfil.luz.frontal.det': "The usual one. Plenty of light straight on, no side shadow, like a selfie taken at arm’s length facing a window. Flat, well-lit face. Homemade.",
  'perfil.luz.flash': "Phone flash",
  'perfil.luz.flash.det': "At night, direct flash: she is bright and the background much darker, with a hard shadow on the wall behind. The most recognisable of all, and the most homemade.",
  'perfil.luz.noche': "Indoors at night",
  'perfil.luz.noche.det': "Warm lamps, low light, some grain in the shadows. Darker than the rest. Homemade.",
  'perfil.luz.ventana': "Window light",
  'perfil.luz.ventana.det': "Comes from one side only: shadow under the cheekbone and the rest of the room darker. The one that gives the most shape without looking staged.",
  'perfil.luz.contraluz': "Backlit",
  'perfil.luz.contraluz.det': "Window behind: blown-out background, face lit by bounce, some haze and flare. Pretty, and clearly deliberate.",
  'perfil.luz.tarde': "Golden hour",
  'perfil.luz.tarde.det': "Low afternoon sun, long shadows, golden rim on the hair. Outdoors. Produced.",
  'perfil.luz.neon': "Neon at night",
  'perfil.luz.neon.det': "City at night: pink and cyan on the skin, dark background, mixed white balance. Produced.",
  'perfil.luz.estudio': "Studio",
  'perfil.luz.estudio.det': "Large softbox with fill, even exposure, clean shadows. The most professional, and also the least like what a real person posts.",

  'ident.estiloEtario': 'Styling',
  'ident.nivelDePiel': 'Skin level',
  'ident.complexion': 'Build',
  'ident.tonoDelFeed': 'Feed tone',
  'ident.escenas': 'Scenes',
  'ident.iluminacion': 'Lighting',
  'ident.calidad': 'Quality',
  'ident.guardadoYReescrito':
    'Saved, and the physical description was rewritten with the new build. The images that ' +
    'already exist have the previous body: regenerate the front shots and the identity sheet in ' +
    'the Gallery so they match.',
  'ident.guardadoSinReescribir':
    'Saved. The profile could not be rewritten; check that a text provider is configured.',
  'ident.guardadoSinRehacer':
    'Saved. It applies to whatever you generate from now on; there is nothing to redo.',
  'ident.esPersonaje':
    'This is a non-human character: skin level, build and scene type do not apply. The generator ' +
    'is forbidden from dressing it or giving it human objects, and its scenes come from the ' +
    'world it lives in — not from indoors or outdoors, which are categories of a house.',
  'ident.cambiaCuerpoAntes':
    'Changing the build rewrites the profile’s physical description, keeping face, hair and skin ' +
    'tone. ',
  'ident.cambiaCuerpoFuerte': 'The images that already exist have the previous body',
  'ident.cambiaCuerpoDespues':
    ': you will have to regenerate the front shots and the identity sheet so they match.',
  'ident.guardarCambios': 'Save changes',
  'ident.ficha': 'Profile',
  'ident.guardarFicha': 'Save profile',
  'ident.fichaGuardada': 'Profile saved.',
  'ident.noSePudoGuardarFicha': 'The profile could not be saved.',
  'ident.noSePudoGuardar': 'It could not be saved.',
  'ident.versionNuevaAntes':
    'It is stored as a new version; the earlier ones stay because they explain which text every ' +
    'already-generated image came out of. Changing the ',
  'ident.descripcionFisica': 'physical description',
  'ident.versionNuevaDespues':
    ' does not redo what already exists: to make it match you have to regenerate the front shots ' +
    'and the identity sheet.',
  'ident.editar': 'edit',

  'gal.accionFallo': 'The action could not be completed.',
  'gal.ultimasDe': 'last {n} of {total}',
  'gal.confirmarCambioFrontal':
    'The identity sheet was generated with the current front shot attached as its reference. If ' +
    'you change the front shot, the sheet will still be the previous face: its three angles have ' +
    'to be regenerated, and so does anything already generated from it.\n\nNothing is deleted, ' +
    'and you can pick the previous one again whenever you want.\n\nChange the front shot?',
  'gal.generando':
    'Generating images. It takes about a minute and they appear on their own — no need to reload ' +
    'or press again.',
  'gal.interrumpida': 'The generation was interrupted',
  'gal.interrumpidaSet':
    'The shots that already appeared are saved. Press “Complete set” to generate only the ' +
    'missing ones.',
  'gal.interrumpidaHoja':
    'The angles that already appeared are saved. Press “Complete sheet” to generate only the ' +
    'missing ones.',
  'gal.etapa1': 'Step 1 · Pick the canonical front shot',
  'gal.etapa1Que':
    'Whichever you pick is attached as the reference image to everything that comes after. It is ' +
    'not a photo to publish: it is the face the model will repeat.',
  'gal.frontalElegida': 'Front shot chosen',
  'gal.frontalElegidaQue':
    'The others are kept with their provenance. You can change the front shot whenever you want: ' +
    'pick another and it replaces this one',
  'gal.frontalElegidaConHoja':
    ' — the identity sheet was generated from the current one, so it would need regenerating.',
  'gal.sinCandidatas': 'No candidates have been generated yet.',
  'gal.faltaFrontal': 'The front shot is still to be picked',
  'gal.faltaFrontalQue': 'The identity sheet is generated from the face you pick in step 1.',
  'gal.etapa2Generar': 'Step 2 · Generate the identity sheet',
  'gal.etapa2FaltaUno': 'Step 2 · {n} angle missing',
  'gal.etapa2FaltanVarios': 'Step 2 · {n} angles missing',
  'gal.hojaAprobada': 'Sheet approved',
  'gal.etapa2Revisa': 'Step 2 · Review and approve the sheet',
  'gal.etapa2GenerarQue':
    'Three angles —three-quarter, profile and medium shot— derived from the chosen front shot. ' +
    'It is the character’s visual memory: it is attached to every final image so the face does ' +
    'not drift.',
  'gal.etapa2FaltanQue':
    '{n} of 3 angles are already saved. Continuing does not repeat them: only the missing ones ' +
    'are generated.',
  'gal.hojaAprobadaQue':
    'These three angles are attached, along with the front shot, to every image in the final set.',
  'gal.etapa2RevisaQue':
    'Check that all three are the same person as the front shot. Regenerate any that is not: ' +
    'approve a weak sheet and the whole final set comes out weak.',
  'gal.generandoBoton': 'Generating…',
  'gal.generarHoja': 'Generate identity sheet',
  'gal.completarHoja': 'Complete sheet',
  'gal.aprobando': 'Approving…',
  'gal.aprobarHoja': 'Approve sheet',
  'gal.confirmarRehacerHoja':
    'The three current angles will be discarded and a new sheet generated from the canonical ' +
    'front shot.\n\nThe sheet is the identity reference attached to ALL content generated ' +
    'afterwards: with a new sheet, what comes next will resemble it and not the content you ' +
    'already have.\n\nThe old angles are discarded, not deleted: you will still be able to look ' +
    'at them and compare.\n\nRedo the sheet?',
  'gal.rehaciendo': 'Redoing…',
  'gal.rehacerHoja': '↻ Redo the whole sheet',
  'gal.sinHoja': 'The identity sheet has not been generated yet.',
  'gal.frontales': 'Front shots',
  'gal.frontalesQue': 'you pick one',
  'gal.hojaDeIdentidad': 'Identity sheet',
  'gal.hojaQue': 'you review and approve',
  'gal.cuerpo': 'Full body',
  'gal.cuerpoQue': 'for distant shots',
  'gal.etapa3': 'No full-body reference yet',
  'gal.etapa3Que':
    'The canonical is a face crop, and the model inherits its framing from the reference: ' +
    'without a full-body photo, full-length shots still come out close in. They come one at a ' +
    'time because each is 1920×2880 and takes a while. Keep the one where her whole figure ' +
    'shows and it still looks like her; generate another if it does not convince you.',
  'gal.cuerpoElegido': 'Body reference chosen',
  'gal.cuerpoElegidoQue':
    'Sent instead of the face on full-length and wide shots. Close-ups still use the canonical: ' +
    'this one is worse there, because the face is only a hundred pixels across.',
  'gal.generarCuerpo': 'Generate one',
  'gal.desdeFoto': 'Derive from a photo',
  'gal.limpiarCandidatas': 'Delete the {n} not chosen',
  'gal.limpiarHoja': 'Delete the {n} not approved',
  'gal.limpiarHojaConfirmar':
    'This deletes {n} sheet shots and their files, with no undo. Approved ones are left alone.',
  'gal.limpiarCandidatasConfirmar':
    'This deletes {n} candidates and their files, with no undo. The canonical is left alone.',
  'gal.desdeFotoElegi':
    'The face in that photo is turned, so three frontal versions were generated too. Pick ' +
    'which one becomes the canonical below: the crop keeps the likeness better, the ' +
    'generated ones face forward. Redo the identity sheet and body reference afterwards.',
  'gal.derivando': 'Cropping…',
  'gal.desdeFotoQue':
    'If you already have a post that works, take the canonical from it: the face is cropped and ' +
    'scaled, with no model in between. Measured: cropping keeps the likeness (0.95), ' +
    'regenerating loses it (0.54). The face has to be clearly visible in the photo.',
  'gal.desdeFotoHecho':
    'Canonical changed. The identity sheet and the body reference came from the previous one, ' +
    'so they now describe a different face: redo both before generating content.',
  'gal.subirCuerpo': 'Upload a photo',
  'gal.formaTitulo': 'Body shape',
  'gal.formaQue':
    'The silhouette is drawn by a LoRA, not by the text: without it the figure comes out ' +
    'straight even when the prompt asks for hips. This is the weight that applies to her, ' +
    'for profile «{perfil}» and level «{nivel}». Change it and generate again.',
  'gal.formaMedido':
    'Measured: 0.8 matches the previous reference and 1.0 starts to stylise. Values in ' +
    'between are not measured.',
  'gal.formaSinLora': 'No shape LoRA',
  'gal.formaGuardando': 'Saving…',
  'gal.formaAfecta': 'Affects every creator with the same profile and level.',
  'gal.subiendoCuerpo': 'Uploading…',
  'gal.subirCuerpoQue':
    'If you already have a full-body photo of her, upload it: it beats any generated one, because the figure is the real one and not one the model invented. It becomes the chosen reference on upload.',
  'gal.generarMasCuerpo': 'Generate another',
  'gal.sinCuerpos': 'You have not generated any yet.',
  'gal.limpiarCuerpo': 'Delete the {n} not chosen',
  'gal.limpiando': 'Deleting…',
  'gal.limpiarCuerpoConfirmar':
    'This deletes {n} images and their files, with no undo. The chosen one is left alone.',
  'gal.etapas': 'Generation steps',

  'gal.sinImagenes': 'No images in this step',
  'gal.nadaActivo': 'Nothing active',
  'gal.nadaActivoQue':
    'Every image in this step has been discarded. Generate again, or look at the history.',
  'gal.descartadaUna': '1 discarded image',
  'gal.descartadasVarias': '{n} discarded images',
  'gal.confirmarBorrar':
    'This image’s file will be deleted ({kb} kB). It cannot be undone.\n\nIts provenance ' +
    '—prompt, seed, model and credits— is kept in the history, so the day’s spending can still ' +
    'be explained.\n\nDelete?',
  'asset.alt': '{tipo} — generated with {modelo}',
  'asset.canonica': '★ Canonical reference',
  'asset.modelo': 'Model',
  'asset.semilla': 'Seed',
  'asset.creditos': 'Credits',
  'asset.verPrompt': 'View prompt',
  'asset.usarEnSuLugar': '★ Use this one instead',
  'asset.usarComoReferencia': '★ Use as the reference',
  'asset.aprobar': '✓ Approve',
  'asset.descartar': '✗ Discard',
  'asset.sinSemilla':
    'This image did not store its seed, so the new one will not be comparable: the pose will ' +
    'change as well as the face.',
  'asset.realceNormalQue':
    'Regenerates with Evaluna’s face at 50 %, same seed. Careful: it changes the features, not ' +
    'just the skin — it will not be the same person.',
  'asset.realceAltoQue':
    'Regenerates with Evaluna’s face at 75 %, same seed. At this strength almost nothing of the ' +
    'original face survives.',
  'asset.realceNormal': '✨ Normal enhance',
  'asset.realceAlto': '✨ Strong enhance',
  'asset.eliminar': '🗑 Delete',
  'asset.copiarPrompt': 'Copy prompt',

  'video.recorteAvisado':
    'It is not {aspecto}: the photo is extended before animating it (+USD 0.04). The image is ' +
    'left intact and only the missing border is generated.',
  'video.estadoEspera': '{estado} · {segundos}s',
  'video.generando': 'generating',
  'video.sigueGenerandose': 'Still generating. It appears on its own when it finishes.',
  'video.movimiento': 'What she does in the clip (optional)',
  'video.movimientoEjemplo': 'she stands up and sticks her tongue out',
  'video.segundos': 'Seconds',
  'video.consejo':
    'With no text the clip barely moves: it breathes and blinks. Write a concrete action to make ' +
    'something happen. Avoid "gently", "subtle" and "slowly": they produce the opposite. Each ' +
    'second costs over a minute of GPU, and past 10 the model loses the face.',
  'video.noSePudo': 'The clip could not be generated.',
  'video.conVideo': 'Animate from a video…',
  'video.reemplazo': 'Put her in the video',
  'video.reemplazoQue':
    'The whole video goes in —scene, light, framing— and only who is in it changes. The ' +
    'clothes and the place come from the video, not from her profile.',
  'video.conVideoQue':
    'Upload a clip and she performs THAT motion. The first 5 seconds are used and the clip lasts that long; only the skeleton is taken, so whoever appears in the video never reaches the result. Your own GPU only.',
  'estudio.sinTomas':
    'There are no publishable shots yet. Generate the final set in Gallery → 3 · Content.',
  'estudio.sePublicaClip': 'This clip is what gets published',
  'estudio.sePublicaFoto': 'This photo is what gets published',
  'estudio.seRecorta': ' — the calendar asked for {aspecto}, it will be cropped on publishing',
  'estudio.comoReel': 'It is published as a reel.',
  'musica.titulo': 'Clip music',
  'musica.sinMusica': 'No music',
  'musica.subir': 'Upload…',
  'musica.subirQue':
    'Add a track to your library. Use royalty-free music: Instagram mutes or blocks what it recognises.',
  'musica.volumen': 'Volume',
  'musica.mezclando': 'Mixing…',
  'musica.buscar': 'Search…',
  'musica.buscarQue':
    'Search free music on Openverse, filtered to CC0 and public domain: no attribution required, no commercial limits.',
  'musica.buscarAccion': 'Search',
  'musica.buscarEjemplo': 'lofi, ambient, guitar…',
  'musica.deDonde':
    'Openverse, CC0 and public domain only. Mostly library loops, not finished songs.',
  'musica.importarQue': 'Add to your library',
  'estudio.cambiar': 'Change',
  'estudio.elegiImagen': 'Pick the image for this post',
  'estudio.coincide': '{ancho}×{alto} — matches {aspecto}',
  'estudio.noCoincide': '{ancho}×{alto} — the calendar asked for {aspecto}, it will be cropped',
  'estudio.calendarioPidioAntes': 'The calendar asked for ',
  'estudio.calendarioPidioDespues': '. Any other ratio is cropped on publishing.',
  'estudio.generadas': '{hechas} of {total} generated',
  'estudio.unaToma': 'Generating one shot…',
  'estudio.cadaTomaTarda':
    'Takes about 40 seconds. If it stalls, a step can hang for up to four minutes before ' +
    'retrying; if it really is stuck, «Generate anyway» starts another.',
  'encargo.tipo.actividad': 'Doing something',
  'encargo.tipo.selfie': 'Selfie',
  'encargo.tipo.espejo': 'Mirror',
  'encargo.tipo.atractivo': 'Attractive',
  'encargo.tipo.imperfecta': 'Badly taken',
  'encargo.encuadre.close_up': 'Close-up',
  'encargo.encuadre.medium': 'Medium shot',
  'encargo.encuadre.full_body': 'Full body',
  'encargo.encuadre.wide': 'Wide shot',
  'encargo.cubo.agua': 'Swimwear',
  'encargo.cubo.gimnasio': 'Gym kit',
  'encargo.cubo.interior': 'Loungewear',
  'encargo.cubo.calle': 'Street clothes',
  'encargo.titulo': 'How this batch comes out',
  'encargo.todoAlAzar': 'All random',
  'encargo.fijados': '{n} fixed',
  'encargo.alAzar': 'Random',
  'encargo.rol': 'Role she plays',
  'encargo.sesion': 'Photo session',
  'encargo.escrito': 'Say it in your own words',
  'encargo.escritoEjemplo': 'a session on a runway in Sydney, at night',
  'encargo.traducir': 'Build the settings',
  'encargo.traduciendo': 'Building…',
  'encargo.entendi': 'I understood: {texto}',
  'estudio.sinTanda': 'Earlier',
  'asset.hacerSet': '+ More of this moment',
  'asset.setDescripcion': 'Anything to ask for (optional). Leave it empty and it stays like this one, changing the pose.',
  'asset.generada': 'Generated',
  'asset.setDe': '{n} shots of the same moment',
  'asset.setQue':
    'Makes more shots of THIS photo: same place, same outfit and the same camera, with her in ' +
    'another pose or at another distance. They are grouped to post together as a carousel and ' +
    'this one stays first. If what you want is different angles of the place, that is what the ' +
    '«photo session» checkbox does when generating a batch.',
  'encargo.sesionAyuda': 'all in one place, same outfit',
  'encargo.ellaMisma': 'Herself',
  'encargo.techo': 'Indoor or outdoor',
  'encargo.adentro': 'Indoor',
  'encargo.afuera': 'Outdoor',
  'encargo.lugar': 'Place',
  'encargo.tipo': 'Kind of photo',
  'encargo.encuadre': 'Framing',
  'encargo.ropa': 'Clothing',
  'encargo.participa': 'Uses the place',
  'encargo.participaSi': 'Yes, she is using it',
  'encargo.participaNo': 'No, she is beside it',
  'encargo.participaNoAplica': 'This place has nothing to be on: it comes out the same.',
  'encargo.actividad': 'What she is doing',
  'encargo.gestos': 'Gestures (one hand)',
  'encargo.ropaDelLugar': 'The chosen place decides it.',
  'encargo.pie':
    'What you pick applies to EVERY shot in the batch. Anything left on «random» is dealt per ' +
    'shot, as before.',
  'estudio.generarContenido': 'Generate content',
  'estudio.faltaAprobarHoja': 'The identity sheet still needs approving',
  'estudio.generarContenidoQue':
    'The model proposes the scenes from the niche, avoiding what has already been posted, and ' +
    'every image is generated with the sheet attached. The text is requested afterwards, image ' +
    'by image, once you have looked at it.',
  'estudio.faltaAprobarHojaQue':
    'Content is generated with the identity sheet attached. Without it, every shot would come ' +
    'out with a different face. Finish it in the Gallery.',

  'estudio.tandaReutiliza':
    'Batch of {total}: {existentes} were already generated and are reused, {faltan} to go.',
  'estudio.completarSet': 'Generate one shot',
  'estudio.generarIgual': 'Generate anyway',
  'estudio.generarIgualConfirmar':
    'A generation is already running. If it stalled, this starts another shot: it costs one more image.',
  'estudio.laUltimaFallo': 'The last generation failed',
  'estudio.generarSet': 'Generate one shot',
  'estudio.generarSesion': 'Generate the session (4 photos)',
  'estudio.sinPublicacion': 'Content with no post · {n}',
  'estudio.descartado': 'Discarded content · {n}',
  'estudio.sinPublicacionQue': 'generated but with no text to go with them',
  'estudio.descartadoQue': 'unused, and not going to be published',
  'estudio.huerfanasQue':
    'Their post can be written from the scene they were generated with, without spending on new ' +
    'images.',
  'estudio.escribiendo': 'Writing…',
  'estudio.escribirPublicaciones': 'Write their posts',
  'tarea.enlaceDePrueba': 'Free trial link',
  'tarea.enlaceDePruebaQue':
    'The Fanvue promo link, with its identifier. It shows on the public page as a second ' +
    'link, above the regular one. Can be left empty.',
  'estudio.duplicadaAntes': ' is too close to “',
  'estudio.duplicadaDespues': '”, so it was not written.',
  'estudio.duplicadaPorQue':
    'With several posts from the same niche the vocabulary runs out and the resemblance is ' +
    'expected. If you think they are different, write it anyway and review it before publishing.',
  'estudio.escribirlaIgual': 'Write it anyway',
  'estudio.imagenes': 'Images',
  'estudio.videos': 'Videos',
  'estudio.quedanDescartadas': 'The remaining ones are discarded: they are shown below.',
  'estudio.generarPublicacion': 'Write the post',
  'estudio.animarQue':
    'Generates a {segundos} s clip with {proveedor}, chosen by this influencer’s skin level.',
  'estudio.aprobarPrimero': 'Approve the shot first: the clip inherits whatever it shows',
  'estudio.animando': 'Animating…',
  'estudio.animar': 'Animate · {precio}',
  'estudio.aprobarParaAnimar': 'Approve the shot to animate it',
  'estudio.escalar': 'Upscale',
  'estudio.escalarDeNuevo': 'Upscale again',
  'estudio.escalando': 'Upscaling…',
  'estudio.factorDeEscalado': 'How much bigger',
  'estudio.escalarQue': 'Comes out at {ancho}×{alto}, on your own GPU. The original is untouched.',
  'estudio.hayEscalada': 'High-res: {ancho}×{alto} · {mb} MB',
  'estudio.refCuerpo': 'Use as body reference',
  'estudio.refCuerpoEsLa': 'This is the body reference',
  'estudio.refCuerpoQuitar': 'Stop using it',
  'estudio.refCuerpoGuardando': 'Saving…',
  'estudio.refCuerpoQue':
    'Sent as the reference on full-length and wide shots. Her whole body has to be visible: ' +
    'the framing is inherited from this photo.',
  'estudio.refCuerpoQuitarQue':
    'With no body reference, distant shots go back to the face crop and come out close in.',
  'estudio.tipoDeContenido': 'Content type',
  'estudio.publicableEnTodas': 'Publishable everywhere',
  'estudio.soloFanvue': 'Fanvue only',
  'espacio.descartadaUna': '1 discarded',
  'espacio.descartadasVarias': '{n} discarded',
  'espacio.ocupan':
    ' take up {mb} MB and are not going to be published. Deleting them frees that space; their ' +
    'provenance stays in the history.',
  'espacio.confirmarLiberar':
    '{n} discarded files ({mb} MB) will be deleted. It cannot be undone.\n\nTheir provenance ' +
    '—prompt, seed, model and credits— is kept in the history.\n\nFree the space?',
  'espacio.parcial': '{borradas} were deleted and {fallaron} could not be. {mb} MB freed.',
  'espacio.total': '{borradas} files deleted, {mb} MB freed.',

  'nsfw.tomasGeneradas': '{n} shots generated. They are waiting to be reviewed.',
  'nsfw.generadasYFallidas': '{hechas} generated and {fallidas} failed.',
  'nsfw.noSePuedeAun': 'Cannot generate yet',
  'nsfw.faltaConfigurar': 'Something still has to be configured before generating.',
  'nsfw.faltaHoja': 'The identity sheet still needs approving',
  'nsfw.faltaHojaQue':
    'Every shot is generated with the sheet attached. Without it, it would come out with a ' +
    'different face — and that matters more here than anywhere else, because it is the only ' +
    'thing tying this material to the same person.',
  'nsfw.noSePudoGenerar': 'It could not be generated',
  'nsfw.fallaron': '{n} failed',
  'nsfw.generadas': '{hechas} of {total} generated',
  'nsfw.dosPasos':
    'Each shot takes two steps: it is generated and then its face is swapped. It takes about a ' +
    'minute and both calls are billed — roughly USD 0.04 per image.',
  'nsfw.generarContenido': 'Generate content',
  'nsfw.enGpuPropia':
    'The model proposes the scenes from the niche. Each shot is generated on your own GPU, in a ' +
    'single step and at no cost — identity is held by Krea2’s editing mechanism from the ' +
    'approved sheet. The machine has to be switched on.',
  'nsfw.enProveedor':
    'The model proposes the scenes from the niche. Each shot takes two steps: first the body and ' +
    'the scene, then her face is swapped in. You pick which ones you want before spending: about ' +
    'USD 0.04 per image, and the ones you discard afterwards are billed too.',
  'nsfw.proporcion': 'Aspect ratio',
  'nsfw.caraEnGpuPropia':
    'Your GPU already holds the identity while generating, so this step is normally redundant. ' +
    'It costs USD 0.04 per shot and the editor has the vendor’s own filter.',
  'nsfw.caraEnProveedor':
    'Runware’s models do not hold identity and generate the register at the same time, so the ' +
    'face is applied in a second step. It costs USD 0.04 per shot.',
  'nsfw.cambiarLaCara': 'Swap the face',
  'nsfw.generandoConCara': 'Generating and swapping the face…',
  'nsfw.elegiUnaEscena': 'Pick a scene',
  'nsfw.generarSetN': 'Generate set · {n}',
  'nsfw.queSeVe': 'What is in frame',
  'nsfw.pensando': 'Thinking…',
  'nsfw.queLasProponga': '✨ Let the model propose them',
  'nsfw.hastaDonde': 'How far it goes',
  'nsfw.nivelExplicito': 'Nude',
  'nsfw.nivelInsinuado': 'Almost explicit',
  'nsfw.nivelExplicitoDetalle':
    'Full nudity. Fanvue subscriber feed only: never Instagram or Facebook, and never the ' +
    'avatar or banner.',
  'nsfw.nivelInsinuadoDetalle':
    'Minimal lingerie, or nude with just enough covered. Nothing intimate on show. Goes to ' +
    'Fanvue and Telegram: it does not go to Instagram or Facebook either.',
  'fanvue.otraVez': 'Post again',
  'fanvue.publicando': 'Publishing…',
  'fanvue.otraVezTitulo': 'Sends the same photo with the same text again. The first one stays.',
  'fanvue.otraVezConfirmar':
    'Post this to {destino} again? It goes out to the channel and that cannot be undone.',
  'fanvue.cancelar': 'Cancel the post',
  'fanvue.cancelando': 'Cancelling…',
  'fanvue.cancelarTitulo': 'Deletes the post and returns the shot to «create post».',
  'fanvue.cancelarConfirmar':
    'Cancel this post? The photo is not deleted: it goes back to ready for a new one.',
  'nsfw.salenDelNicho':
    'They come from her niche: a volleyball player and a cook do not get the same ones.',
  'nsfw.deEspaldas': '· from behind',
  'nsfw.escribiUna': 'Or write one: in bed, morning light through the window…',
  'nsfw.agregar': 'Add',
  'nsfw.describiLaEscena':
    'Describe the scene, not her: face, body and style come from her identity sheet, so that she ' +
    'is the same person as in the rest of the content.',

  'fanvue.clipHereda': 'The clip inherits whatever the shot shows.',
  'fanvue.aprobarPrimero': 'Approve the shot first: the clip inherits whatever it shows.',
  'fanvue.aprobalaParaAnimar': 'Approve it to animate it',
  'fanvue.creando': 'Creating…',
  'fanvue.yaSalioEn': 'Already published on {destino}.',
  'fanvue.sumarTelegram': '+ Publish it on Telegram too ({stars} ⭐)',
  'fanvue.publicarConStars': 'Publish for {stars} ⭐',
  'fanvue.publicarGratis': 'Publish free, in the open',
  'fanvue.enFanvue': 'On Fanvue, behind the subscription',
  'fanvue.telegramDePago': 'On Telegram, blurred · {stars} ⭐ to unlock',
  'fanvue.telegramGratis': 'On Telegram, IN THE OPEN · no Stars',
  'fanvue.gratisEnTelegram': 'Give it away on Telegram (no Stars)',
  'fanvue.crearPublicacion': 'Create the post',
  'fanvue.faltanSalir': 'Not published yet',
  'fanvue.yaSalieron': 'Already published',
  'fanvue.publicada': 'Published on Fanvue, for subscribers.',
  'fanvue.faltaConectar':
    'Fanvue still has to be connected and a profile assigned to this influencer, under Platforms.',
  'fanvue.manual':
    'It did not go out through the API: it was left as a package to publish by hand. Usually the ' +
    'Fanvue connection is missing a permission — reconnecting under Credentials fixes it, and ' +
    'that is where you see which one is missing.',
  'fanvue.contesto': 'Fanvue answered “{estado}” instead of publishing.',
  'fanvue.publicadasTodas': '{n} published on Fanvue.',
  'fanvue.publicadasParciales': '{hechas} published and {fallos} that did not go out.',
  'fanvue.faltaClave': 'The Runware key is missing',
  'fanvue.faltaClaveP1Antes':
    'This content is generated with Runware, which is pay-per-use and has no free tier. The key ' +
    'is yours and goes under ',
  'fanvue.credenciales': 'Credentials',
  'fanvue.faltaClaveP1Despues':
    ': that way you contract directly, and whoever asks for a generation pays for it.',
  'fanvue.faltaClaveP2':
    'Runware was chosen for its terms, not its price. The cheapest providers in the survey forbid ' +
    'providing the service to third parties, and this panel has more than one user — the ban ' +
    'would reach us even if none of them ever touched their API.',
  'fanvue.faltaClaveP3Antes': 'If you would rather not pay, under ',
  'fanvue.modelos': 'Models',
  'fanvue.faltaClaveP3Despues':
    ' you can pick your own GPU for the Fanvue image: it runs on your machine, costs nothing and ' +
    'has nobody’s filter.',
  'fanvue.gpuPeroVideo': 'The image comes from your GPU; the video needs Runware',
  'fanvue.gpuPeroVideoP1Antes': 'The Fanvue image is configured to use ',
  'fanvue.tuPropiaGpu': 'your own GPU',
  'fanvue.gpuPeroVideoP1Despues':
    ', so it is generated with no key and at no cost — the machine just has to be switched on.',
  'fanvue.gpuPeroVideoP2Antes': 'What does need the Runware key is ',
  'fanvue.animar': 'animating',
  'fanvue.gpuPeroVideoP2Despues':
    ' a shot, and the face swap if you ask for it. Both are pay-per-use; generating images here ' +
    'is not.',
  'fanvue.conQueSeGenera': 'What it is generated with',
  'fanvue.imagen': 'Image',
  'fanvue.video': 'Video',
  'fanvue.seEligenAntes': 'They are chosen under ',
  'fanvue.seEligenDespues':
    ', separately from the ones for publishable content: those fall inside the free daily quota ' +
    'and these are billed in dollars per generation. A single dropdown mixing them would make ' +
    'picking an expensive one look like picking a free one.',
  'fanvue.paraPublicar': 'Ready to publish · {n}',
  'fanvue.publicandoLote': 'Publishing… {hechas} of {total}',
  'fanvue.siPublicar': 'Yes, publish all {n}',
  'fanvue.salenAlFeed': 'They go to the subscriber feed. It cannot be undone.',
  'fanvue.publicarLas': 'Publish all {n}',
  'fanvue.noSalieron': '{n} did not go out:',
  'fanvue.ningunaAun':
    'There are none yet. The {n} images that already exist are classified as publishable on any ' +
    'platform.',
  'fanvue.nadaEnRedesAntes':
    'Nothing that appears here can be published on Instagram or Facebook: the server rejects it ' +
    'on the attempt, it is not a checkbox you can untick. On Fanvue it goes to the subscriber ' +
    'feed — ',
  'fanvue.no': 'not',
  'fanvue.nadaEnRedesDespues':
    ' to the avatar, the banner or the intro video, which are visible without a subscription and ' +
    'have to be safe.',

  'lora.sinLora': 'No LoRA',
  'lora.fallo': 'Failed: {estado}',
  'lora.listo': 'Ready',
  'lora.sinProbar': 'Trained, untested',
  'lora.entrenando': 'Training',
  'lora.sinLoraQue':
    'Identity comes from the sheet’s references. Using Z-Image requires training one.',
  'lora.falloQue': 'The training did not finish cleanly. It can be retried from the LoRA tab.',
  'lora.listoQue':
    'It has been used to generate at least once, so the LoRA works. Whether it applies to a given ' +
    'shot depends on the chosen model: it only takes effect within its own family.',
  'lora.sinProbarQue':
    'This state does not change on its own: it stays here until a generation succeeds with it. ' +
    'Write a scene by hand under Fanvue and generate one shot — if it works, it turns to “Ready” ' +
    'and the model is allowed to propose scenes. If it is rejected, nothing is charged.',
  'lora.entrenandoQue': '{pasos} steps, started {cuando}{avance}.',
  'lora.tarjeta': 'Identity LoRA',

  'lora.zipSubido': 'ZIP uploaded with {n} images.',
  'lora.noSePudo': 'It did not work',
  'lora.entendidoOcultar': 'Got it, hide this',
  'lora.setDeEntrenamiento': 'Training set',
  'lora.setDeEntrenamientoQue':
    'It comes from what has already been published, which is the strongest signal there is: it ' +
    'passed your judgement and it was used. Each image carries a caption describing the scene and ' +
    'saying nothing about the facial features — whatever the caption explains is what the LoRA ' +
    'stops learning.',
  'lora.zipListo': 'ZIP ready with {n} images.',
  'lora.armando': 'Building…',
  'lora.generarZip': '1 · Build ZIP · {n}',
  'lora.estimado':
    'Estimate: about {minutos} minutes in total, taken from how long the previous ones took. It ' +
    'is a reference, not a promise.',
  'lora.sinEstimacion':
    'There is nothing to estimate from yet: this is the first one that will finish. The next one ' +
    'will have a reference.',
  'lora.seActualizaSola':
    'This card updates on its own. No need to press again — each training run is billed separately.',
  'lora.enUso': 'in use',
  'setPlaca.tarjeta': 'Training set, generated on the machine',
  'setPlaca.que':
    'Shots are generated here, crossing framing, angle, light, clothing and background, so the ' +
    'variation is guaranteed rather than hoped for. A set built from already-published photos ' +
    'hits a ceiling: that material is uniform, and a LoRA learns whatever repeats.',
  'setPlaca.generar': 'Generate the set on the machine',
  'setPlaca.arrancando': 'Starting…',
  'setPlaca.arrancado': 'Generating {n} shots. You can leave this screen.',
  'setPlaca.tarda': 'about {minutos} min left',
  'setPlaca.cancelar': 'Cancel',
  'setPlaca.enLaPlaca': '{n} on the machine · {elegidas} picked',
  'setPlaca.armarZip': 'Build ZIP with {n}',
  'setPlaca.armando': 'Building…',
  'setPlaca.zipListo': 'ZIP built with {n} images.',
  'setPlaca.zipEn': 'ZIP with {n} images, {mb} MB. It stayed on the machine:',
  'setPlaca.zipQueHacer':
    'It is not downloaded: too large to pass through the panel, and it is already on the ' +
    'machine you upload to RunPod from. Open that folder and upload it from there.',
  'loraPlaca.tarjeta': 'On-board LoRA',
  'pila.tarjeta': 'Style LoRAs · local GPU only',
  'cuerpo.tarjeta': 'Body shape LoRA · local GPU only',
  'cuerpo.que':
    'The shape is drawn by a LoRA, not by the text: the body reference is generated without the ' +
    'identity one —it is an anchor, not content— and with none at all the silhouette comes out ' +
    'straight even when the prompt asks for hips. The weight rises with the skin level because ' +
    'how much of the figure shows is not the same across the six.',
  'cuerpo.medido':
    'Two points on the ramp are measured: 0.8 matches the previous reference and 1.0 starts to ' +
    'stylise. The ones in between are interpolation — which is why they live here and not in code.',
  'cuerpo.sinLora': 'No LoRA',
  'cuerpo.porDefecto': 'As shipped',
  'cuerpo.restablecer': 'Back to as shipped',
  'cuerpo.guardando': 'Saving…',
  'pila.que':
    'The ones describing how the material looks —skin, grain, camera— which apply to every ' +
    "influencer. A person's own LoRA does not go here: it goes on her page.",
  'pila.volverAlWorkflow': 'Back to the workflow stack',
  'pila.configurada': 'configured here',
  'pila.resumenSinTocar': "All four stages use their workflow's own stack. Tap to view or change.",
  'pila.resumenConfiguradas': '{n} of 4 stages have their own stack. Tap to view or change.',
  'pila.ninguno': 'none',
  'pila.delWorkflow': "the workflow's own",
  'pila.loQueNoAparece':
    'Not listed: the identity LoRA —not a style, it is what makes the reference mean ' +
    "something—, each influencer's own, which live on her page, and those of another " +
    'architecture (Wan, LTX), which do nothing on Krea 2. Video workflows load theirs hard-wired ' +
    'and are not configured here.',
  'pila.noSePudoListar': 'Could not list the LoRAs on the machine: {motivo}',
  'loraPlaca.ningunoInstalado':
    "No LoRA of hers on the machine. Copy the .safetensors into ComfyUI's LoRA folder, inside a " +
    'subfolder named {carpeta}, and it shows up here.',
  'loraPlaca.hayDeOtros':
    'There are {n} character LoRAs on the machine, but none in a folder named {carpeta}. ' +
    'Rename her folder to that so they get assigned to her.',
  'loraPlaca.que':
    "This influencer's trained LoRA, stacked when generating on the local GPU. The file is " +
    "copied by hand into ComfyUI's LoRA folder: it weighs hundreds of megabytes and cannot be " +
    'uploaded through the panel.',
  'loraPlaca.archivo': 'File',
  'loraPlaca.ninguno': '— none —',
  'loraPlaca.noEstaEnLaPlaca': '(no longer on the machine)',
  'loraPlaca.encendido': 'LoRA ON',
  'loraPlaca.encendidoQue': 'stacked on every local-GPU generation',
  'loraPlaca.elegiPrimero': 'pick a file first',
  'loraPlaca.carpeta': 'folder: {carpeta}',
  'loraPlaca.guardado': 'Saved.',
  'loraPlaca.noSePudoListar': 'Could not list the LoRAs on the machine: {motivo}',
  'lora.ahoraSeGeneraConEste': 'Generation now uses this one.',
  'lora.usarEste': 'Use this one',
  'lora.cuantoTira': 'Strength',
  'lora.porOmision': ' · default',
  'lora.masMarca': 'Higher marks the identity harder, and the flaws too. No retraining needed.',
  'lora.cuantoTiraContenido': 'Content LoRA strength',
  'lora.conPoco':
    'Too low and she sometimes comes out dressed; too high and it warps faces. It is global, not ' +
    'hers.',
  'lora.sinLoraHasta': 'Generation will run without a LoRA until you pick one.',
  'lora.generarSinLora': 'Generate without the LoRA, to compare',
  'lora.entrenarTitulo': '2 · Train the LoRA',
  'lora.seEntrenaConAntes': 'It trains on ',
  'lora.ese': 'that',
  'lora.seEntrenaConDespues':
    ' ZIP —{imagenes} images, {mb} MB—. Each run keeps its own identifier, so you can have ' +
    'several: one per architecture and per step count.',
  'lora.primeroZipAntes': 'Build the ZIP first, above. Training uses ',
  'lora.primeroZipDespues':
    ' file and not a new one: between building it and training, the floor, the cap or the ' +
    'published images can change, and you have to train on what you looked at.',
  'lora.primeroZipP2':
    'You can train as many times as you like — on both architectures and with different step ' +
    'counts. Each one is kept separately in the history.',
  'lora.soloProbar': ' · only to test the pipeline',
  'lora.minimoIdentidad': ' · minimum for identity',
  'lora.maximoUtil': ' · as high as is useful',
  'lora.cuesta': 'Costs ',
  'lora.pasadasSobre': ' passes over the {n} images',
  'lora.entrenandoQueda': 'Training. It will be called {air}.',
  'lora.enviando': 'Sending…',
  'lora.entrenar': 'Train',
  'lora.zipVenceAntes':
    'The ZIP expires in {cuando} and deletes itself: it is a temporary file, not a copy. If you ' +
    'move the controls above it has to be built again, so that training uses what you are ' +
    'looking at. ',
  'lora.descargarlo': 'Download it',
  'lora.zipVenceDespues': ' if you want to train elsewhere.',
  'lora.oSubiZip': 'Or upload a ZIP',
  'lora.subiendo': 'Uploading…',
  'lora.arrastraZip': 'Drag a ZIP here, or click',
  'lora.sirveElQue':
    'The one you downloaded and pruned by hand works, or one built with another tool. All that is ' +
    'checked is that it is a ZIP.',
  'lora.queEntra': 'What goes in',
  'lora.cuantas': 'How many',
  'lora.parecidoMinimo': 'Minimum likeness',
  'lora.sinPiso': 'no floor',
  'lora.deLasPublicadas': ' of {publicadas} published · {medidas} measured, {sinMedir} unmeasured',
  'lora.sonPocas':
    'That is too few to train on. Below twelve, the LoRA learns the background and the clothes ' +
    'more than the face. Lower the floor, or generate and publish a few more.',
  'lora.disparadorAntes': 'The trigger word is ',
  'lora.disparadorDespues':
    ': the word that invokes the LoRA when generating, and the one that opens every caption.',
  'lora.lasQueEntran': 'The ones going in · {n}',

  'cont.sinMedir': 'unmeasured',
  'cont.sinContenido': 'No content',
  'cont.sinCalendario': 'The calendar has not been generated yet.',
  'cont.todoDescartado':
    'Everything generated was discarded when its images were rejected. Generate another set above.',
  'cont.publicando': 'Publishing',
  'cont.avanceLote': '{hechas} of {total}',
  'cont.publicarEnTanda': 'Publish as a batch',
  'cont.siPublicarEn': 'Yes, publish {n} on {plataforma}',
  'cont.plataformaCuantas': '{plataforma} · {n}',
  'cont.salenDeAUna': 'They go out one at a time, in order. It cannot be undone.',
  'cont.paqueteListo': 'Package ready to publish',
  'cont.copiarTexto': 'Copy text',
  'cont.descargarImagen': 'Download image',
  'cont.abrirPlataforma': 'Open the platform',
  'cont.publicadoEnInstagram': 'Published on Instagram.',
  'cont.verLaPublicacion': 'View the post',
  'cont.salioComoPaquete': 'Not published: it came out as a manual package.',
  'cont.salioComoPaqueteQue':
    'The connection exists but was not in a state to publish. Check its status under Platforms ' +
    'and reconnect if you need to.',
  'cont.sinImagenUna': '{n} post with no image',
  'cont.sinImagenVarias': '{n} posts with no image',
  'cont.sinImagenQue':
    'With no image, the export package comes out with the text ready and nothing to upload. Pick ' +
    'a thumbnail on each post.',
  'cont.paraPublicar': 'Ready to publish · {n}',

  'post.elegiImagenPrimero': 'Pick an image or a clip: with no file the post fails on the way out.',
  'post.sacarDeLaCola': 'Take it out of the queue. The text and the image are not lost.',
  'post.ponerEnCola': 'Put it in the queue: the cron publishes it at its time.',
  'post.republicarTitulo':
    'Writes a new text for this same photo and leaves it ready to publish again',
  'post.confirmarRepublicar':
    'A new post will be created with this same photo and a text written from scratch. The ' +
    'original post is left as it is. Continue?',
  'post.borrarTitulo': 'Delete this post',
  'post.confirmarBorrarPublicada':
    'This post has already gone out to a platform. Deleting it here does NOT delete it there: ' +
    'that has to be done by hand in the app. Delete it from the system?',
  'post.confirmarBorrar':
    'Delete this post? The image or clip goes back to “Content with no post” and can be used ' +
    'again.',
  'post.borrando': 'Deleting…',
  'post.borrar': 'Delete',
  'post.enBorradorAntes': 'Draft: ',
  'post.noSePublicaSola': 'it does not publish itself',
  'post.enBorradorDespues':
    '. The cron only publishes what has been approved — press “Approve” and it goes out at its time.',
  'post.yaPublicada':
    'Already published. The image cannot be changed from here: changing it would not touch the ' +
    'post that is already on the platform.',
  'post.animarTitulo': 'Generates a clip and assigns it to this post',
  'post.aprobarEnGaleria': 'Approve the shot in the Gallery: the clip inherits whatever it shows',
  'post.animarEstaToma': 'Animate this shot · {precio}',

  'post.editarTexto': 'Edit the text',
  'post.editarTextoTitulo':
    'Changes the caption, hashtags and alt text for this platform. It regenerates nothing and costs no money.',
  'post.elPie': 'Caption',
  'post.pieCuantoQueda': '{n} characters left',
  'post.losHashtags': 'Hashtags',
  'post.hashtagsComo': 'Separated by spaces, with or without #. Up to six, and no spaces inside each one.',
  'post.elAlt': 'Alt text',
  'post.altComo': 'What describes the photo to whoever cannot see it. Empty, or ten characters and up.',
  'post.noSePudoGuardarTexto': 'The text could not be saved.',
  'post.quitarImagen': 'Remove the image',
  'post.yaPublicadoVer': 'Already published · view',
  'post.yaPublicadoSinEnlace': 'Already published (no link stored)',
  'post.igSinImagen': 'Instagram does not accept posts without an image: pick one above.',
  'post.yaPublicadaEtiqueta': 'Already published',
  'post.yaPublicadas': 'Already published · {n}',
  'post.yaPublicadasUltimas': 'Already published · last {n} of {total}',
  'post.publicando': 'Publishing…',
  'post.publicarAhora': 'Publish now',
  'post.sePublicaDeVerdad': 'This really publishes.',
  'post.confirmar': 'Confirm',

  'recon.pregunta':
    'Does any post say “no link stored”? It can be recovered from Instagram, for every ' +
    'influencer at once.',
  'recon.consultando': 'Querying…',
  'recon.recuperar': 'Recover links',
  'recon.resumen':
    '{encontradas} posts on the account · {vinculadas} linked · {corregidas} corrected',
  'recon.corregido': '↻ link corrected: {tema}',
  'recon.yaNoEsta': 'No longer on the account: {tema}',
  'recon.sinEmparejar': 'Unmatched: {tema}',
  'recon.noSePudoLeer': 'They could not be read.',

  'met.titulo': 'Metrics',
  'met.seguidores': 'Followers',
  'met.siguiendo': 'Following',
  'met.publicaciones': 'Posts',
  'met.ultimosDias': 'Last {n} days',
  'met.visualizaciones': 'Views',
  'met.alcance': 'Reach',
  'met.interacciones': 'Interactions',
  'met.cuentasAlcanzadas': 'Accounts reached',
  'met.sinEstadisticas':
    'Instagram does not return statistics for this account yet. That is common until there are ' +
    'posts with reach.',
  'met.leido': 'Read {cuando}',

  'plat2.usuario': 'Handle',
  'plat2.loConfirmaFanvue': 'Fanvue confirms it when the profile is linked',
  'plat2.sinCrear': 'Not created',
  'plat2.estado': 'Status',
  'plat2.correo': 'Email',
  'plat2.llegaA': 'Messages are delivered to',
  'plat2.administracion': 'Management',
  'tg.escribir': 'Write the channel description',
  'tg.otraVez': 'Write another',
  'tg.escribiendo': 'Writing…',
  'tg.escribirQue':
    'The model writes it with her name and country, in a bold tone, and applies it to the ' +
    'channel. 255 characters, which is the Telegram limit. You can generate it again.',
  'tg.noSePudo': 'Could not write the description.',
  'multi.titulo': 'Publish to several at once',
  'multi.explica':
    'It posts to the ones you tick, one after another. Ones that already went out are not listed.',
  'multi.conStars': 'charging {{stars}} Stars',
  'multi.gratis': 'free',
  'multi.regalar': 'Give it away on Telegram',
  'multi.publicarEn': 'Publish to {{n}}',
  'multi.seVanA': 'This really posts to {{lista}}.',
  'multi.publicando': 'Publishing {{hechas}} of {{total}}…',
  'multi.salieron': 'Posted to {{n}}.',
  'multi.salieronConManual': 'Posted to {{n}}, and {{manual}} came back as a manual package.',
  'multi.noSalieron': 'Posted to {{n}}, and {{fallos}} did not go out.',
  'multi.elegiUna': 'Tick at least one.',
  'tg.fijar': 'Write the welcome message',
  'tg.fijarOtra': 'Write another',
  'tg.fijarQue':
    'It posts it to the channel and pins it at the top, which is what people see when they open ' +
    'it. A channel cannot greet a new subscriber: a pinned message is the closest thing there is.',
  'tg.noSeFijo': 'Could not write the message.',
  'tg.sinAnclar':
    'The message was posted but could not be pinned: the bot lacks the pin permission, which is ' +
    'separate from posting. You can pin it by hand from Telegram.',
  'plat2.publicaCon': 'Posts through',
  'plat2.botCompartido': 'The shared bot, from Credentials',
  'plat2.conexionOauth': 'OAuth connection',
  'plat2.conexionGeneral': 'The manager’s shared connection',
  'plat2.conScopes': '{estado} ({n} scopes)',
  'plat2.sinConectar': 'Not connected',
  'plat2.usuariosSugeridos': 'Suggested handles',
  'plat2.disponibilidad': 'Availability is not checked: verify it when creating the account.',
  'plat2.buscandoPaginas': 'Looking for your Pages…',
  'plat2.cualPagina': 'Which Page belongs to this influencer?',
  'plat2.cualPaginaQue':
    'Your profile administers several. The one you pick is tied to her: content will be ' +
    'published there.',
  'plat2.conectadaA': 'Connected to “{pagina}”.',
  'plat2.noSePudoConectar': 'It could not be connected.',
  'plat2.conectando': 'Connecting…',
  'plat2.consultandoFanvue': 'Querying Fanvue…',
  'plat2.primeroManagerAntes': 'First connect the manager account once, from ',
  'plat2.perfilAsignado': 'Fanvue profile assigned.',
  'plat2.noSePudoAsignar': 'The profile could not be assigned.',
  'plat2.perfilDesasignado': 'Fanvue profile unassigned.',
  'plat2.noSePudoDesasignar': 'The profile could not be unassigned.',
  'plat2.administradaPor': '@{handle} · managed through the shared connection',
  'plat2.quitando': 'Removing…',
  'plat2.desasignar': 'Unassign',
  'plat2.elegiCual': 'Pick which of the profiles your manager administers is this influencer.',
  'plat2.elegirPerfil': 'Pick a profile…',
  'plat2.asignando': 'Assigning…',
  'plat2.asignar': 'Assign',
  'plat2.sinCreadoras':
    'Your manager does not administer any creator yet. Create her Fanvue account with the ' +
    'mailbox above, and from THAT account invite the manager under Connect Management.',
  'plat2.todasAsignadas':
    'Every creator your manager administers is already assigned to another influencer. Create a ' +
    'new Fanvue account and invite the manager from it.',
  'plat2.confirmarCasilla':
    'The address assigned is PERMANENT: Fanvue does not allow changing it later. Assign it now?',
  'plat2.casillaAsignada': 'Mailbox assigned: {direccion}',
  'plat2.asignarCasilla': 'Assign a dedicated mailbox',
  'plat2.casillaQueAntes': 'Fanvue requires a real, distinct address per account, and ',
  'plat2.rechazaAlias': 'it rejects “+” aliases',
  'plat2.casillaQueMedio':
    ', so the one inherited from Instagram does not work here. This one lives on the panel’s own ' +
    'domain, its mail is read under “Mail received”, and ',
  'plat2.noTocaOtras': 'it does not touch the address used on the other platforms',

  'correo.suDireccion': 'Her address',
  'correo.todaviaNoTiene': 'She does not have one yet',
  'correo.cambiarlaPor': 'Change it to',
  'correo.nombre': 'name',
  'correo.cambiando': 'Changing…',
  'correo.siCambiarla': 'Yes, change it',
  'correo.armadoQue':
    'The previous address stops existing: anything sent to it will bounce. If a sign-up is half ' +
    'done, its confirmation link is lost.',
  'correo.enReposoQue':
    'It is used on all her platforms. The mail she already received is not lost when it changes.',
  'correo.ahoraSuDireccion': 'Her address is now {direccion}.',
  'correo.noSePudoCambiar': 'The address could not be changed.',
  'correo.recibido': 'Mail received · {n}',
  'correo.seConserva':
    'It is kept for thirty days. What these messages are needed for —a confirmation link— is ' +
    'good on the first day.',
  'correo.nadaAun': 'Nothing has arrived yet',
  'correo.nadaAunQue':
    'This is where the confirmations and codes sent to her address appear. There is no mailbox ' +
    'to open.',
  'correo.llegaronAntes': '{n} messages arrived in her mailbox. They are under the tab ',

  'oauth.noSePudoDesconectar': 'It could not be disconnected.',
  'oauth.conectada': 'Account connected. You can publish from here now.',
  'oauth.cancelada': 'The authorisation was cancelled. Nothing changed.',
  'oauth.sinPermiso':
    'Connected, but without permission to publish. {permiso} still has to be added in the Meta ' +
    'app, and then reconnect.',
  'oauth.otro': '{plataforma}: {resultado}',
  'oauth.sinPermisoAviso':
    'Connected without publishing permission: content will come out as a manual package.',
  'oauth.desconectar': 'Disconnect {plataforma}',
  'oauth.conectar': 'Connect {plataforma}',

  'clave.titulo': 'Password',
  'clave.ocultar': 'Hide',
  'clave.mostrar': 'Show',
  'clave.borrar': 'Delete',
  'clave.guardada': 'Saved {cuando}',
  'clave.laQueUsaste': 'The one you used when creating the account',
  'clave.corregir': 'Correct what I entered',
  'clave.direccionDeCorreo': 'Email address',
  'clave.identificadorPagina': 'Page identifier',
  'clave.nombreDeUsuario': 'Username',
  'clave.passwordNueva': 'New password',
  'clave.dejaloVacio': 'Leave it empty to keep the current one',

  'bio.creadoraDe': '{quien} is a digital content creator',
  'bio.sobre': ' covering {nichos}',
  'bio.desde': ', based in {lugar}',

  'datos.nombrePublico': 'Public name',
  'datos.usuarioSugerido': 'Suggested handle',
  'datos.sinSugerencias': 'No suggestions could be derived from the public name.',
  'datos.categoria': 'Category / niche',
  'datos.bioSugerida': 'Suggested bio ({n} characters)',
  'datos.sitioWeb': 'Page website (this one does become a link)',
  'datos.fotoDePerfil': 'Profile photo',
  'datos.fotoDePortada': 'Cover photo ({ancho}×{alto})',
  'datos.aliasSobreCasilla':
    'An alias on your main mailbox. There is nothing to create: the mail arrives in your inbox.',
  'datos.fanvueNecesitaPropia':
    'Fanvue rejects “+” aliases, so it needs an address of its own. It is assigned from this ' +
    'influencer’s Fanvue section.',
  'datos.sinCasilla':
    'There is no mailbox yet. Configure a main mailbox or your own domain under Credentials —and ' +
    'the address is generated automatically for every influencer— or complete “Create the email ' +
    'account” to enter one by hand.',
  'datos.disclosure': 'Disclosure for the bio',
  'datos.nombreParaLaCuenta': 'Name for the account',
  'datos.direccionesSugeridas': 'Suggested addresses (try them in order)',
  'datos.sinDirecciones': 'The public name yields no valid Gmail address.',
  'datos.categoriaSugerida': 'Suggested category',
  'datos.correoDeLaCuenta': 'Account email',
  'datos.sinCasillaPropia':
    'She has no dedicated mailbox yet. Assign one with the button below: the address shown under ' +
    'Platforms is a “+” alias inherited from Instagram, and Fanvue rejects it because it ' +
    'collapses it to the base address.',
  'datos.deQuien': '{quien}’s details',
  'datos.abrirImagen': 'Open the image in a new tab',
  'datos.portadaGenerada': 'Cover generated. It is under “Cover photo”, below.',
  'datos.noSePudoGenerar': 'It could not be generated.',
  'datos.generandoPortada': 'Generating cover…',
  'datos.generarPortada': '🖼 Generate cover photo',
  'datos.portadaPorQue':
    'Landscape, with the figure pushed to the right, because Facebook crops the cover to 851×315 ' +
    'and covers its bottom-left corner with the profile photo. No shot in the system works: they ' +
    'are all vertical. It uses the stage-3 model —the same one as content— that you pick under ' +
    'Usage: it is the one that accepts references and chooses an aspect ratio, and without both ' +
    'the cover comes out with a different face or a different shape.',
  'datos.bannerGenerado': 'Banner generated. It is right below.',
  'datos.generandoBanner': 'Generating banner…',
  'datos.generarBanner': '🛏 Generate Fanvue banner',
  'datos.bannerPorQue':
    'Lying on a bed, at 1904×536 for the size Fanvue asks for (1192×335). Lying down because a ' +
    'horizontal body is the only thing that fills a strip three and a half times wider than it ' +
    'is tall; standing leaves one column and two thirds of wall. It comes out one step below her ' +
    'skin level —lingerie at most— because the banner is visible without a subscription and ' +
    'Fanvue requires it to be safe. The bottom-left corner is left clear for the profile photo.',
  'datos.noSePudoGuardarTarea': 'The task could not be saved.',
  'datos.passwordOpcional': 'Password',
  'datos.opcional': '(optional)',
  'datos.laQuePusiste': 'The one you set when creating the account',
  'datos.seGuardaCifrada':
    'It is stored encrypted. Losing the password of an already-verified account means losing the ' +
    'account; you can look it up later under Platforms.',

  'proxy.titulo': 'Public page',
  'proxy.que':
    'An address of her own on this domain, with her photo and all her networks. It goes in each ' +
    'network’s bio: Instagram allows one link and X allows another, so without this you have to ' +
    'choose which destination to give up — and change it by hand every time a new account appears.',
  'proxy.faltaCanonica':
    'Her canonical front shot still has to be picked in the gallery. It is the photo the page ' +
    'shows, and without it there is just a name on a dark background.',
  'proxy.abrir': 'Open',
  'proxy.generando': 'Generating…',
  'proxy.generar': 'Generate proxy page',
  'proxy.publicada': 'Page published.',
  'proxy.despublicada': 'Page unpublished: it now answers 404.',
  'proxy.noSePudo': 'The page could not be changed.',

  'tareas.sinTareas': 'No tasks',
  'tareas.sinTareasQue': 'There are no human actions pending.',
  'tareas.faltaCuenta': 'create your creator account',
  'tareas.faltaKyc': 'verify your identity',
  'tareas.faltaY': ' and ',
  'tareas.antesDeFanvue': 'Before the Fanvue steps',
  'tareas.antesDeFanvueMedio': ', you still have to {que} on Fanvue. It is done ',
  'tareas.unaSolaVez': 'once',
  'tareas.antesDeFanvueDespues':
    ' and covers all your influencers: without it, her linked profile cannot be created. ',
  'tareas.estaEnCredenciales': 'It is under Credentials.',
  'tareas.faltaAlgunPaso':
    'Is a step missing? The ones already there update on their own; this adds any that have been ' +
    'introduced since the influencer was generated.',
  'tareas.faltaCorreo':
    'The email account still has to be created. Every platform asks for one at sign-up.',
  'tareas.grupoCorreo': 'Email',
  'tareas.grupoInterno': 'Internal review',
  'tareas.faltaPrincipal':
    'Which Fanvue account is the main one has still to be decided. It determines whether this one ' +
    'is registered with an identity document or as a linked account, which are two different ' +
    'processes.',
  'tareas.faltaRetiro':
    'The main account has not completed a withdrawal yet, and Fanvue does not enable linked ' +
    'accounts until then. Her sign-up unblocks itself when that step is marked done.',

  'principal.correoRegistrada': 'Email it was registered with',
  'principal.elQueUsaste': 'the one you used on Fanvue',
  'principal.correoQue':
    'It may not be the panel’s mailbox: the main account is usually registered with an address of ' +
    'your own, and often already existed. It is the one that recovers the account.',
  'principal.laPrincipal': 'the main one',
  'principal.ahoraEs': '{quien} is now the main account.',
  'principal.sinPrincipal': 'No main account.',
  'principal.antesDeEmpezar': 'Before you start: ',
  'principal.cualEs': 'which of your influencers is the main Fanvue account?',
  'principal.cualEsQue':
    ' She is the one carrying your identity verification. There is exactly one, and the others are ' +
    'created hanging off her. The panel cannot work it out on its own: the KYC lives on Fanvue.',
  'principal.es': 'It is {quien}',
  'principal.comoSeCrea': 'How her account is created',
  'principal.altaNormal': 'Through normal sign-up: she is your main account.',
  'principal.altaNormalAntes':
    'Use “Open the platform”, signed out or in a private window. You can register her with the ' +
    'panel’s mailbox or with an address of your own — the verification arrives wherever you put it. ',
  'principal.llevaKyc': 'This one does carry identity verification',
  'principal.altaNormalDespues':
    ': Fanvue verifies the person who gets paid, and that verification has to live on some ' +
    'account. The other influencers hang off this one.',
  'principal.vinculada': 'As a linked account, no documents.',
  'principal.vinculadaAntes': 'Sign in as ',
  'principal.vinculadaMedio1':
    ' and go to Settings → Account → Linked accounts → “Create linked account”. Choose type ',
  'principal.tipoIa': 'AI',
  'principal.vinculadaMedio2':
    ' and enter the address from “Account email”, below — copy it rather than typing it, because ' +
    'it is permanent. The sign-up link arrives in that mailbox and is read under “Mail received”, ' +
    'on this same screen. ',
  'principal.noUsesAltaNormal': 'Do not use normal sign-up',
  'principal.vinculadaMedio3':
    ': it would ask for documents again. Fanvue does not enable linking until the main account has ' +
    'completed ',
  'principal.unRetiro': 'a withdrawal',
  'principal.esOtra': 'No, the main one is someone else',
  'principal.enRealidadEs': 'Actually the main one is {quien}',

  'tarea.faltaPasoPrevio': 'An earlier step is missing',
  'tarea.yaLoCompleté': 'I have done this',
  'tarea.revalidar': 'Re-check',
  'tarea.verInstrucciones': 'Read the instructions for this step',
  'tarea.queHacer': 'What to do',
  'tarea.aliasSobreCasilla': 'Alias on your main mailbox',
  'tarea.direccionCreada': 'The address you ended up creating',
  'tarea.usarEstaDireccion': 'Use this address',
  'tarea.guardarYCompletar': 'Save and complete',
  'tarea.nadaQueCrear':
    'There is nothing to create: mail sent to this address arrives in your inbox.',
  'tarea.seGuardaParaTodas':
    'It is stored for all of this influencer’s platforms. If you configure a main mailbox under ' +
    'Credentials, this step resolves itself.',
  'tarea.usuarioEnFanvue': 'The handle you ended up with on Fanvue',
  'tarea.usuarioEnFanvueQue':
    'The handle is the one on the public profile, without the @. The password is the one for HER ' +
    'Fanvue account, not the manager’s and not yours: the next step asks you to sign in with it ' +
    'to invite the manager.',
  'tarea.identificadorPagina': 'Page identifier',
  'tarea.identificadorPaginaQue':
    'How to get it: go to Platforms and press “Connect Facebook”. It will fail on purpose and ' +
    'list your Pages with their identifiers; copy this one’s, paste it here and connect again. It ' +
    'is NOT the number in “profile.php?id=”: on new Pages those are two different things, and only ' +
    'Meta knows the one the API uses.',
  'tarea.usuarioRegistradoEn': 'The handle registered on {plataforma}',
  'tarea.usuarioRegistradoQue':
    'Enter the one you actually got, not the suggested one: availability is not checked and most ' +
    'likely you had to use a different one.',
  'tarea.porQueCerrado': '▸ Why this is needed, and what never to share',
  'tarea.porQueAbierto': '▾ Why this is needed',
  'tarea.queDatosUsar': 'What data to use',
  'tarea.queNoCompartir': 'What NEVER to share',
  'tarea.hecho': '✓ Done',
  'tarea.marcadaCompletada': 'Marked as completed',

  'wf.sinCorridas': 'No runs',
  'wf.sinCorridasQue': 'No workflows recorded.',
  'wf.corrida': 'Run {id}',
  'wf.creditos': '{n} credits',

  'uso.candidatas': 'Front candidates',
  'uso.candidatasQue': 'a batch is generated and one is chosen; the rest are discarded',
  'uso.hoja': 'Identity sheet',
  'uso.hojaQue': 'the angles that anchor the face in everything else',
  'uso.tomas': 'Content shots',
  'uso.tomasQue': 'the ones seen under Content, discarded ones included',
  'uso.titulo': 'This influencer’s spending',
  'uso.creditosEn': 'estimated credits across {n} images, counting every stage',
  'uso.imagenes': '{n} images',
  'uso.otras': 'Other',
  'uso.publicacionesExternas': 'External posts',
  'uso.nadaPublicado': 'Nothing has been published yet.',
  'uso.conMetricas': 'with metrics',
  'uso.sinMetricas': 'no metrics data',

  'audit.titulo': 'Audit log',
  'audit.sinEventos': 'No events.',

  'errcre.noSePudoRetomar': 'It could not be resumed.',
  'errcre.descartar': 'Dismiss this notice',
  'errcre.irAConsumo': 'Go to Usage',
  'errcre.retomando': 'Resuming…',
  'errcre.yaLoHabilite': 'I have enabled it, continue',
  'errcre.gastoAntes': 'The profile is fine: what is blocking is ',
  'errcre.modoCero': 'USD 0 mode',
  'errcre.gastoDespues':
    ', which is on by default so nobody spends by accident. This skin level generates with a ' +
    'provider that charges. Turn it off under Usage, or pick a level that uses the free provider.',
  'errcre.reintentarAhora': 'Retry now',
  'errcre.cuotaQue':
    'The day’s budget has run out. There is nothing to fix: it resumes on its own when the quota ' +
    'resets, or you can force it if you have already raised the cap.',
  'errcre.corregirFicha': 'Fix the profile',
  'errcre.yaLaCorregi': 'I have fixed it, continue',
  'errcre.politicaQue':
    'Retrying does not fix this: the text the policy rejected has to change. Saving the profile ' +
    'validates it.',
  'errcre.reintentar': 'Retry',
  'errcre.reintentable':
    'It resumes with the same profile. What was already generated is not paid for again.',
  'errcre.noReintentable': 'Retrying may not be enough: read the message above before insisting.',

  'cred.fanvueConectado': 'Fanvue connected.',
  'cred.sinPermisoPublicar':
    'It connected, but the publishing permission is missing. Reconnect and accept everything.',
  'cred.fanvueRechazo': 'Fanvue rejected the connection: {motivo}',
  'cred.sinDetalle': 'no detail',
  'cred.correoManagerGuardado': 'Manager email saved.',
  'cred.noSePudoGuardar': 'It could not be saved.',
  'cred.pasoCerradoUna': 'Done. The step was closed on {n} influencer.',
  'cred.pasoCerradoVarias': 'Done. The step was closed on {n} influencers.',
  'cred.listo': 'Done.',
  'cred.tuCuentaDeCreador': 'Your creator account',
  'cred.tuCuentaDeCreadorQue':
    'Create it with YOUR real details as the owning person. The influencers come later, as linked ' +
    'profiles inside it — they are not separate accounts.',
  'cred.verificacion': 'Identity verification',
  'cred.verificacionQue':
    'Document and selfie, inside Fanvue. It takes a few working days. It is required even if your ' +
    'face never appears in the content: it verifies whoever gets paid, not the character.',
  'cred.dosPasosAntes': 'These two steps are done ',
  'cred.unaSolaVez': 'once',
  'cred.dosPasosDespues':
    ' and cover all your influencers. They happen inside Fanvue: here you only record that they ' +
    'are done.',
  'cred.hecho': 'Done',
  'cred.yaLoHice': 'I have done it',
  'cred.conexionDeAgencia': 'Agency connection',
  'cred.agenciaConectada':
    'Connected. {n} profile(s) assigned; a single authorisation publishes for all of them.',
  'cred.agenciaSinPermiso':
    'Connected, but without publishing permission. Before reconnecting, check that the Fanvue app ' +
    'declares read:media in its dashboard: a permission the app does not declare makes the ' +
    'authorisation fail with invalid_scope, even when we ask for it.',
  'cred.agenciaSinConectar':
    'Authorise once with your manager account and it covers every influencer that has invited it.',
  'cred.volverAConectar': 'Reconnect',
  'cred.conectarFanvue': 'Connect Fanvue',
  'cred.tresCuentas': 'You will be using three different Fanvue accounts',
  'cred.cuentaPrincipal': 'Main creator account',
  'cred.cuentaPrincipalQue':
    ' — the one carrying your identity verification. It is also your first influencer, and the ' +
    'others hang off it as linked accounts.',
  'cred.manager': 'Manager',
  'cred.managerQue': ' — the one that authorises this app and publishes for all of them.',
  'cred.deCadaInfluencer': 'Each influencer’s own',
  'cred.deCadaInfluencerQue': ' — only to invite the manager.',
  'cred.cadaPasoDiceAntes':
    'Each step says which one you need to be in. Getting it wrong does not fail on the spot: it ' +
    'leaves everything tied to the wrong account, and a Fanvue account’s email ',
  'cred.noSeCambiaNunca': 'can never be changed',
  'cred.sesionPanel': 'In the panel',
  'cred.sesionPrincipal': 'Main account',
  'cred.sesionManager': 'Manager account',
  'cred.sesionInfluencer': 'Each influencer’s account',
  'cred.paso1Antes': 'Press ',
  'cred.paso1Boton': '“Assign a dedicated mailbox”',
  'cred.paso1Medio':
    ', below. The panel gives you a real address on its own domain, and the mail it receives is ' +
    'read on this very screen.',
  'cred.paso1Segunda': 'It is needed because Fanvue requires an address ',
  'cred.nuncaRegistrado': 'never registered before',
  'cred.paso1Fin': ' and rejects “+” aliases.',
  'cred.paso2Antes': 'Go to ',
  'cred.paso2Medio': ' and press ',
  'cred.registrarseManager': '“Sign up as a Manager”',
  'cred.paso2Fin': ', with the mailbox from the previous step.',
  'cred.paso2SiTeLleva': 'If it takes you to the sign-in screen, ',
  'cred.iniciaSesion': 'sign in',
  'cred.paso2Devuelve': ': it brings you back on its own. ',
  'cred.noCreesCuenta': 'Do not create an account there',
  'cred.paso2Fin2': ' — that screen is for signing in.',
  'cred.paso3': 'Verify the account from the link that arrives in the panel, and come back.',
  'cred.paso4Antes': 'Sign out and sign in with ',
  'cred.deElla': 'her',
  'cred.paso4Medio': ' account. In Connect Management, under “Connect a manager”, paste ',
  'cred.correoDelManagerFuerte': 'the manager’s email',
  'cred.paso4Fin': ' — not hers.',
  'cred.paso4Segunda':
    'You are inside her account and the address that goes in is the other one: this is the step ' +
    'that goes wrong most often.',
  'cred.paso5': 'Accept the invitation it received.',
  'cred.paso6Antes': 'Come back here and press ',
  'cred.paso6En': ', under ',
  'cred.paso6Seccion': '“2. Manager account and profiles”',
  'cred.paso6Medio':
    '. It will ask you to sign in even if you have a session: enter the credentials ',
  'cred.delManager': 'of the manager',
  'cred.paso6Fin': ', not those of your main account or of the influencer.',
  'cred.paso6Privada': 'Do it in a ',
  'cred.ventanaPrivada': 'private window',
  'cred.paso6PrivadaFin':
    ', or sign out of Fanvue first: if the influencer’s session from the previous step is still ' +
    'open, it authorises that account and not the manager.',
  'cred.correoDelManager': 'Manager email',
  'cred.casillaAsignada': 'Mailbox assigned: {direccion}',
  'cred.asignando': 'Assigning…',
  'cred.asignarCasilla': 'Assign a dedicated mailbox',

  'kind.cf.title': 'Cloudflare accounts',
  'kind.cf.purpose':
    'Generates the images with Workers AI. The free quota is per account: 10,000 neurons a day ' +
    'each, resetting at 00:00 UTC.',
  'kind.cf.console': 'Cloudflare API tokens',
  'kind.cf.p1': 'Open the link and press “Create Token” → “Create Custom Token”.',
  'kind.cf.p2':
    'Under “Permissions” add one row per permission, all of type Account (see the table below). ' +
    'If you only want to generate images, the first one is enough.',
  'kind.cf.p3': 'Under “Account Resources” pick the account you want to use.',
  'kind.cf.p4': 'Copy the token and paste it below. It is shown only once.',
  'kind.cf.p5':
    'The account identifier is in the sidebar of the Cloudflare dashboard, under “Account ID”.',
  'kind.cf.permAi': 'Generating images and text. The only indispensable one.',
  'kind.cf.permWorkers': 'Deploying. Not needed to generate.',
  'kind.cf.permD1': 'The production database.',
  'kind.cf.permR2': 'The images, the cache and the quota-exhausted signal.',
  'kind.cf.edit':
    'If the token already exists and is only missing permissions, there is no need to create ' +
    'another: on the token’s row, three-dot menu → “Edit” → add the missing ones → “Update ' +
    'token”. Editing does not change the value, so it does not have to be pasted here again. ' +
    '“Roll”, on the other hand, does generate a new one and it has to be updated.',
  'kind.cf.metaLabel': 'Account identifier',
  'kind.cf.metaHint': '32 characters. It is in the sidebar of the Cloudflare dashboard.',
  'kind.cf.secret': 'API token',

  'kind.dominio.title': 'Your own domain for the mail',
  'kind.dominio.purpose':
    'The most reliable option: each influencer gets name@yourdomain.com and everything is ' +
    'forwarded to your mailbox. Unlimited addresses, no “+” and no phone number. It works on ' +
    'every platform because the address is indistinguishable from any other — there is nothing ' +
    'for them to reject. It wins over the main mailbox when both are set.',
  'kind.dominio.console': 'Cloudflare dashboard',
  'kind.dominio.p1':
    'You need a domain on Cloudflare. It costs about USD 10 a year; it is the only thing here ' +
    'that is not free.',
  'kind.dominio.p2': 'On the domain, go to “Email” → “Email Routing” and enable it.',
  'kind.dominio.p3':
    'Create a catch-all rule forwarding to your mailbox, so you do not have to create one rule ' +
    'per influencer.',
  'kind.dominio.p4': 'Come back here and enter the domain and the forwarding mailbox.',
  'kind.dominio.metaHint': 'On its own, with no https:// and no slashes.',
  'kind.dominio.secret': 'Forward the mail to',
  'kind.dominio.fold':
    'You already have a main mailbox: this is an optional improvement, not something missing.',
  'kind.dominio.edit':
    'Whether Email Routing is enabled is not checked from here: doing so would need a token with ' +
    'permission over the zone, different from the one the rest of the system uses. If it is not ' +
    'enabled the mail bounces, and that shows up on the first sign-up.',

  'kind.casilla.purpose':
    'The free alternative to your own domain: a single mailbox of yours that all the addresses ' +
    'come out of, in the form youraddress+name@gmail.com. Everything arrives in your inbox. With ' +
    'either of the two configured, the “Create the email account” step stops asking you to create ' +
    'anything: the address is generated automatically.',
  'kind.casilla.console': 'How “+” aliases work',
  'kind.casilla.p1': 'Enter your address as it is, with no label: the system adds its own.',
  'kind.casilla.p2':
    'Any provider that supports “+” works: Gmail, Outlook, Proton and most others.',
  'kind.casilla.p3':
    'There is nothing to create and no phone to verify. The mail arrives in whichever mailbox you ' +
    'enter.',
  'kind.casilla.secret': 'Your email address',
  'kind.casilla.fold': 'You already have your own domain, which wins over this: it is not needed.',
  'kind.casilla.edit':
    'No platform documents whether it accepts “+” addresses, so it cannot be known in advance: it ' +
    'is found out at sign-up. If one rejects it, the alternative without that risk is your own ' +
    'domain with Cloudflare Email Routing — set EMAIL_DOMAIN and EMAIL_FORWARD_TO and each ' +
    'influencer gets name@yourdomain.com, indistinguishable from any other address.',

  'kind.fanvue.purpose':
    'What makes publishing to Fanvue from here possible. A single app covers all your ' +
    'influencers: you authorise once with the manager and then assign their profiles from each ' +
    'influencer’s screen.',
  'kind.fanvue.console': 'Fanvue developer dashboard',
  'kind.fanvue.p1':
    'API access is limited to approved users: if you have not requested it yet, do so at the link ' +
    'above. No company or verification is required — the policy does not ask for a legal entity.',
  'kind.fanvue.p2':
    'You have to create the app yourself: Fanvue expressly forbids creating it on a third party’s ' +
    'behalf, because whoever registers it must operate it.',
  'kind.fanvue.p3':
    'Under “Authentication (OAuth)” you will find the Client ID and the Client Secret. The Client ' +
    'ID is not secret —it travels in the authorisation URL—; the secret is.',
  'kind.fanvue.p4':
    'Under “Redirects” add the URI shown below, exactly as it is. If you test in both ' +
    'environments, add both: production and test.',
  'kind.fanvue.p5':
    'Under “Define permissions” tick these nine: openid, offline_access, offline, write:post, ' +
    'write:media, read:post, read:creator, read:insights and read:agency. The first three are ' +
    'what make Fanvue return a refresh token: without them the connection lasts an hour and dies ' +
    'without warning. NOT the chat ones — the system does not answer messages, and asking for ' +
    'permissions that are not used slows reviews down.',
  'kind.fanvue.p6': 'Paste the Client ID and the secret below, and save.',
  'kind.fanvue.p7':
    'Fanvue tokens last an hour and renew themselves. Unlike Facebook, there is no token here ' +
    'that lasts forever.',
  'kind.fanvue.metaHint':
    'From “Authentication (OAuth)”. Not secret: it travels in the authorisation URL.',
  'kind.fanvue.secretPh': 'From “Authentication (OAuth)”. Do not share it over chat or email.',

  'kind.fb.title': 'Meta app — Facebook keys',
  'kind.fb.purpose':
    'What makes publishing to a Facebook Page possible. It can be the SAME Meta app as the ' +
    'Instagram one, but they are NOT the same keys: these come from Settings → Basic and those ' +
    'from the Instagram use case. Mixing them breaks sign-in, and the error does not say which of ' +
    'the two is wrong.',
  'kind.meta.console': 'Meta for Developers apps',
  'kind.fb.p1':
    'Before this, the influencer needs a Facebook PAGE created from your personal profile. Not an ' +
    'account of her own: the API does not publish to profiles. It is in her tasks.',
  'kind.fb.p2':
    'You can reuse the app you already created for Instagram. Open that app at the link above.',
  'kind.fb.p3':
    'In the left-hand menu: Settings → Basic. That is where the “App ID” and the “App secret” ' +
    'are. They are DIFFERENT values from the Instagram ones, even on the same app.',
  'kind.fb.p4':
    'Add the “Facebook Login” product if it is not there: left-hand menu → Add product.',
  'kind.fb.p5':
    'Under “Facebook Login” → Settings, add the redirect URI shown below, exactly as it is. It is ' +
    'different from the Instagram one and both have to be entered.',
  'kind.fb.p6': 'Paste the identifier and the secret below, and save.',
  'kind.fb.p7':
    'While the app stays unpublished, it works only with your own profile —the one with a role on ' +
    'the app— and with the Pages you administer. App Review is NOT needed for that: it is the ' +
    'same mechanism you already use on Instagram with the tester role. Review only becomes ' +
    'necessary to publish to third-party Pages.',
  'kind.fb.metaLabel': 'App ID',
  'kind.fb.metaHint': 'From Settings → Basic. NOT the “Instagram app ID”.',
  'kind.fb.secret': 'App secret',
  'kind.fb.secretPh': '32 characters, from Settings → Basic',

  'kind.ig.title': 'Meta app — Instagram keys',
  'kind.ig.purpose':
    'What makes publishing from here possible, instead of downloading the image and uploading it ' +
    'by hand. A single app covers all your influencers: while the app stays unpublished and each ' +
    'account has the tester role, publishing needs no App Review and no business verification.',
  'kind.ig.p1':
    'Before this, the influencer needs a professional Instagram account (Creator or Business). No ' +
    'Facebook Page is required. It is in her tasks.',
  'kind.ig.p2':
    'Open the link and press “Create app”. A five-step wizard opens: Details, Use cases, ' +
    'Business, Requirements and Overview.',
  'kind.ig.p3':
    'Under “Use cases” pick “Manage messages and content on Instagram”, the one with the ' +
    'Instagram icon. Do NOT pick “Other”: Meta is retiring it and it leads to the old experience.',
  'kind.ig.p4':
    'Under “Business” pick “I don’t want to connect a business portfolio yet”. It is for ' +
    'accessing third-party accounts and for publishing the app, and is not needed for your own. ' +
    'It can be added later.',
  'kind.ig.p5':
    'Finish the wizard. What follows happens inside the app dashboard, not in the wizard.',
  'kind.ig.p6':
    'In the dashboard, first row: “Customise the ‘Manage messages and content on Instagram’ use ' +
    'case”. Press the arrow on the right. With the new flow there is NO “Instagram” product in ' +
    'the left-hand menu: everything hangs off the use case.',
  'kind.ig.p7':
    '“Welcome to the Instagram API” opens. This is already the Instagram-login setup: if the top ' +
    'says “switch to API setup with Facebook login”, you are on the right one and there is ' +
    'nothing to change.',
  'kind.ig.p8':
    'At the very top are the “Instagram app ID” and the “Instagram app secret”. Those are the ' +
    'ones that go below. CAREFUL: they are NOT the ones under Settings → Basic; those belong to ' +
    'the Meta app and Instagram sign-in fails with them.',
  'kind.ig.p9':
    'A PERMISSION IS MISSING. The use case brings instagram_business_basic, manage_comments and ' +
    'manage_messages, but NOT instagram_business_content_publish, which is the one that allows ' +
    'publishing. Without it sign-in works and publishing fails.',
  'kind.ig.p10':
    'Add it from the “permissions and features page” link in point 1, and request ' +
    'instagram_business_content_publish. You can leave the two for messages and comments: we do ' +
    'not use them.',
  'kind.ig.p11':
    'Point 4, “Set up Instagram business login”: add the redirect URI shown below there, exactly ' +
    'as it is. Meta rejects any other, even one that differs by a trailing slash.',
  'kind.ig.p12': 'Paste the identifier and the secret below, and save.',
  'kind.ig.p13':
    'Once per influencer: under “App roles” add her Instagram account as a tester. She accepts ' +
    'the invitation from Instagram; that step is in her “Switch to a professional account” task, ' +
    'points 4 and 5, because it happens on the same Settings screen. While the app is ' +
    'unpublished, only accounts with that role can connect: the account being yours is not enough.',
  'kind.ig.metaLabel': 'Instagram app ID',
  'kind.ig.metaHint': 'Digits only, above “Welcome to the Instagram API”. Not the Meta one.',
  'kind.ig.secret': 'Instagram app secret',
  'kind.ig.secretPh': '32 characters, from that same screen',
  'kind.ig.edit':
    'The app stays unpublished and that is fine. Step 5 on that screen says review is needed “to ' +
    'access live data”: that is for accounts with no role on the app. The ones you added as ' +
    'testers work without review.\n\nThe check looks at the shape of the data, it does not ' +
    'verify it with Meta: Instagram login exposes no way to validate the pair without walking the ' +
    'whole flow, which needs an account authorising. The real test is the first connection.',

  'kind.runware.purpose':
    'Generates the adult content: the images and clips that can only be published on Fanvue, ' +
    'behind the paywall. It is billed per generation and has no free tier, so without this key ' +
    'the “Fanvue only” panel is visible but does not generate. If you are not going to publish ' +
    'that kind of content, it is not needed.',
  'kind.runware.console': 'Runware keys',
  'kind.runware.p1': 'Open the link and create the account.',
  'kind.runware.p2': 'Add credit: it bills per use, with no monthly fee and no minimum.',
  'kind.runware.p3': 'Press “Create Key”, give it a description and copy it.',
  'kind.runware.p4':
    'Paste it below. It is stored without checking: these providers have no free way to validate ' +
    'a key, and doing so would cost a generation.',
  'kind.runware.secret': 'API key',
  'stars.titulo': 'Telegram Stars',
  'stars.bajada':
    'How many Stars it costs to unlock an explicit photo in the channel. Telegram posts it ' +
    'blurred and reveals it to whoever pays.',
  'stars.imagenNsfw': 'Explicit image',
  'stars.referencia': '≈ USD {usd}, noted on {fecha}',
  'stars.losOtros':
    'The set and video prices already exist and are not used yet: the only path that publishes ' +
    'today is a single image.',
  'ventas.titulo': 'Sold on Telegram',
  'ventas.deEsta': '· {n} sales · {stars} ⭐',
  'ventas.avisoTitulo': 'Sales notifications',
  'ventas.avisoBajada':
    'Telegram collects the Stars on its own; to FIND OUT about each purchase it has to notify ' +
    'this address. Without it the channel still sells and nothing gets recorded. Note: ' +
    'registering this disconnects any other notification that bot had.',
  'ventas.avisoListo': 'Set up: Telegram reports every purchase.',
  'ventas.avisoFalta': 'Not set up: sales are not being recorded.',
  'ventas.avisoDiagnostico': '{pendientes} notifications queued. Last error: {error}',
  'ventas.avisoRegistrar': 'Register notifications',
  'ventas.avisoRehacer': 'Register them again',
  'ventas.avisoRegistrando': 'Registering…',
  'ventas.avisoRegistrado': 'Telegram will report every purchase.',
  'ventas.sinAtribuir': '{n} sales that could not be attributed to any influencer.',
  'kind.telegram.purpose':
    "The bot that posts to your influencers' channels. One bot for all of them: a bot can be an " +
    'administrator of as many channels as needed, and the message goes out with the identity of ' +
    'the channel, not the bot. The Telegram Stars balance sits in this bot account, pooled across ' +
    'all of them.',
  'kind.telegram.console': 'BotFather, on Telegram',
  'kind.telegram.p1': 'Open @BotFather on Telegram and send it /newbot.',
  'kind.telegram.p2': 'Give it a name and a username ending in «bot».',
  'kind.telegram.p3':
    'Copy the token it returns. If the bot already exists, ask for it with /token.',
  'kind.telegram.p4':
    'Then, on each influencer, add this bot as an administrator of her channel with permission ' +
    'to post. Without that the token works but cannot write anywhere.',
  'kind.telegram.guia': 'Step-by-step guide',
  'kind.telegram.secret': 'Bot token',
  'kind.civitai.purpose':
    'This is where your GPU gets its LoRAs: the realism, skin and style weights. Without a token, ' +
    'Civitai returns the sign-in page instead of the file, and the download lands as a ' +
    '.safetensors that ComfyUI rejects much later without saying why.',
  'kind.civitai.console': 'Civitai account',
  'kind.civitai.p1': 'Open the link while signed in to Civitai.',
  'kind.civitai.p2': 'Scroll down to “API Keys”, at the bottom of the page.',
  'kind.civitai.p3': 'Press “Add API key”, name it and copy it: it is shown only once.',
  'kind.civitai.p4': 'It only downloads weights. It posts nothing and acts for no one.',
  'kind.civitai.secret': 'API token',

  'cred.correoCopiado': 'Address copied.',
  'cred.passwordManager': 'Manager password',
  'cred.guardadaSufijo': ' · stored',
  'cred.escribiNueva': 'Type a new one to replace it',
  'cred.laDeTuCuenta': 'The one for your Fanvue account',
  'cred.passwordGuardada': 'Password stored and encrypted.',
  'cred.passwordQue':
    'It is stored encrypted with the environment key, like the influencers’ own. It is not ' +
    'checked: validating it would mean signing in from here, which is exactly what Fanvue treats ' +
    'as an abuse attempt.',
  'cred.correoRecibido': 'Mail received · {n}',
  'cred.quincePerfilesAntes': 'Fanvue allows up to ',
  'cred.quincePerfiles': '15 AI creator profiles',
  'cred.quincePerfilesDespues':
    ' per verified identity, and they unlock after the first payment: the first influencer has to ' +
    'be paid before the second can be created. ',
  'cred.abrirFanvue': 'Open Fanvue ↗',

  'cred.accesoAlPanel': 'Access to the panel',
  'cred.noSePudoCambiar': 'The password could not be changed.',
  'cred.passwordDeFabrica': 'The panel opens with the factory password',
  'cred.passwordDeFabricaQue':
    'It is written in the project’s Makefile, so it protects nothing. Below there are Cloudflare ' +
    'tokens, API keys and account passwords: change it before exposing this on the internet.',
  'cred.passwordCambiada': 'Password changed. Sessions already open remain valid.',
  'cred.esLoUnico': 'It is the only thing protecting everything on this screen.',
  'cred.passwordActual': 'Current password',
  'cred.passwordNueva': 'New password',
  'cred.minimo12': 'At least 12 characters. Stored with PBKDF2, never in the clear.',
  'cred.cambiando': 'Changing…',
  'cred.cambiarPassword': 'Change password',
  'cred.cambiarLaPassword': 'Change the password',

  'cred.titulo': 'Credentials',
  'cred.cargando': 'Loading credentials…',
  'cred.accionFallo': 'The action could not be completed.',
  'cred.bajadaAntes':
    'Everything is configured from here. You only have to visit the provider’s site to ',
  'cred.generar': 'generate',
  'cred.bajadaDespues':
    ' the key — each block explains exactly what to press and carries the direct link.',
  'cred.faltaClaveCifrado': 'Missing ',
  'cred.faltaClaveCifradoQue':
    'Without an encryption key no credential can be stored: they would be stored in the clear in ' +
    'the database, and that is not done. Generate one with ',
  'cred.faltaClaveCifradoO': ' or ',
  'cred.appYPermisos': '1. App and API permissions',
  'cred.porEntorno':
    'A key set through an environment variable is in use. Whatever you store here replaces it; ' +
    'deleting that goes back to using the environment one.',
  'cred.sinConfigurar': 'Not configured',
  'cred.sinConfigurarQue': 'The system cannot use {que} until you enter a credential.',
  'cred.configurar': 'Configure',
  'cred.agregarOtra': 'Add another account',
  'cred.reemplazarEnUso': 'Replace the one in use',
  'cred.reemplazarLa': 'Replace the credential',
  'cred.comoObtenerla': 'How to get it',
  'cred.uriRedireccion': 'Authorised redirect URI',
  'cred.permiso': 'Permission',
  'cred.paraQue': 'What for',
  'cred.etiqueta': 'Label',
  'cred.etiquetaQue': 'Short and stable. It is what separates each account’s quota under Usage.',
  'cred.etiquetaEjemplo': 'main',
  'cred.nombre': 'Name',
  'cred.nombreQue': 'Only to recognise it here.',
  'cred.nombreEjemplo': 'Personal account',
  'cred.secretoPublico': 'It is stored encrypted all the same, even though it is not a secret.',
  'cred.secretoPrivado': 'Stored encrypted. It is not shown again.',
  'cred.comprobando': 'Checking…',
  'cred.guardarYComprobar': 'Save and check',
  'cred.alGuardarSeComprueba':
    'Saving checks it against the provider’s API. The check runs no model, so it uses no quota.',
  'cred.imagenesConAntes': 'Images are generated with ',
  'cred.imagenesConDespues': '. Each account’s spending is tracked separately — see ',
  'cred.consumo': 'Usage',
  'cred.imagenesConFin': ' for the breakdown.',

  'cred.enUso': 'In use',
  'cred.guardadaSinUsar': 'Stored, unused',
  'cred.comprobada': 'Checked {cuando}',
  'cred.funcionaSinUsarAntes': 'It works, but it is not being used. Press ',
  'cred.usarEsta': 'Use this one',
  'cred.funcionaSinUsarDespues': ' so the system generates with it.',
  'cred.probando': 'Testing…',
  'cred.probar': 'Test',
  'cred.dejarDeUsar': 'Stop using',
  'cred.activando': 'Activating…',
  'cred.borrar': 'Delete',

  'cred.noSePudoMostrar': 'The credential could not be shown.',
  'cred.seTapaSola': 'It hides itself again in 30 seconds. It is recorded in the audit log.',
  'cred.ver': 'Show',
  'cred.tuPasswordDelPanel': 'Your panel password',
  'cred.verificando': 'Verifying…',
  'cred.mostrar': 'Show',

  'ext.titulo': 'Browser extension',
  'ext.queHaceAntes':
    'The extension shows your reference accounts alongside Instagram, navigates to whichever you ' +
    'pick and counts how many you followed. ',
  'ext.noSiguePorVos': 'It does not follow for you.',
  'ext.queHaceDespues':
    ' One token covers all your influencers: which one you are working is chosen inside the ' +
    'extension itself.',
  'ext.direccionDelPanel': 'The other thing the extension asks for: the panel’s address',
  'ext.direccionQueAntes': 'This is the one to use, even though the panel also answers on its ',
  'ext.direccionQueDespues':
    ' address: if the Worker is ever renamed, the extension keeps working.',
  'ext.paraQueSirve': 'What it is for',
  'ext.opcionExtension': 'Browser extension — working outreach',
  'ext.opcionMcp': 'MCP · read-only — letting an AI query the system',
  'ext.mcpQueAntes': 'An AI client will be able to ',
  'ext.leer': 'read',
  'ext.mcpQueMedio':
    ' your influencers, reference accounts, outreach and spending. It cannot generate, publish or ' +
    'change settings. It connects to ',
  'ext.mcpQueFin': ' with this token.',
  'ext.opcionMcpGeneracion': 'MCP · read and generate — letting an AI also start batches',
  'ext.mcpGenera':
    'On top of reading, this token lets an AI client START PHOTO BATCHES, which cost money and ' +
    'use up the daily quota. It cannot publish or change settings. Only give it to a client you ' +
    'run yourself.',
  'ext.mcpGeneracion': 'MCP · generates',
  'ext.copialoAhora': 'Copy it now: it is not shown again.',
  'ext.paraReconocerlo': 'To recognise it: “Chrome at home”',
  'ext.creando': 'Creating…',
  'ext.crearToken': 'Create token',
  'ext.sinNombre': 'unnamed',
  'ext.mcpLectura': 'MCP · read',
  'ext.extension': 'extension',
  'ext.usado': 'used {cuando}',
  'ext.sinUsar': 'unused',
  'ext.regenerando': 'Regenerating…',
  'ext.regenerar': 'Regenerate',
  'ext.revocar': 'Revoke',
  'ext.tokenRegenerado': 'Token regenerated. The previous one stopped working.',
  'ext.noSePudoRegenerar': 'The token could not be regenerated.',
  'ext.noSePudoCrear': 'The token could not be created.',

  'perfil.economyModelos':
    'FLUX.1 schnell in all three stages. It does not accept reference images.',
  'perfil.balancedModelos': 'FLUX.2 Klein 4B in all three stages',
  'perfil.premiumModelos': 'Nano Banana 2 Lite (gemini-3.1-flash-lite-image) in all three stages',
  'perfil.premiumOjo':
    'It needs billing enabled in Google AI Studio — the free tier rejects these models. And it ' +
    'rejects the sensual and daring registers: those need fal. The compatibility thresholds also ' +
    'have to be recalibrated: they came from measuring FLUX faces.',
  'perfil.seedreamModelos':
    'ByteDance’s Seedream 4.5 (fal-ai/bytedance/seedream/v4.5) in all three stages',
  'perfil.seedreamOjo':
    'The best measured for the sensual and daring registers. Against a real influencer’s identity ' +
    'centroid it scored 0.71-0.81 across all four shots — the highest range of everything tested— ' +
    'and it renders at 12 megapixels, so the face is never left with too few pixels. It bills per ' +
    'image rather than per GPU-second: no cold starts. Unlike FLUX it does not carry Black Forest ' +
    'Labs’ licence moderation.',
  'perfil.seedream5Modelos':
    'Seedream 5.0 [pro] in all three stages. Up to 2K and up to ten references.',
  'perfil.seedream5Ojo':
    'Twice the price of 4.5. Its path has not been exercised against the API yet.',

  'op.image': 'Image',
  'op.video': 'Video',
  'op.text': 'Text',
  'op.vision': 'Vision',
  'op.moderation': 'Moderation',

  'consumo.titulo': 'Usage and settings',
  'uso.gasto': 'Spending',
  'uso.esteMes': 'this month',
  'uso.deTope': ' · of {monto}',
  'uso.sinTope': ' · no cap',
  'uso.hoyConsumidos': 'today · {creditos} {unidad} used',
  'uso.sinTramoGratuitoAntes':
    'Without the Workers AI free tier, what was used this month would have cost ',
  'uso.sinTramoGratuitoMedio': '. Only what exceeds the daily 10,000 is billed, plus ',
  'uso.sinTramoGratuitoFin': ' from providers that bill from the first call.',
  'uso.loQueSeGasto': 'What was spent',
  'uso.soloParaMirar': 'For looking only: nothing here changes how the system works.',
  'uso.consumoDeHoy': 'Today’s usage',
  'uso.cuotaAgotada': 'Daily quota exhausted',
  'uso.reinicia': 'Resets {cuando}',
  'uso.cloudflare': 'Cloudflare',
  'uso.elProveedor': 'The provider',
  'uso.dejoDeAceptarAntes': ' stopped accepting requests. Our ledger had estimated ',
  'uso.dejoDeAceptarDe': ' of ',
  'uso.dejoDeAceptarFin': ', but the real billing was higher.',
  'uso.estimacionPrevia':
    'The upfront estimate is there to stop in time, not to keep exact accounts: a rejected ' +
    'generation consumes quota all the same, and the published rates do not cover the whole cost. ' +
    'When the provider answers, its figure beats ours.',
  'uso.creditosHoy': '{creditos} {unidad} today · {usd}',
  'uso.sinTopeDiario': 'No daily cap: nothing stops generation on budget grounds.',
  'uso.sinTopeDiarioAntes': 'You chose that below, under ',
  'uso.topeDeGastoDiario': 'Daily spending cap',
  'uso.sinTopeDiarioDespues':
    '. The brakes that are not about money still apply: the provider when it reports its quota ' +
    'exhausted, and the monthly cap if you set one.',
  'uso.estimados': '{usados} / {limite} estimated {unidad}',
  'uso.creditosSonAntes':
    'Credits are this system’s unit, not any provider’s: the bar adds up everything generated ' +
    'today — ',
  'uso.proveedorActivo': 'the active provider',
  'uso.creditosSonMedio': ' on the profile in use—. The cap of ',
  'uso.creditosSonFin': ' was set by you below, under ',
  'uso.primeras10k': ' The first 10,000 from Workers AI are free.',
  'uso.sinTramoGratuito':
    ' This profile does not touch Cloudflare’s free tier: it bills from the first image.',
  'uso.porProveedor': 'Usage by provider (7 days)',
  'uso.sinConsumo': 'No usage recorded.',
  'uso.paraQue': 'What for',
  'uso.creditos': 'Credits',
  'uso.llamadas': 'Calls',
  'uso.fallos': 'Failures',
  'uso.almacenamiento': 'Image storage',
  'uso.imagenesPunto': ' images · ',
  'uso.mbDe': ' MB of ',
  'uso.disponibles': ' available',
  'uso.enKv':
    'The images live in KV, not R2. Enabling R2 requires adding a card even though its free tier ' +
    'charges nothing, so it is not a requirement to deploy. KV is inside the Workers free plan ' +
    'and is enough for around 500 influencers.',
  'uso.siLlegasAlLimite':
    'If you hit the limit, delete influencers you no longer use from their own screen: their media ' +
    'goes too and the space comes back.',
  'uso.losFrenos': 'The brakes',
  'uso.losFrenosQue':
    'What keeps a mistake from costing money. Best touched rarely and on purpose.',

  'freno.modoCero': 'USD 0 mode',
  'freno.estadoAntes': 'Status: ',
  'freno.activo': 'on',
  'freno.desactivado': 'off',
  'freno.modoCeroQue':
    'With the mode on, no operation that could incur a charge runs. When the free quota is used ' +
    'up, the work is deferred to the reset instead of spending. Turning it off requires an ' +
    'explicit confirmation and never happens automatically.',
  'freno.confirmarDesactivar':
    'Turning off USD 0 mode allows operations that can incur real charges. Continue?',
  'freno.desactivarModoCero': 'Turn off USD 0 mode',
  'freno.confirmarVolver':
    'Back to free mode:\n\n· USD 0 mode is turned on\n· The quality profile switches to ' +
    '“balanced” (Cloudflare)\n· The daily cap returns to the free tier\n· The fal and Gemini ' +
    'credentials are DEACTIVATED\n\nThe keys are not deleted: they come back with one click ' +
    'from Credentials. From that moment there is no operation that can incur a charge. Continue?',
  'freno.volviendo': 'Going back…',
  'freno.volverAlModoGratis': 'Back to free mode',
  'freno.volverQueAntes':
    'It leaves the system as it came out of the box: only free Workers AI models, with no charge ' +
    'possible. It also deactivates the credentials for ',
  'freno.volverQueY': ' and ',
  'freno.volverQueDespues':
    ' —it does not delete them: they come back with one click— so the state is visible and you do ' +
    'not have to trust a switch on another screen. Influencers on the “sensual” or “daring” skin ' +
    'level stop being able to generate: that level has no free version.',
  'freno.topeDiarioQue':
    'This system applies it, across every provider. No provider offers a spending cut-off of its ' +
    'own, so it is the only brake there is: on reaching it, generation stops until the next day.',
  'freno.escribiEnDolares': 'Or type the daily cap in dollars',
  'freno.confirmarQuitarTope':
    'With no daily cap, nothing stops generation on budget grounds. This month you are at ' +
    'USD {gasto}. Remove the cap?',
  'freno.sinTopeActivo': 'No cap (active)',
  'freno.sinTope': 'No cap',
  'freno.sinTopeDiarioAntes': 'No daily cap. On the ',
  'freno.cadaInfluencerCuesta': ' profile each influencer costs ',
  'freno.yNadaLimita': ' — {usd} — and nothing limits how many are generated per day.',
  'freno.hoyAntes': 'Today: ',
  'freno.porDiaConPerfil': ' per day. On the ',
  'freno.entranPorDia': 'Room for {n} per day.',
  'freno.noAlcanza':
    'Not even enough for one: raise the cap or generation will be left half-finished.',
  'freno.topeMensual': 'Monthly cap',
  'freno.topeMensualQue':
    'The daily one guards against a bad day. This one guards against thirty normal days adding up ' +
    'to a bill nobody decided on.',
  'freno.sinTopeMinuscula': 'no cap',
  'freno.oEscribilo': 'or type it',
  'freno.sinNingunFreno':
    'No monthly cap and no daily cap: there is no spending brake left. The only thing that can ' +
    'stop generation is a provider reporting its quota exhausted.',
  'freno.sinTopeMensual':
    'No monthly cap: the only brake is the daily one, and thirty days straight at the maximum add ' +
    'up to ~USD {monto}.',
  'freno.herramientas': 'Tools',
  'freno.herramientasQue': 'Things you configure once and forget.',
  'freno.featureFlags': 'Feature flags',
  'freno.inactivo': 'inactive',
  'freno.puedeGenerarCargos': 'can incur charges',

  'ref.catModelos': 'Models',
  'ref.catMeme': 'Meme',
  'ref.catComic': 'Comic',
  'ref.catTodas': 'All',
  'ref.titulo': 'Leads',
  'ref.cargando': 'Loading accounts…',
  'ref.bajadaIg':
    'Other people’s accounts you follow for inspiration or comparison, sorted by followers.',
  'ref.bajadaFb':
    'Facebook Pages, groups and people to open and review. Links only: nobody is followed from ' +
    'here.',
  'ref.noSePudoAsignar': 'It could not be assigned.',
  'ref.noSePudoAnotar': 'The round could not be recorded.',
  'ref.noSePudoAgregar': 'The account could not be added.',
  'ref.agregarUna': 'Add an account',
  'ref.placeholderFb': 'https://www.facebook.com/… · a Page, a group, a profile',
  'ref.placeholderIg': 'https://www.instagram.com/handle/  ·  or @handle',
  'ref.nombreOpcional': 'Name (optional)',
  'ref.agregando': 'Adding…',
  'ref.agregar': 'Add',
  'ref.porUsuarioAntes': 'It is stored by handle, not by link: ',
  'ref.porUsuarioY': ' and ',
  'ref.porUsuarioFin': ' are the same account and are not duplicated.',
  'ref.seguidoresAMano': 'Follower counts are entered by hand.',
  'ref.seguidoresAManoQue':
    ' Reading them from someone else’s account has exactly one official route —Business ' +
    'Discovery— and it requires Facebook Login: a reviewed Meta app and a linked Page. This panel ' +
    'uses Instagram Login precisely so as not to require that. And scraping the profile does not ' +
    'work: Instagram answers datacentre IPs with a login wall, which is what we are.',
  'ref.cuentas': 'Accounts · {n}',
  'ref.filtrarPorPais': 'Filter by country…',
  'ref.filtrarPorPaisAria': 'Filter by country',
  'ref.quitarFiltro': 'Clear filter',
  'ref.ningunaAun': 'You have not added any yet.',
  'ref.editarPais': 'Click to edit the country',
  'ref.sinPais': '— country',
  'ref.editar': 'Click to edit',
  'ref.seg': 'flw.',
  'ref.quitar': 'Remove',
  'ref.cuantos': 'how many?',
  'ref.aCuantosSiguio': 'How many {quien} followed from @{cuenta}',
  'ref.anotarVuelta': 'Click to record a round by hand',
  'ref.quitarDe': 'Remove {quien} from @{cuenta}',
  'ref.asignarA': 'Assign @{cuenta} to an influencer',
  'ref.elegiInfluencer': 'Pick an influencer…',
  'ref.masAsignar': '+ Assign',

  'bien.titulo': 'Welcome',
  'bien.bajada':
    'Your account starts empty on purpose: what you generate is yours and nobody else sees it. ' +
    'This guide is what has to be done once.',
  'bien.queEs': 'What this system is',
  'bien.queEsAntes': 'It is for ',
  'bien.crearYOperar': 'creating and running virtual influencers',
  'bien.queEsDespues':
    ': fictional characters with a stable face, who publish photos and videos on social networks.',
  'bien.loDificilAntes':
    'The hard part is not generating good-looking images —any model does that— it is that ',
  'bien.mismaPersona': 'they are all of the same person',
  'bien.loDificilMedio':
    '. That is why creating an influencer is three stages with two pauses: first you pick her face ' +
    'from four candidates, then an “identity sheet” of three angles is generated for you to ' +
    'approve, and only then is content generated ',
  'bien.conEsasImagenes': 'with those images attached',
  'bien.loDificilFin': ' so the face does not drift.',
  'bien.paso1': '1 · Your Cloudflare account',
  'bien.paso1QueAntes':
    'The system generates with your account, not somebody else’s. It is free and enough to start: ' +
    'Cloudflare gives ',
  'bien.creditosPorDia': '10,000 AI credits per day',
  'bien.paso1QueDespues': ' at no charge.',
  'bien.creaCuentaEn': 'Create an account at ',
  'bien.noPideTarjeta': '. No card required.',
  'bien.entraA': 'Go to ',
  'bien.perfilTokens': 'Profile → API Tokens ↗',
  'bien.yCreaUnoAntes': ' and create one with the ',
  'bien.yCreaUnoDespues': ' permission. Copy it: it is shown only once.',
  'bien.copiaTambienAntes': 'Copy your ',
  'bien.idDeCuenta': 'account ID',
  'bien.copiaTambienDespues': ' too, from the sidebar of the Cloudflare dashboard.',
  'bien.pegaLosDos': 'Paste both under ',
  'bien.credencialesCloudflare': 'Credentials → Cloudflare account',
  'bien.paso2': '2 · The text, which is free too',
  'bien.paso2QueAntes':
    'The text —the character profile, the scenes, the captions— is written by a separate model. ' +
    'With a key from ',
  'bien.oGroq': ' or ',
  'bien.paso2QueDespues': ' it comes out considerably better, and both have free usage.',
  'bien.seSacaEn': 'You get it at ',
  'bien.ySePegaEn': ' and paste it under ',
  'bien.sinEstoIgual': '. It works without this, using Cloudflare’s free model.',
  'bien.paso3': '3 · Generate your first influencer',
  'bien.paso3QueAntes': 'Under ',
  'bien.influencersNueva': 'Influencers → New',
  'bien.paso3QueDespues':
    '. You pick her visual style —how her photos look, what clothes, what scenes— and the system ' +
    'does the rest. It will stop twice waiting for you: to pick her face, and to approve her ' +
    'identity sheet.',
  'bien.conLoGratuitoAntes':
    'On the free tier the images come out simpler, and sensual content and video cannot be ' +
    'generated: that needs a key from ',
  'bien.conLoGratuitoDespues':
    ', which is pay-per-use. When you want to take that step it is explained under Credentials.',
  'bien.antesDeGastar': 'Before spending',
  'bien.modoCeroAntes': '',
  'bien.modoCero': 'USD 0 mode',
  'bien.modoCeroDespues':
    ' is on by default: no operation that could incur a charge runs. When the free allowance is ' +
    'used up, the work is deferred to the next day instead of spending.',
  'bien.siLoDesactivasAntes': 'If you turn it off, set ',
  'bien.topeDiarioYMensual': 'a daily cap and a monthly one',
  'bien.siLoDesactivasDespues':
    ' under Usage and settings. No provider offers a spending cut-off of its own: that cap is the ' +
    'only brake there is.',
  'bien.videoCuestaMas': 'One video costs more than a whole influencer’s worth of images.',
  'bien.videoCuestaMasQue': ' It is not a pricier image: it is another order of spending.',
  'bien.manualCompleto': 'The full manual',
  'bien.manualCompletoQue':
    'This guide is the setup. If you are looking for what each screen does, how the networks ' +
    'connect, what each post status means or why something did not go out, it is all in the manual.',
  'bien.abrirManual': 'Open the manual ↗',
  'bien.listo': 'Done ✓',
  'bien.entendido': 'Got it, stop showing this',
  'bien.quedaEnElMenu': 'It stays in the menu in case you want to read it again.',

  'mod.perfilGratis': 'Free — Klein 4B, inside the Cloudflare quota',
  'mod.perfilPago': 'Paid — Seedream 4.5 via Runware',
  'mod.titulo': 'Models',
  'mod.bajada': 'What each thing is generated with. What was spent is under Usage.',
  'mod.faltaCredencial': ' — credential missing',
  'mod.publicableEnTodas': 'Publishable everywhere',
  'mod.publicableEnTodasQue': 'What goes out to Instagram, Facebook and the Fanvue profile.',
  'mod.texto': 'Text',
  'mod.modeloDeLenguaje': 'Language model',
  'mod.modeloDeLenguajeQue':
    'It writes the profiles, the captions and the content plan. Cloudflare’s are free inside the ' +
    'daily quota; Runware’s bill per million tokens. Picking a free one puts Cloudflare first in ' +
    'the cascade.',
  'mod.modeloDeLenguajeAdultos':
    'For Fanvue scenes and captions. A moderated model does not write them worse: it refuses. The local, unrestricted one is the right pick.',
  'mod.elMismoQueLoApto': 'Same as the safe one',
  'mod.recomendado': 'Recommended — {cual}',
  'mod.recomendadoSolo': 'Recommended',
  'mod.descripcionDeImagenes': 'Image captioning',
  'mod.modeloQueDescribe': 'The model that describes',
  'mod.modeloQueDescribeQue':
    'It writes the caption for each image in the LoRA training set. It bills per image and the ' +
    'result is cached: it only re-describes if the model changes.',
  'mod.perfilDeCalidad': 'Quality profile',
  'mod.perfilDeCalidadHoy': 'What is used when you pick nothing below. Today: {cual}.',
  'mod.perfilDeCalidadQue': 'What is used when you pick nothing below.',
  'mod.canonica': 'Canonical shot',
  'mod.canonicaQue':
    'The four faces you choose from. It is the only one that starts from a prompt and nothing ' +
    'else: this is where the person comes from.',
  'mod.elQueDigaElPerfil': 'Whatever the quality profile says',
  'mod.hojaDeIdentidad': 'Identity sheet',
  'mod.hojaDeIdentidadQue':
    'The three angles, generated with the canonical shot attached. Without that reference they ' +
    'would be three different people.',
  'mod.contenidoHastaPlaya': 'Content, up to beach',
  'mod.contenidoHastaPlayaQue':
    'Street, sportswear and beach. Any model handles these well, Cloudflare’s free ones included.',
  'mod.contenidoSensual': 'Sensual and daring content',
  'mod.contenidoSensualQue':
    'From here on a vendor filter gets in the way: it rejects the shot while charging for the ' +
    'attempt, or returns her dressed. An open-weights model is the way to go.',
  'mod.elMismoQueContenido': 'The same as the content one',
  'mod.video': 'Video',
  'mod.videoQue':
    'An already-approved shot is animated, from each influencer’s Content tab. The clip is born ' +
    'from an image that already went through the identity chain, so it is the same person. A clip ' +
    'costs more than a whole influencer’s worth of images: it is another order of spending.',
  'mod.hastaPlaya': 'Up to beach',
  'mod.hastaPlayaQue': 'Street, sportswear and beach. The same cut-off as for images.',
  'mod.sensualYAtrevido': 'Sensual and daring',
  'mod.sensualYAtrevidoQue':
    'Same criterion as for images: no third-party filter that could reject what was asked for.',
  'mod.elMismoQueArriba': 'The same as the one above',
  'mod.soloFanvue': 'Fanvue only',
  'mod.soloFanvueQue':
    'Adult material. It goes to the subscriber feed and the server prevents publishing it anywhere ' +
    'else.',
  'mod.imagenYVideo': 'Image and video',
  'mod.identidadPorMecanismo':
    'Identity is held by a mechanism in the model, not by the prompt: the ones without it generate ' +
    'fine and return somebody else.',
  'mod.porGeneracion': 'Billed per generation, with no free quota.',
  'mod.oPegaOtro': 'Or paste another model’s identifier',
  'mod.loraAdulto': 'Adult-content LoRA',
  'mod.loraAdultoQue':
    'It stacks on top of each influencer’s identity LoRA. It is needed when the base model does not ' +
    'generate nudity on its own, which is the case for the whole FLUX family.',
  'mod.disparadorEjemplo': 'Its trigger word, if it has one — e.g. aidmaNSWFunlock',
  'mod.arquitectura': 'Architecture',
  'mod.arquitecturaQue':
    'Which models it stacks on. Stacking it where it does not belong does not give a worse result: ' +
    'it does nothing, and costs the same.',
  'mod.cambioDeCara': 'Face swap, as a second step',
  'mod.cambioDeCaraQue':
    'Runware has no face swap: what exists are models that edit by instruction, given the generated ' +
    'image plus a photo of her and asked to replace it. The only thing that decides which one works ' +
    'is whether the vendor added a filter — with one, an explicit input is rejected or comes back ' +
    'untouched, and is billed all the same.',
  'mod.conQueSeCambia': 'What the face is swapped with',
  'mod.conQueSeCambiaQue':
    'Used only when generating with the “With face swap” button. It costs on top of the generation.',
  'mod.sinEditores': 'No editors available',
  'mod.porImagen': '{cual} · USD {usd} per image',
  'mod.conFiltro': ' · with the vendor’s filter',

  'nueva.sinServidor': 'Could not reach the server.',
  'nueva.topeNoAlcanza': 'The daily cap you set is not enough for a complete influencer.',
  'nueva.cuotaNoAlcanza': 'Today’s free quota is no longer enough for a complete influencer.',
  'nueva.conElPerfil': 'On the ',
  'nueva.cadaInfluencerCuesta': ' profile each influencer costs ',
  'nueva.yTuTopeEsta': ' and your cap is at ',
  'nueva.subiloEn': '. Raise it under ',
  'nueva.consumoYAjustes': 'Usage and settings',
  'nueva.oGeneraIgual':
    ' or generate anyway: whatever does not fit today waits in the queue and completes when the ' +
    'day resets.',
  'nueva.podesGenerarIgual':
    'You can generate her anyway: whatever does not fit today waits in the deferred queue and ' +
    'completes on its own when the quota resets.',
  'nueva.imagenesIniciales': 'Initial images: ',
  'nueva.unaSolaToma':
    'A single shot: the cheapest way to see how she comes out before asking for a batch.',
  'nueva.menosImagenes': 'Fewer images use less quota and allow creating more influencers today.',
  'nueva.estiloVisual': 'Visual style',
  'nueva.conModoCeroActivo': ' — with USD 0 mode on',
  'nueva.trajeDeBanio':
    'Swimwear only appears where the setting justifies it —beach, pool— and in at most one shot ' +
    'of the set.',
  'nueva.necesitaSinFiltroAntes': 'This level needs a ',
  'nueva.modeloSinFiltro': 'model with no vendor filter',
  'nueva.necesitaSinFiltroDespues':
    ': one with a filter rejects this register and charges for the attempt all the same.',
  'nueva.gpuPropiaAntes': 'You will generate it with ',
  'nueva.tuPropiaGpu': 'your own GPU',
  'nueva.gpuPropiaDespues':
    ', which is the ideal case for this register: it has nobody’s filter and costs no money, so ' +
    'USD 0 mode does not block it. All that is needed is for the machine to be switched on — if ' +
    'it is not, the shots fail with a connection error.',
  'nueva.runwareMedio1': ', which serves open-weights models with no third-party filter and ',
  'nueva.cobraPorImagen': 'bills per image',
  'nueva.runwareMedio2': '. That is why you need ',
  'nueva.desactivadoModoCero': 'USD 0 mode turned off',
  'nueva.runwareFin':
    ' under Usage: it is on by default, so if you have not touched it, generation stops there.',
  'nueva.sinGastarAntes':
    'If you want to generate it without spending, pick your own GPU as the content model under ',
  'nueva.todaviaNoPodes': 'With this account you cannot generate this level yet:',
  'nueva.modoCeroActivoEn': ' USD 0 mode is on. It is fixed under ',
  'nueva.oEligiendoGpu': ', or by choosing your own GPU under ',
  'nueva.rechazaEscenasAntes': 'The provider also rejects some scenes and ',
  'nueva.cadaRechazoSePaga': 'every rejection is billed',
  'nueva.rechazaEscenasDespues':
    '. A rejected shot no longer drags the rest of the set down: the others generate all the same ' +
    'and “Complete set” fills the gaps.',
  'nueva.dondeMasPasa': ' It is the register where this happens most.',
  'nueva.desnudezProhibida':
    'Full or partial nudity remains forbidden by the system’s policies for anything that goes out ' +
    'to social networks, regardless of what the model allows. Explicit material is generated on ' +
    'each influencer’s Fanvue tab, and only goes there.',
  'nueva.plataformas': 'Platforms',
  'nueva.plataformasQue': 'The sign-up tasks and the content variants for each one are generated.',
  'nueva.modoCeroEstaAntes': 'USD 0 mode is ',
  'nueva.desactivado': 'off',
  'nueva.activo': 'on',
  'nueva.modoCeroEstaDespues':
    ', and it is only changed from Settings, with an explicit confirmation.',
  'nueva.tonoDePiel': 'Skin tone',
  'nueva.colorDePelo': 'Hair colour',
  'nueva.texturaDePelo': 'Hair texture',
  'nueva.colorDeOjos': 'Eye colour',
  'nueva.sinPreferenciaAntes': 'Whatever you leave as “no preference” is chosen by the model',
  'nueva.segunLaUbicacion': ' according to the location ({pais})',
  'nueva.alEscribirLaFicha': ' when writing the profile',
  'nueva.sinPreferenciaDespues':
    '. What you fix here overrides that, even when it is not typical of the place.',

  'nueva.estiloVisualQue':
    'It conditions every image of this influencer, from the first one on. What is already ' +
    'generated cannot be changed.',

  'man.titulo': 'Manual',
  'man.bajada': 'Everything the panel does, option by option. Meant to be read out of order.',
  'man.secComoFunciona': 'How it works',
  'man.secCredenciales': 'Credentials',
  'man.secAjustes': 'Usage and settings',
  'man.secCrear': 'Creating an influencer',
  'man.secEstilo': 'The visual style',
  'man.secFicha': 'Her screen, tab by tab',
  'man.secRedes': 'Connecting the networks',
  'man.secContenido': 'Generating and publishing',
  'man.secPantallas': 'The other screens',
  'man.secCuentas': 'Accounts and roles',
  'man.secProblemas': 'When something goes wrong',

  'man.problemaTitulo': 'The problem it solves',
  'man.problemaP1Antes': 'Any model can generate a good-looking image. The hard part is that ',
  'man.mismaPersona': 'they are all of the same person',
  'man.problemaP1Despues':
    ': asking for “a 25-year-old brunette” ten times returns ten different women.',
  'man.problemaP2Antes':
    'That is why an influencer is not created in one go. It is three stages with two pauses where ' +
    'you decide, and all later content is generated ',
  'man.adjuntando': 'with the approved images attached',
  'man.problemaP2Despues': ' as a reference. Without that anchor the face drifts within days.',
  'man.tresEtapas': 'The three stages',
  'man.etapa1': 'Candidates.',
  'man.etapa1Que':
    ' Four possible faces from the chosen style. You pick one and it becomes the canonical face. ' +
    'It is the only stage a model without references can do, because it starts from text and ' +
    'nothing else.',
  'man.etapa2': 'Identity sheet.',
  'man.etapa2Antes': ' Three angles —three-quarter, profile and medium shot— generated ',
  'man.aPartirDeLaCandidata': 'from the candidate',
  'man.etapa2Despues': ', with it attached. You approve them or they are regenerated.',
  'man.etapa3': 'Content.',
  'man.etapa3Que': ' Every post is generated with the sheet attached.',
  'man.pausasAProposito':
    'The pauses are deliberate. Approving a face that does not convince is expensive: everything ' +
    'generated afterwards will have that face.',

  'man.cadaClaveEsTuya': 'Every key is yours',
  'man.cadaClaveAntes': 'What you enter here ',
  'man.soloLoVesVos': 'is seen only by you',
  'man.cadaClaveDespues':
    '. The system generates with your account and the spending is yours. They are stored encrypted ' +
    'and are never shown in full again: there is a button to reveal them when needed, and another ' +
    'to test each one against its provider.',
  'man.lasQuePodesCargar': 'The ones you can enter',
  'man.cuentaCloudflare': 'Cloudflare account',
  'man.imprescindible': 'Indispensable.',
  'man.cfAntes':
    ' It generates the images. You need an API token with the Workers AI permission —from ',
  'man.cfEnlace': 'Profile → API Tokens',
  'man.cfDespues':
    ', shown only once— and the account ID, which is in the sidebar. Free: 10,000 AI credits per ' +
    'day, no card.',
  'man.geminiAntes':
    'It writes the text: the character profile, the scenes and the captions. It has free usage and ' +
    'comes out considerably better than the bundled model. You get it at ',
  'man.geminiDespues': '. It also generates video.',
  'man.groqQue': 'An alternative to Gemini for the text. It also has free usage.',
  'man.falQue':
    'Pay-per-use. It is the only thing that enables the sensual skin level and higher-quality ' +
    'video: Cloudflare’s free model rejects both.',
  'man.casillaDeCorreo': 'Mailbox',
  'man.casillaQue':
    'The base address each influencer’s email is derived from, with aliases of the form ' +
    'yourmailbox+influencer@…',
  'man.dominioDeCorreo': 'Mail domain',
  'man.dominioQue':
    'An alternative to the above if you have your own domain: real aliases instead of suffixes.',
  'man.appDeMeta': 'Meta app',
  'man.appDeMetaQue':
    'It does not appear on your screen, and that is correct: there is one for the whole panel, ' +
    'configured by whoever administers it, and it is already set. To publish on Instagram or ' +
    'Facebook you do not have to enter anything — only connect your account from the influencer’s ' +
    'screen.',

  'man.frenosDeGasto': 'The spending brakes',
  'man.vieneActivo': 'On by default.',
  'man.modoCeroQue':
    ' No operation that could incur a charge runs. When the free allowance is used up, the work is ' +
    'deferred to the next day instead of spending. It never turns itself off: only by human ' +
    'decision.',
  'man.topeDiario': 'Daily cap',
  'man.topeDiarioQue':
    'How many Cloudflare credits can be consumed per day. It ships at 9,500 out of the 10,000 free ' +
    'ones, leaving margin.',
  'man.topeMensual': 'Monthly cap in USD',
  'man.topeMensualQue':
    'The only real brake when USD 0 mode is off. No provider offers a spending cut-off of its own.',
  'man.diferidas': 'Deferred operations',
  'man.diferidasQue':
    'What was left waiting for quota. It resumes on its own the next day; it is visible on Home.',
  'man.calidadDeImagen': 'Image quality',
  'man.economia': 'Economy',
  'man.economiaQue': 'The cheapest thing that gives usable results.',
  'man.equilibrado': 'Balanced',
  'man.equilibradoQue': 'The one that ships. It is the right choice unless you have a reason.',
  'man.premium': 'Premium',
  'man.premiumQue': 'Better quality, more expensive.',
  'man.sinRestricciones': 'Unrestricted',
  'man.sinRestriccionesQue':
    'Two Seedream variants, for registers the other models’ filters reject. They need fal.',
  'man.avanzado': 'Advanced: one model per stage.',
  'man.avanzadoAntes':
    ' It lets you pick the model for each of the three stages separately. Stage 1 accepts any of ' +
    'them —it starts from text— but stages 2 and 3 ',
  'man.soloAdmitenReferencias': 'only accept models that take reference images',
  'man.avanzadoDespues':
    ', because they attach the earlier ones. Empty means “whatever the profile says”, which is the ' +
    'right choice unless you have a specific reason.',
  'man.videoYResto': 'Video, storage and the rest',
  'man.proveedorDeVideo': 'Video provider',
  'man.proveedorDeVideoQue':
    'Automatic, Gemini, fal, or LTX in two versions. Automatic picks based on what is available ' +
    'and the spending mode.',
  'man.almacenamiento': 'Image storage',
  'man.almacenamientoQue': 'Where the generated files are kept and how much space they take.',
  'man.avisoEnPies': 'Disclosure in the captions',
  'man.avisoEnPiesQue':
    'Adds a line declaring that the content is generated. Off by default; some platforms require it.',
  'man.consumoPorProveedor': 'Usage by provider',
  'man.consumoPorProveedorQue':
    'The last seven days, broken down. Useful for seeing what is eating the budget.',
  'man.extensionQue':
    'Generates a token to connect the Chrome extension, which assists with creating accounts by ' +
    'hand.',
  'man.featureFlagsQue':
    'Switches for features under test. If you do not know what one does, leave it alone.',

  'man.loQueSeCompleta': 'What you fill in',
  'man.nombreYPais': 'Name and country',
  'man.nombreYPaisQue': 'Where she is from narratively. It conditions settings and language.',
  'man.edad': 'Age',
  'man.edadQue': 'There is a floor, and the panel warns you when you get close to it.',
  'man.deQueHabla': 'What she talks about',
  'man.deQueHablaQue': 'Her niche. There are suggestions, and you can write your own.',
  'man.rasgos': 'Traits',
  'man.rasgosQue': 'What she is like in character. It feeds the text, not the images.',
  'man.idiomas': 'Publishing languages',
  'man.idiomasQue': 'Which languages the captions are written in.',
  'man.fichaDelPersonaje': 'Character profile',
  'man.fichaDelPersonajeQue':
    'A model writes it from the above and it is editable. Without a profile, content cannot be ' +
    'generated.',
  'man.alAzar': 'Random',
  'man.alAzarQue':
    'Fills everything in with a coherent combination. Useful to get going and adjust afterwards.',

  'man.queDefineCada': 'What each option defines',
  'man.registroQue':
    'Editorial, natural, amateur or mixed. It is what changes the look most: editorial reads as a ' +
    'production; amateur, as a phone.',
  'man.nivelDePielQue':
    'Street, sportswear, beach, sensual or daring. It defines how much is shown and how the shot ' +
    'is composed.',
  'man.escenarioDeVestuario': 'Wardrobe setting',
  'man.escenarioDeVestuarioQue':
    'Water, gym, indoors or street. The clothing is derived from this: a microbikini in water, ' +
    'sportswear at the gym.',
  'man.tipoDeCuerpo': 'Body type',
  'man.tipoDeCuerpoQue': 'No preference, athletic, curvy or slim.',
  'man.tonoDelFeedQue': 'Warm, cool, neutral or high-contrast. The overall palette.',
  'man.escenarios': 'Settings',
  'man.escenariosQue': 'Indoors, outdoors or a mix.',
  'man.sobreAtrevido': 'About “daring”:',
  'man.sobreAtrevidoQue':
    ' most shots come out from behind and with minimal clothing, except where the scene does not ' +
    'allow it —gym, street—. At least one shot in each batch shows the face, so the character ' +
    'stays recognisable.',
  'man.nivelesNecesitanFal': 'The sensual and daring levels need a fal key entered.',

  'man.lasNuevePestanias': 'The nine tabs',
  'man.pesResumenQue':
    'Status, the next pending human action, the character profile and the visual style.',
  'man.pesIdentidadQue':
    'The four candidates, the chosen front shot and the identity sheet. This is where you approve ' +
    'or regenerate.',
  'man.pesGaleriaQue': 'Every generated image, by stage.',
  'man.pesContenidoQue':
    'The posts: their text, their status, their date, and the buttons to publish, rewrite the ' +
    'caption or delete.',
  'man.pesPlataformasQue':
    'This influencer’s connected accounts, with the status of each connection.',
  'man.pesTareasQue':
    'Creating each network account by hand: what to create, with the suggested handle, the bio and ' +
    'the photos ready.',
  'man.pesWorkflowQue': 'The generation runs, with their progress and their errors.',
  'man.pesConsumoQue': 'What this particular influencer has spent.',
  'man.pesAuditoriaQue': 'Who did what and when. Not editable.',

  'man.primeroSeCrea': 'First you create, then you connect',
  'man.primeroSeCreaAntes': 'Under ',
  'man.tareas': 'Tasks',
  'man.primeroSeCreaMedio':
    ' is everything that has to be created by hand —the account, the Page, the bio, the profile ' +
    'photo and the cover— with the text ready to copy and the images ready to download. Only then ' +
    'does the ',
  'man.conectar': 'Connect',
  'man.primeroSeCreaFin': ' button appear, which opens Meta’s dialog.',
  'man.tokenNoVence':
    'The Page token does not expire, so it is done once. If the dialog is completed without ' +
    'ticking a permission, the panel says so and you have to connect again.',
  'man.requisitosDeCada': 'What each platform requires',
  'man.igQue': 'A professional account linked to a Facebook Page. It publishes and reads metrics.',
  'man.fbQue':
    'It publishes to Pages, never to personal profiles. That is an API limit, not the panel’s.',
  'man.fanvueQue':
    'No integration: it has no API. The panel prepares the package and you upload it by hand.',

  'man.estadosDeUna': 'A post’s states',
  'man.borrador': 'Draft.',
  'man.borradorQue': ' The image or video and its text have been generated.',
  'man.aprobada': 'Approved.',
  'man.aprobadaQue': ' You have read it and it works. Until it is, it does not publish itself.',
  'man.programada': 'Scheduled.',
  'man.programadaQue': ' It has a date and time.',
  'man.publicada': 'Published.',
  'man.publicadaQue': ' It went out on at least one platform.',
  'man.publicacionAutomatica': 'Automatic publishing',
  'man.automaticaAntes': 'A process periodically checks what is due and publishes it to ',
  'man.igYFb': 'Instagram and Facebook',
  'man.automaticaDespues':
    '. The two are independent: if something already went out on Instagram and not yet on ' +
    'Facebook, only what is missing is published. Being marked as published does not disable the ' +
    '“Publish now” button for the others.',
  'man.soloAprobadas':
    'Only approved ones are published. Drafts never go out on their own, even with their time past.',

  'man.elMenu': 'The menu',
  'man.menuInicioQue':
    'Which influencers you have, what is waiting on you, the day’s free quota, recent errors and ' +
    'what was deferred.',
  'man.menuNueva': 'New influencer',
  'man.menuNuevaQue': 'Creating one.',
  'man.menuPlataformasQue':
    'Metrics for every connected account: a summary of Instagram and Facebook together, and one ' +
    'tab per platform.',
  'man.menuLeadsQue':
    'Instagram accounts you follow as references —with their followers and their trend— and ' +
    'Facebook links to open.',
  'man.menuConsumoQue': 'Everything about spending and settings.',
  'man.menuServicios': 'Services',
  'man.menuServiciosQue': 'Which provider is active for each thing, and whether it answers.',
  'man.menuCredencialesQue': 'Your keys.',
  'man.menuGuia': 'Guide',
  'man.menuGuiaQue': 'The setup.',
  'man.menuLaboratorio': 'Lab',
  'man.menuLaboratorioQue':
    'Only if enabled, and only for whoever administers: comparing image models, testing your own ' +
    'checkpoints on fal, and generating video with Gemini or Wan. It is subject to USD 0 mode like ' +
    'everything else.',
  'man.ademasEstadisticasAntes': 'Each influencer also has her own ',
  'man.estadisticas': 'Metrics',
  'man.ademasEstadisticasDespues': ' screen, with the trend of followers, reach and interactions.',

  'man.quienPuedeQue': 'Who can do what',
  'man.rolAdminQue':
    'Everything, plus creating accounts and seeing the lab. This is who configures the Meta app ' +
    'for everyone else.',
  'man.rolOperadorQue': 'Creates and runs their own influencers. This is the normal role.',
  'man.soloLectura': 'Read-only',
  'man.rolLecturaQue': 'Sees everything of their own but changes nothing.',
  'man.cadaCuentaVacia': 'Every account starts empty and what it generates is its own.',
  'man.cadaCuentaVaciaQue':
    ' The influencers, the credentials, the content and the spending belong to whoever created ' +
    'them: nobody sees anyone else’s, not even through a direct link.',

  'man.loQueMasAparece': 'What comes up most',
  'man.pCuota': '“Daily quota exhausted” or spending blocked',
  'man.pCuotaQue':
    'The day’s free allowance ran out and USD 0 mode is on. The work is not lost: it is deferred ' +
    'to the next day. If you want it to continue, turn the mode off and set caps.',
  'man.pPermiso': '“Connected without publishing permission”',
  'man.pPermisoQue':
    'The authorisation completed but a permission was not ticked in Meta’s dialog. It is fixed by ' +
    'connecting again and accepting everything.',
  'man.pMuro': 'The Facebook post does not appear on the wall',
  'man.pMuroQue':
    'The Meta app is not published on Meta’s side yet. The entry exists and is public, but only ' +
    'people with a role on the app can see it. Whoever administers the panel resolves this; it is ' +
    'not something about your account.',
  'man.pDistintas': 'The images come out different from each other',
  'man.pDistintasQue':
    'Usually an identity sheet approved in a hurry. It is worth regenerating: everything that ' +
    'comes after is anchored to it.',
  'man.pSinBoton': 'The connect button does not appear',
  'man.pSinBotonQue':
    'The prior creation task is missing: first the account or Page is created, then it is connected.',
  'man.pSinContenido': 'Content cannot be generated',
  'man.pSinContenidoQue':
    'The character profile is missing, or the identity sheet has not been approved. Both are ' +
    'required.',
  'man.pSensual': 'The sensual level is not available',
  'man.pSensualQue': 'It needs a fal key. Cloudflare’s free model rejects that register.',
  'man.pModeloFalta': 'A model does not appear in stages 2 or 3',
  'man.pModeloFaltaQue':
    'Because it does not accept reference images, and those stages attach them. Without a ' +
    'reference, every image would be a different person.',
  'man.antesDeGastar': 'Before spending',
  'man.videoCuesta': 'One video costs more than a whole influencer’s worth of images.',
  'man.videoCuestaQue': ' It is not a pricier image: it is another order of magnitude.',
  'man.regenerarCuesta':
    'Regenerating candidates or identity sheets costs every time. It is cheap compared with ' +
    'publishing for months with a face that does not convince, but it is not free.',
};

const DICCIONARIO: Record<Idioma, Record<Clave, string>> = { es: ES, en: EN };

const CLAVE_GUARDADA = 'vcos.idioma';

/**
 * Busca un texto y sustituye sus huecos.
 *
 * ── Por qué devuelve la clave cuando no la encuentra ────────────────────────────
 * Los tipos garantizan que exista **si la clave está escrita literalmente**. Pero algunas se
 * arman en tiempo de ejecución —`estado.${status}`, `tarea.${type}`— a partir de constantes que
 * manda el servidor, y ahí el compilador no puede ayudar: si el servidor agrega un estado que el
 * panel todavía no conoce, la entrada no existe.
 *
 * Devolvía `undefined`, que React pinta como nada: la insignia de estado habría quedado vacía y
 * eso se lee como que la influencer no tiene estado. Devolver la clave es feo y es correcto — se
 * ve que falta una traducción, en vez de parecer que falta un dato.
 */
function traducir(idioma: Idioma, clave: Clave, valores?: Record<string, string | number>): string {
  const plantilla = DICCIONARIO[idioma][clave] ?? DICCIONARIO.en[clave] ?? clave;
  if (!valores) return plantilla;
  return plantilla.replace(/\{(\w+)\}/g, (coincidencia, nombre: string) =>
    nombre in valores ? String(valores[nombre]) : coincidencia,
  );
}

/**
 * Qué idioma mostrar la primera vez.
 *
 * Lo elegido gana: alguien que tocó el selector ya expresó una preferencia, y volver a pisarla
 * en cada visita es no haberle hecho caso.
 *
 * Sin elección guardada es inglés, aunque el navegador esté en español. Detectarlo parece más
 * atento y acá no lo es: el panel se opera al lado del contenido que se publica, y ese contenido
 * sale en inglés por defecto. Mostrar la interfaz en un idioma y lo que produce en otro no es
 * cortesía, es dos aplicaciones. El selector sigue a un clic.
 */
function idiomaInicial(): Idioma {
  const guardado =
    typeof localStorage === 'undefined' ? null : localStorage.getItem(CLAVE_GUARDADA);
  if (guardado && (IDIOMAS as readonly string[]).includes(guardado)) return guardado as Idioma;

  return 'en';
}

/**
 * El idioma en curso, para lo que no puede usar un hook.
 *
 * `haceCuanto`, `formatDateTime` y `timeUntil` son funciones puras: se llaman dentro del
 * JSX de veinte pantallas y desde arreglos que se arman fuera de un componente. Convertirlas
 * en hooks obligaría a tocar cada sitio que las llama para pasarles el traductor, y a
 * inventar un componente donde hoy hay una expresión.
 *
 * No hace falta que avisen de sus cambios: cambiar el idioma cambia el contexto, el contexto
 * vuelve a dibujar el árbol entero, y en ese dibujado estas funciones se llaman de nuevo. La
 * variable ya está actualizada para entonces porque `setIdioma` la escribe antes.
 */
let enCurso: Idioma = idiomaInicial();

/** El idioma activo, para las funciones puras. Dentro de un componente va `useT()`. */
export function idiomaActual(): Idioma {
  return enCurso;
}

/** Traduce fuera de un componente, en el idioma activo. */
export function texto(clave: Clave, valores?: Record<string, string | number>): string {
  return traducir(enCurso, clave, valores);
}

type Traductor = {
  t: (clave: Clave, valores?: Record<string, string | number>) => string;
  idioma: Idioma;
  setIdioma: (idioma: Idioma) => void;
};

const IdiomaContext = createContext<Traductor | null>(null);

export function ProveedorDeIdioma({ children }: { children: React.ReactNode }) {
  const [idioma, setIdiomaEstado] = useState<Idioma>(idiomaInicial);

  const setIdioma = useCallback((siguiente: Idioma) => {
    enCurso = siguiente;
    setIdiomaEstado(siguiente);
    if (typeof localStorage !== 'undefined') localStorage.setItem(CLAVE_GUARDADA, siguiente);
    // El atributo del documento no es decorativo: de él dependen la separación silábica, las
    // comillas tipográficas y lo que anuncia un lector de pantalla.
    if (typeof document !== 'undefined') document.documentElement.lang = siguiente;
  }, []);

  const valor = useMemo<Traductor>(
    () => ({
      idioma,
      setIdioma,
      t: (clave, valores) => traducir(idioma, clave, valores),
    }),
    [idioma, setIdioma],
  );

  return <IdiomaContext.Provider value={valor}>{children}</IdiomaContext.Provider>;
}

/**
 * El traductor, desde cualquier pantalla.
 *
 * Fuera del proveedor devuelve inglés en vez de romper. Una pantalla no debería caerse por no
 * poder traducirse — es el mismo criterio que usan los avisos.
 */
export function useT(): Traductor {
  return (
    useContext(IdiomaContext) ?? {
      idioma: 'en',
      setIdioma: () => undefined,
      t: (clave, valores) => traducir('en', clave, valores),
    }
  );
}

/**
 * El mismo texto, como componente.
 *
 * Existe por `ErrorBoundary`, que es una clase —`componentDidCatch` no tiene equivalente en
 * hooks— y por lo tanto no puede llamar a `useT()`. Envolverlo en una función sólo para poder
 * traducir su cartel sería reescribir el único componente de clase del panel por un motivo que
 * no es el suyo.
 */
export function TextoTraducido({
  clave,
  valores,
}: {
  clave: Clave;
  valores?: Record<string, string | number>;
}) {
  const { t } = useT();
  return <>{t(clave, valores)}</>;
}

/** Elige la etiqueta del idioma activo en una entrada de catálogo servida por la API. */
export function etiqueta(entrada: { es: string; en: string }, idioma: Idioma): string {
  return entrada[idioma];
}
